export type Parameter = {
  name: string;
  type: string;
  required?: boolean;
  description: string;
  example?: string;
};

export type Field = {
  name: string;
  type: string;
  description: string;
};

export type Endpoint = {
  id: string;
  group: 'U.S. Stocks' | 'U.S. Options' | 'U.S. Indices' | 'Hong Kong Stocks' | 'China A-shares' | 'Global Markets' | 'Flat Files' | 'WebSocket';
  /**
   * Asset classes whose sidebar lists this endpoint. Most datasets belong to one
   * class, but a Longbridge route that carries verified U.S. coverage is listed
   * under U.S. Stocks as well as its canonical group, so a U.S.-focused reader
   * finds it without a second copy of the page.
   */
  markets?: Array<Endpoint['group']>;
  /** Global discovery facets; separate from the existing store navigation. */
  regions?: Array<'US' | 'HK' | 'CN' | 'GLOBAL'>;
  title: string;
  method: 'GET';
  path: string;
  summary: string;
  description: string;
  useCases: string[];
  parameters: Parameter[];
  fields: Field[];
  request: string;
  response: string;
  notes?: string[];
  /** Override the REST verb/path bar for non-REST resources such as S3 datasets. */
  resourceLabel?: string;
  resourcePath?: string;
  /** Purpose-built examples for workflows that cannot use the generic JSON request snippet. */
  codeExamples?: Partial<Record<'curl' | 'python' | 'typescript', string>>;
  responseMediaType?: string;
  relatedDatasets?: Array<{
    id: string;
    title: string;
    path: string;
    description: string;
    minPlan: string;
  }>;
};

/**
 * Level one of the navigation tree below an asset class: the broad kind of
 * question a dataset answers. Deliberately market-agnostic, so the same
 * headings appear for U.S., Hong Kong, and mainland-China catalogs and a reader
 * who learns the shape in one market can navigate the others.
 */
export type EndpointSection =
  | 'Reference Data'
  | 'Prices & Activity'
  | 'Aggregates (OHLC)'
  | 'Snapshots'
  | 'Trades & Quotes'
  | 'Company Fundamentals'
  | 'Estimates & Analyst Coverage'
  | 'Ownership & Disclosures'
  | 'Corporate Actions & Calendars'
  | 'Market Analytics'
  | 'News & Content'
  | 'Derivatives'
  | 'Flat Files'
  | 'Streaming';

/**
 * Level two: the dataset family within a section. A section is only worth
 * splitting when it holds enough routes to be hard to scan, but every section
 * declares subsections anyway so the tree has one uniform depth.
 */
export type EndpointSubsection =
  // Reference Data
  | 'Securities & Tickers'
  | 'Trading Sessions'
  | 'Taxonomies'
  // Prices & Activity
  | 'Aggregate Bars (OHLC)'
  | 'Intraday & Snapshots'
  | 'Trades & Quotes'
  | 'Technical Indicators'
  // Company Fundamentals
  | 'Financial Statements'
  | 'Metrics & Ratios'
  | 'Valuation'
  | 'Company Profile'
  | 'Funds & ETFs'
  // Estimates & Analyst Coverage
  | 'Consensus Estimates'
  | 'Estimate Revisions'
  | 'Ratings & Price Targets'
  | 'Earnings Results'
  // Ownership & Disclosures
  | 'Shareholders'
  | 'Insider Activity'
  | 'Filings'
  | 'Short Interest'
  | 'Sustainability'
  // Corporate Actions & Calendars
  | 'Dividends & Buybacks'
  | 'Corporate Actions'
  | 'Event Calendars'
  // Market Analytics
  | 'Capital Flow'
  | 'Market Sentiment'
  | 'Trading Statistics'
  | 'Broker Holdings'
  | 'Cross-Listing (A/H)'
  | 'Index Composition'
  | 'Macroeconomics'
  | 'Quantitative Factors'
  // News & Content
  | 'Company News'
  | 'Topics'
  // Derivatives
  | 'Options'
  | 'Warrants'
  // Streaming
  | 'Connecting'
  | 'Stock Streams'
  | 'Options Streams'
  | 'Index Streams'
  // Flat Files
  | 'Query & Download'
  | 'Stock Archives'
  | 'Options Archives'
  | 'Index Archives';

const aggregateParameters: Parameter[] = [
  { name: 'stocksTicker', type: 'string', required: true, description: 'Case-sensitive market ticker.', example: 'AAPL' },
  { name: 'multiplier', type: 'integer', required: true, description: 'Size of the timespan multiplier.', example: '1' },
  { name: 'timespan', type: 'string', required: true, description: 'Aggregate window: minute, hour, day, week, month, quarter, or year.', example: 'day' },
  { name: 'from', type: 'string', required: true, description: 'Window start as YYYY-MM-DD or a Unix millisecond timestamp.', example: '2025-01-02' },
  { name: 'to', type: 'string', required: true, description: 'Window end as YYYY-MM-DD or a Unix millisecond timestamp.', example: '2025-01-10' },
  { name: 'adjusted', type: 'boolean', description: 'Adjust results for splits. Defaults to true.', example: 'true' },
  { name: 'sort', type: 'string', description: 'Timestamp order: asc or desc.', example: 'asc' },
  { name: 'limit', type: 'integer', description: 'Maximum base aggregates queried. Default 5,000; maximum 50,000.', example: '5000' },
];

const aggregateFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Exchange symbol for the instrument.' },
  { name: 'adjusted', type: 'boolean', description: 'Whether the response is split-adjusted.' },
  { name: 'queryCount', type: 'integer', description: 'Base aggregates used for the response.' },
  { name: 'resultsCount', type: 'integer', description: 'Number of returned bars.' },
  { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
  { name: 'results[].o', type: 'number', description: 'Open price.' },
  { name: 'results[].h', type: 'number', description: 'High price.' },
  { name: 'results[].l', type: 'number', description: 'Low price.' },
  { name: 'results[].c', type: 'number', description: 'Close price.' },
  { name: 'results[].v', type: 'number', description: 'Trading volume.' },
  { name: 'results[].vw', type: 'number', description: 'Volume-weighted average price.' },
  { name: 'results[].n', type: 'integer', description: 'Transactions in the aggregate window.' },
  { name: 'results[].t', type: 'integer', description: 'Window start as Unix milliseconds.' },
  { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
];

/**
 * Index aggregates are derived from published index values rather than trades,
 * so the bars carry no volume, VWAP, or transaction count.
 */
const indexAggregateFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Index symbol for the aggregates.' },
  { name: 'queryCount', type: 'integer', description: 'Base aggregates used for the response.' },
  { name: 'resultsCount', type: 'integer', description: 'Number of returned bars.' },
  { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
  { name: 'results[].o', type: 'number', description: 'Opening index value for the window.' },
  { name: 'results[].h', type: 'number', description: 'Highest index value in the window.' },
  { name: 'results[].l', type: 'number', description: 'Lowest index value in the window.' },
  { name: 'results[].c', type: 'number', description: 'Closing index value for the window.' },
  { name: 'results[].t', type: 'integer', description: 'Window start as Unix milliseconds.' },
  { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
];

const chinaCommon: Parameter[] = [
  { name: 'ts_code', type: 'string', description: 'TS instrument code. Some datasets accept comma-separated values.', example: '000001.SZ' },
  { name: 'trade_date', type: 'string', description: 'Trading date in YYYYMMDD format.', example: '20260707' },
  { name: 'start_date', type: 'string', description: 'Inclusive start date in YYYYMMDD format.', example: '20260101' },
  { name: 'end_date', type: 'string', description: 'Inclusive end date in YYYYMMDD format.', example: '20260707' },
  { name: 'fields', type: 'string', description: 'Comma-separated output fields. Omit for the default field set.', example: 'ts_code,trade_date,open,close' },
];

const stockAggregateFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Case-sensitive U.S. equity ticker.' },
  { name: 'volume', type: 'number', description: 'Share volume in the aggregate window.' },
  { name: 'open', type: 'number', description: 'Opening trade price.' },
  { name: 'close', type: 'number', description: 'Closing trade price.' },
  { name: 'high', type: 'number', description: 'Highest trade price.' },
  { name: 'low', type: 'number', description: 'Lowest trade price.' },
  { name: 'window_start', type: 'integer', description: 'Aggregate window start as a Unix nanosecond timestamp in UTC.' },
  { name: 'transactions', type: 'integer', description: 'Number of qualifying trades in the aggregate window.' },
];

const stockTradeFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Case-sensitive U.S. equity ticker.' },
  { name: 'conditions', type: 'string', description: 'Serialized list of trade condition codes.' },
  { name: 'correction', type: 'integer', description: 'Trade correction indicator.' },
  { name: 'exchange', type: 'integer', description: 'Exchange or reporting-facility identifier.' },
  { name: 'id', type: 'string', description: 'Trade identifier, unique per ticker, exchange, and TRF.' },
  { name: 'participant_timestamp', type: 'integer', description: 'Exchange event time as Unix nanoseconds in UTC.' },
  { name: 'price', type: 'number', description: 'Execution price per share.' },
  { name: 'sequence_number', type: 'integer', description: 'Increasing per-ticker event sequence number.' },
  { name: 'sip_timestamp', type: 'integer', description: 'SIP receipt time as Unix nanoseconds in UTC.' },
  { name: 'size', type: 'number', description: 'Executed share quantity.' },
  { name: 'tape', type: 'integer', description: 'Listing tape: 1=A, 2=B, or 3=C.' },
  { name: 'trf_id', type: 'integer', description: 'Trade Reporting Facility identifier, when applicable.' },
  { name: 'trf_timestamp', type: 'integer', description: 'TRF receipt time as Unix nanoseconds in UTC, when applicable.' },
];

const stockQuoteFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Case-sensitive U.S. equity ticker.' },
  { name: 'ask_exchange', type: 'integer', description: 'Exchange identifier for the best ask.' },
  { name: 'ask_price', type: 'number', description: 'National best ask price.' },
  { name: 'ask_size', type: 'number', description: 'Shares offered at the best ask.' },
  { name: 'bid_exchange', type: 'integer', description: 'Exchange identifier for the best bid.' },
  { name: 'bid_price', type: 'number', description: 'National best bid price.' },
  { name: 'bid_size', type: 'number', description: 'Shares bid at the best bid.' },
  { name: 'conditions', type: 'string', description: 'Serialized list of quote condition codes.' },
  { name: 'indicators', type: 'string', description: 'Serialized list of quote indicator codes.' },
  { name: 'participant_timestamp', type: 'integer', description: 'Exchange event time as Unix nanoseconds in UTC.' },
  { name: 'sequence_number', type: 'integer', description: 'Increasing per-ticker event sequence number.' },
  { name: 'sip_timestamp', type: 'integer', description: 'SIP receipt time as Unix nanoseconds in UTC.' },
  { name: 'tape', type: 'integer', description: 'Listing tape: 1=A, 2=B, or 3=C.' },
  { name: 'trf_timestamp', type: 'integer', description: 'TRF receipt time as Unix nanoseconds in UTC, when applicable.' },
];

const optionAggregateFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Canonical option contract ticker beginning with O:.' },
  { name: 'volume', type: 'number', description: 'Contract volume in the aggregate window.' },
  { name: 'open', type: 'number', description: 'Opening option trade price.' },
  { name: 'close', type: 'number', description: 'Closing option trade price.' },
  { name: 'high', type: 'number', description: 'Highest option trade price.' },
  { name: 'low', type: 'number', description: 'Lowest option trade price.' },
  { name: 'window_start', type: 'integer', description: 'Aggregate window start as a Unix timestamp in UTC.' },
  { name: 'transactions', type: 'integer', description: 'Number of qualifying option trades in the window.' },
];

const optionTradeFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Canonical option contract ticker beginning with O:.' },
  { name: 'conditions', type: 'string', description: 'Serialized list of OPRA trade condition codes.' },
  { name: 'correction', type: 'integer', description: 'Trade correction indicator.' },
  { name: 'exchange', type: 'integer', description: 'Options exchange identifier.' },
  { name: 'participant_timestamp', type: 'integer', description: 'Exchange event time as Unix nanoseconds in UTC.' },
  { name: 'price', type: 'number', description: 'Execution price per contract unit.' },
  { name: 'sequence_number', type: 'integer', description: 'Increasing per-contract event sequence number.' },
  { name: 'sip_timestamp', type: 'integer', description: 'OPRA receipt time as Unix nanoseconds in UTC.' },
  { name: 'size', type: 'number', description: 'Executed contract quantity.' },
];

const optionQuoteFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Canonical option contract ticker beginning with O:.' },
  { name: 'ask_exchange', type: 'integer', description: 'Options exchange identifier for the best ask.' },
  { name: 'ask_price', type: 'number', description: 'Best ask price.' },
  { name: 'ask_size', type: 'number', description: 'Contracts offered at the best ask.' },
  { name: 'bid_exchange', type: 'integer', description: 'Options exchange identifier for the best bid.' },
  { name: 'bid_price', type: 'number', description: 'Best bid price.' },
  { name: 'bid_size', type: 'number', description: 'Contracts bid at the best bid.' },
  { name: 'conditions', type: 'string', description: 'Serialized list of OPRA quote condition codes.' },
  { name: 'participant_timestamp', type: 'integer', description: 'Exchange event time as Unix nanoseconds in UTC.' },
  { name: 'sequence_number', type: 'integer', description: 'Increasing per-contract event sequence number.' },
  { name: 'sip_timestamp', type: 'integer', description: 'OPRA receipt time as Unix nanoseconds in UTC.' },
];

const indexAggregateFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Canonical index ticker beginning with I:.' },
  { name: 'open', type: 'number', description: 'Opening index value.' },
  { name: 'close', type: 'number', description: 'Closing index value.' },
  { name: 'high', type: 'number', description: 'Highest published index value.' },
  { name: 'low', type: 'number', description: 'Lowest published index value.' },
  { name: 'window_start', type: 'integer', description: 'Aggregate window start as a Unix timestamp in UTC.' },
];

const indexValueFlatFileFields: Field[] = [
  { name: 'ticker', type: 'string', description: 'Canonical index ticker beginning with I:.' },
  { name: 'value', type: 'number', description: 'Published index value.' },
  { name: 'timestamp', type: 'integer', description: 'Publication time as a Unix timestamp in UTC.' },
];

type FlatFilePage = {
  id: string;
  title: string;
  slug: string;
  archivePath: string;
  summary: string;
  description: string;
  minPlan: string;
  concurrency: number;
  fields: Field[];
  sample: string;
  historyStart?: string;
  formatNote?: string;
};

/**
 * Build a dedicated dataset page from a hard-coded archive definition. The
 * examples use the Eroya manifest as the source of truth, stream each gzip to a
 * temporary file, and atomically rename it only after a complete transfer.
 */
function flatFilePage(dataset: FlatFilePage): Endpoint {
  const directory = dataset.slug.replace(/^us-/, '');
  const curl = `# One dataset session is one direct download.
mkdir -p ${directory}
curl -fL --retry 5 --retry-all-errors \
  -H "Authorization: Bearer \$EROYA_API_KEY" \
  -o "${directory}/2025-01-02.csv.gz" \
  "https://api.eroya.co/v1/flatfiles/download?dataset=${dataset.slug}&date=2025-01-02"

# For a range, query the manifest first. One window may span at most 366 days.
mkdir -p ${directory}
curl -fsS --retry 5 \\
  -H "Authorization: Bearer \$EROYA_API_KEY" \\
  "https://api.eroya.co/v1/flatfiles/list?dataset=${dataset.slug}&start=2025-01-01&end=2025-12-31" \\
  | jq -r '.files[].date' \\
  | xargs -P ${dataset.concurrency} -n 1 sh -c '
      date="\$1"; part="${directory}/\$date.csv.gz.part"; final="${directory}/\$date.csv.gz"
      test -f "\$final" && exit 0
      curl -fL --retry 5 --retry-all-errors \\
        -H "Authorization: Bearer \$EROYA_API_KEY" \\
        -o "\$part" \\
        "https://api.eroya.co/v1/flatfiles/download?dataset=${dataset.slug}&date=\$date" \\
        && mv "\$part" "\$final"
    ' sh`;

  const python = `# pip install requests
import os
import time
from concurrent.futures import ThreadPoolExecutor
from datetime import date, timedelta
from pathlib import Path

import requests

BASE = "https://api.eroya.co/v1"
DATASET = "${dataset.slug}"
START = date.fromisoformat("${dataset.historyStart ?? '2003-09-10'}")
END = date.today()
OUT = Path("${directory}")
WORKERS = ${dataset.concurrency}
HEADERS = {"Authorization": "Bearer " + os.environ["EROYA_API_KEY"]}
OUT.mkdir(parents=True, exist_ok=True)

def manifest_windows(start, end):
    cursor = start
    while cursor <= end:
        stop = min(end, cursor + timedelta(days=365))
        yield cursor, stop
        cursor = stop + timedelta(days=1)

def request(method, url, **kwargs):
    for attempt in range(5):
        try:
            response = requests.request(method, url, timeout=(30, 300), **kwargs)
            response.raise_for_status()
            return response
        except requests.RequestException:
            if attempt == 4:
                raise
            time.sleep(2 ** attempt)

files = []
for start, end in manifest_windows(START, END):
    response = request(
        "GET",
        BASE + "/flatfiles/list",
        headers=HEADERS,
        params={"dataset": DATASET, "start": start.isoformat(), "end": end.isoformat()},
    )
    files.extend(response.json()["files"])

def download(item):
    target = OUT / (item["date"] + ".csv.gz")
    expected = item["sizeBytes"]
    if target.exists() and target.stat().st_size == expected:
        return
    part = target.with_suffix(target.suffix + ".part")
    with request(
        "GET",
        BASE + "/flatfiles/download",
        headers=HEADERS,
        params={"dataset": DATASET, "date": item["date"]},
        stream=True,
        allow_redirects=True,
    ) as response, part.open("wb") as output:
        for chunk in response.iter_content(8 * 1024 * 1024):
            if chunk:
                output.write(chunk)
    if part.stat().st_size != expected:
        raise IOError("size mismatch for " + item["date"])
    part.replace(target)

with ThreadPoolExecutor(max_workers=WORKERS) as pool:
    list(pool.map(download, files))`;

  const typescript = `// Node.js 20+. Save as backfill.mjs and set EROYA_API_KEY.
import { createWriteStream } from "node:fs";
import { mkdir, rename, stat } from "node:fs/promises";
import { Readable } from "node:stream";
import { pipeline } from "node:stream/promises";

const base = "https://api.eroya.co/v1";
const dataset = "${dataset.slug}";
const output = new URL("./${directory}/", import.meta.url);
const headers = { Authorization: \`Bearer \${process.env.EROYA_API_KEY}\` };
await mkdir(output, { recursive: true });

const list = await fetch(
  \`${'${base}'}/flatfiles/list?dataset=${dataset}&start=2025-01-01&end=2025-12-31\`,
  { headers },
);
if (!list.ok) throw new Error(\`manifest HTTP \${list.status}\`);
const { files } = await list.json();

let cursor = 0;
async function worker() {
  while (cursor < files.length) {
    const item = files[cursor++];
    const final = new URL(item.date + ".csv.gz", output);
    const part = new URL(item.date + ".csv.gz.part", output);
    try {
      if ((await stat(final)).size === item.sizeBytes) continue;
    } catch {}
    const response = await fetch(
      \`${'${base}'}/flatfiles/download?dataset=${dataset}&date=\${item.date}\`,
      { headers, redirect: "follow" },
    );
    if (!response.ok || !response.body) throw new Error(\`\${item.date}: HTTP \${response.status}\`);
    await pipeline(Readable.fromWeb(response.body), createWriteStream(part));
    if ((await stat(part)).size !== item.sizeBytes) throw new Error(\`\${item.date}: size mismatch\`);
    await rename(part, final);
  }
}

await Promise.all(Array.from({ length: ${dataset.concurrency} }, worker));`;

  return {
    id: dataset.id,
    group: 'Flat Files',
    title: dataset.title,
    method: 'GET',
    path: '/flatfiles/download',
    resourceLabel: 'S3',
    resourcePath: dataset.archivePath,
    summary: dataset.summary,
    description: dataset.description,
    useCases: ['Full-market backfills', 'Research', 'Warehouse ingestion', 'Large-scale model training'],
    parameters: [
      { name: 'dataset', type: 'string', required: true, description: 'Fixed dataset slug used by the list and download APIs.', example: dataset.slug },
      { name: 'start', type: 'date', required: true, description: 'Inclusive manifest start date. A list request may span at most 366 days.', example: '2025-01-01' },
      { name: 'end', type: 'date', required: true, description: 'Inclusive manifest end date.', example: '2025-12-31' },
      { name: 'concurrency', type: 'integer', description: 'Bounded parallel downloads. Start with this value and lower it after 429 or network errors.', example: String(dataset.concurrency) },
    ],
    fields: dataset.fields,
    request: `https://api.eroya.co/v1/flatfiles/list?dataset=${dataset.slug}&start=2025-01-01&end=2025-12-31`,
    codeExamples: { curl, python, typescript },
    responseMediaType: 'text/csv · gzip',
    response: dataset.sample,
    notes: [
      `Archive path: ${dataset.archivePath}. Minimum Eroya plan: ${dataset.minPlan}.`,
      dataset.formatNote ?? 'Files are gzipped CSVs with a header row.',
      'Treat /flatfiles/list as the manifest: it skips weekends and holidays and gives the exact compressed size used to validate each transfer.',
      'For multi-year jobs, split manifests into windows of no more than 366 days. Keep completed .csv.gz files as checkpoints and only retry missing or size-mismatched objects.',
      `Use no more than ${dataset.concurrency} concurrent transfers initially. Retry 429 and transient 5xx responses with exponential backoff and never log the API key or temporary signed URL.`,
      'Files are normally finalized by about 11:00 AM ET on the following day. All timestamps are UTC; convert explicitly before aligning them to U.S. market sessions.',
    ],
  };
}

const stockFlatFilePages: Endpoint[] = [
  flatFilePage({
    id: 'flatfiles-stocks-day-aggregates',
    title: 'Stocks Day Aggregates',
    slug: 'us-stocks-day-aggs',
    archivePath: 'us_stocks_sip/day_aggs_v1',
    summary: 'One daily OHLCV row per U.S. equity in a session-sized CSV.',
    description: 'Download a complete market day of unadjusted daily candles across U.S. equities. This is the smallest stock archive and the preferred source for long-horizon full-market backtests, screens, and daily warehouse tables.',
    minPlan: 'Pro',
    concurrency: 4,
    formatNote: 'Stock files are unadjusted, gzipped CSVs with a header row. Apply split adjustments yourself or use adjusted REST aggregates.',
    fields: stockAggregateFlatFileFields,
    sample: `ticker,volume,open,close,high,low,window_start,transactions
AAPL,55740731,243.36,243.85,244.18,241.89,1735813800000000000,612482
MSFT,27951200,425.53,418.79,426.07,414.85,1735813800000000000,331847`,
  }),
  flatFilePage({
    id: 'flatfiles-stocks-minute-aggregates',
    title: 'Stocks Minute Aggregates',
    slug: 'us-stocks-minute-aggs',
    archivePath: 'us_stocks_sip/minute_aggs_v1',
    summary: 'Minute OHLCV candles across the full U.S. equity market.',
    description: 'Download one compressed file per session containing unadjusted minute candles for all U.S. equities across pre-market, regular, and after-hours trading. Stream and partition these files instead of loading a full session into memory.',
    minPlan: 'Pro',
    concurrency: 4,
    formatNote: 'Stock files are unadjusted, gzipped CSVs with a header row. Apply split adjustments yourself or use adjusted REST aggregates.',
    fields: stockAggregateFlatFileFields,
    sample: `ticker,volume,open,close,high,low,window_start,transactions
AAPL,4930,200.29,200.5,200.63,200.29,1744792500000000000,129
AAPL,1815,200.39,200.34,200.61,200.34,1744792560000000000,57`,
  }),
  flatFilePage({
    id: 'flatfiles-stocks-trades',
    title: 'Stocks Trades',
    slug: 'us-stocks-trades',
    archivePath: 'us_stocks_sip/trades_v1',
    summary: 'Every reported U.S. equity trade with nanosecond timestamps.',
    description: 'Download tick-level trade prints from U.S. exchanges, FINRA facilities, and dark pools. Daily archives are large: keep concurrency conservative, stream compressed bytes to disk, and transform them in partitions after the download is verified.',
    minPlan: 'Premium',
    concurrency: 2,
    formatNote: 'Stock files are unadjusted, gzipped CSVs with a header row. Corporate-action adjustments do not apply to tick trades.',
    fields: stockTradeFlatFileFields,
    sample: `ticker,conditions,correction,exchange,id,participant_timestamp,price,sequence_number,sip_timestamp,size,tape,trf_id,trf_timestamp
AAPL,"[12,37]",0,11,52983575722108,1735826400123400000,243.36,1063799,1735826400123456789,100,3,,`,
  }),
  flatFilePage({
    id: 'flatfiles-stocks-quotes',
    title: 'Stocks Quotes',
    slug: 'us-stocks-quotes',
    archivePath: 'us_stocks_sip/quotes_v1',
    summary: 'Nanosecond NBBO quote updates across the U.S. equity market.',
    description: 'Download top-of-book bid and ask updates from the consolidated U.S. market. Quote sessions can be tens of gigabytes compressed, so use the manifest size for capacity planning, limit concurrency, and process the gzip as a stream or in external-memory query engines.',
    minPlan: 'Premium',
    concurrency: 2,
    formatNote: 'Stock files are unadjusted, gzipped CSVs with a header row. Corporate-action adjustments do not apply to tick quotes.',
    fields: stockQuoteFlatFileFields,
    sample: `ticker,ask_exchange,ask_price,ask_size,bid_exchange,bid_price,bid_size,conditions,indicators,participant_timestamp,sequence_number,sip_timestamp,tape,trf_timestamp
AAPL,12,243.38,200,11,243.36,300,"[1]",,1735826400065321200,2060,1735826400065700400,3,`,
  }),
];

const optionFlatFilePages: Endpoint[] = [
  flatFilePage({
    id: 'flatfiles-options-day-aggregates',
    title: 'Options Day Aggregates',
    slug: 'us-options-day-aggs',
    archivePath: 'us_options_opra/day_aggs_v1',
    summary: 'Daily OHLCV candles for every traded U.S. option contract.',
    description: 'Download one session-wide file of daily candles across all 17 OPRA options exchanges. Each archive is a complete market-day partition suited to long-horizon options research, volatility studies, and full-chain backtests.',
    minPlan: 'Pro',
    concurrency: 4,
    historyStart: '2014-06-02',
    formatNote: 'Option aggregate files are gzipped CSVs with a header row and canonical O: contract tickers.',
    fields: optionAggregateFlatFileFields,
    sample: `ticker,volume,open,close,high,low,window_start,transactions
O:SPY250117C00600000,1842,3.42,3.67,3.91,3.11,1735813800,927`,
  }),
  flatFilePage({
    id: 'flatfiles-options-minute-aggregates',
    title: 'Options Minute Aggregates',
    slug: 'us-options-minute-aggs',
    archivePath: 'us_options_opra/minute_aggs_v1',
    summary: 'Minute OHLCV candles across the full U.S. options market.',
    description: 'Download one compressed session file containing minute candles for every qualifying U.S. option contract. Stream or partition these archives when reconstructing intraday chains or training volatility and execution models.',
    minPlan: 'Pro',
    concurrency: 3,
    historyStart: '2014-06-02',
    formatNote: 'Option aggregate files are gzipped CSVs with a header row and canonical O: contract tickers.',
    fields: optionAggregateFlatFileFields,
    sample: `ticker,volume,open,close,high,low,window_start,transactions
O:SPY250117C00600000,38,3.42,3.48,3.52,3.39,1735813800,19`,
  }),
  flatFilePage({
    id: 'flatfiles-options-trades',
    title: 'Options Trades',
    slug: 'us-options-trades',
    archivePath: 'us_options_opra/trades_v1',
    summary: 'Every reported U.S. option trade with nanosecond timestamps.',
    description: 'Download consolidated tick-level executions from all OPRA options exchanges. Use these daily partitions for market-microstructure studies, execution analysis, regulatory audit trails, and custom aggregate construction.',
    minPlan: 'Premium',
    concurrency: 2,
    historyStart: '2014-06-02',
    formatNote: 'Option trade files are gzipped CSVs with a header row and OPRA condition and exchange identifiers.',
    fields: optionTradeFlatFileFields,
    sample: `ticker,conditions,correction,exchange,participant_timestamp,price,sequence_number,sip_timestamp,size
O:SPY250117C00600000,"[209]",0,323,1735826400123400000,3.48,1063799,1735826400123456789,4`,
  }),
  flatFilePage({
    id: 'flatfiles-options-quotes',
    title: 'Options Quotes',
    slug: 'us-options-quotes',
    archivePath: 'us_options_opra/quotes_v1',
    summary: 'Top-of-book option quotes consolidated from all OPRA exchanges.',
    description: 'Download bid and ask updates for the complete U.S. listed-options market. Quote sessions are large, so plan capacity from the manifest, keep transfer concurrency conservative, and process each gzip as a stream.',
    minPlan: 'Premium',
    concurrency: 2,
    historyStart: '2014-06-02',
    formatNote: 'Option quote files are gzipped CSVs with a header row and OPRA condition and exchange identifiers.',
    fields: optionQuoteFlatFileFields,
    sample: `ticker,ask_exchange,ask_price,ask_size,bid_exchange,bid_price,bid_size,conditions,participant_timestamp,sequence_number,sip_timestamp
O:SPY250117C00600000,323,3.50,12,316,3.45,8,"[1]",1735826400065321200,2060,1735826400065700400`,
  }),
];

const indexFlatFilePages: Endpoint[] = [
  flatFilePage({
    id: 'flatfiles-indices-day-aggregates',
    title: 'Indices Day Aggregates',
    slug: 'us-indices-day-aggs',
    archivePath: 'us_indices/day_aggs_v1',
    summary: 'Daily OHLC values across more than 10,000 supported indices.',
    description: 'Download daily open, high, low, and close values derived from every index update published during the session. These files cover benchmark, sector, regional, and thematic families from major global index providers.',
    minPlan: 'Pro',
    concurrency: 4,
    historyStart: '2023-02-14',
    formatNote: 'Index aggregates contain values rather than trades, so they do not carry volume, VWAP, or transaction counts.',
    fields: indexAggregateFlatFileFields,
    sample: `ticker,open,close,high,low,window_start
I:SPX,4745.20,4769.83,4785.39,4736.77,1735813800`,
  }),
  flatFilePage({
    id: 'flatfiles-indices-minute-aggregates',
    title: 'Indices Minute Aggregates',
    slug: 'us-indices-minute-aggs',
    archivePath: 'us_indices/minute_aggs_v1',
    summary: 'Minute OHLC values across the supported index universe.',
    description: 'Download minute candles aggregated from every index value published during a session. Empty intervals mean the provider emitted no new value, and should remain empty rather than being forward-filled during ingestion.',
    minPlan: 'Pro',
    concurrency: 3,
    historyStart: '2023-02-14',
    formatNote: 'Index aggregates contain values rather than trades, so they do not carry volume, VWAP, or transaction counts.',
    fields: indexAggregateFlatFileFields,
    sample: `ticker,open,close,high,low,window_start
I:SPX,4745.20,4746.02,4746.81,4744.97,1735813800`,
  }),
  flatFilePage({
    id: 'flatfiles-indices-values',
    title: 'Index Values',
    slug: 'us-indices-values',
    archivePath: 'us_indices/values_v1',
    summary: 'Every published index value in a daily market-wide archive.',
    description: 'Download tick-by-tick values across more than 10,000 indices from S&P, Nasdaq, Dow Jones, FTSE Russell, MSCI, and other providers. These are the source observations used to construct index aggregates.',
    minPlan: 'Premium',
    concurrency: 1,
    historyStart: '2023-02-14',
    formatNote: 'Index value archives can be several gigabytes per session. They contain values rather than executable trades or quotes.',
    fields: indexValueFlatFileFields,
    sample: `ticker,value,timestamp
I:SPX,4769.83,1735851600
I:NDX,16832.92,1735851600`,
  }),
];

/**
 * Shared caveats for the `/china/*` routes. These pass the upstream response
 * through unchanged, so they answer in the upstream's own envelope rather than
 * the status/results shape the rest of the catalog uses, and they reject a call
 * that selects nothing instead of returning the whole market.
 */
const chinaEnvelopeNotes: string[] = [
  'Supply at least one selector, such as ts_code or a trading date. A call carrying only an API key returns upstream error 50101 with a "必填参数" (required parameter) message rather than an empty result set.',
  'These routes return the upstream envelope: code 0 with a data object on success, or a non-zero code with a msg string on failure. This differs from the status/results envelope used elsewhere in the catalog.',
  'The upstream applies its per-interface, per-minute call ceiling to the Eroya account rather than to the calling key, so this capacity is shared across all keys. Space out bulk extraction and retry on a throttled response.',
];

const longbridgeEnvelopeFields: Field[] = [
  { name: 'status', type: 'string', description: 'OK when the request completed and results are populated.' },
  { name: 'requestId', type: 'string', description: 'Eroya request identifier.' },
  { name: 'results', type: 'object | array', description: 'Normalized camelCase dataset payload.' },
  { name: 'errors[]', type: 'object', description: 'Partial failure details, when the request still returned usable results.' },
];

const languageParameter: Parameter = {
  name: 'language',
  type: 'string',
  description: 'Response language for names, descriptions, and narrative summaries: en (default), zh-CN, or zh-HK.',
  example: 'en',
};

const tradeSessionsParameter: Parameter = {
  name: 'sessions',
  type: 'string',
  description: 'regular (default) for the continuous session only, or all to include pre-market, post-market, and overnight bars.',
  example: 'regular',
};

const marketParameter: Parameter = {
  name: 'market',
  type: 'string',
  description: 'Market code: US, HK, CN, SH, SZ, SG, JP, or EU. Default HK.',
  example: 'HK',
};

const dateWindowParameters: Parameter[] = [
  { name: 'from', type: 'date', description: 'Start date in YYYY-MM-DD. Defaults to today.', example: '2026-08-01' },
  { name: 'to', type: 'date', description: 'End date in YYYY-MM-DD. Defaults to the start date.', example: '2026-08-31' },
];

/**
 * The market routes take genuinely different inputs from one another, so their
 * parameters are listed per route instead of defaulting every page to `market`.
 * Routes that read nothing but the shared response language list no parameters.
 */
function marketRouteParameters(slug: string): Parameter[] {
  switch (slug) {
    case 'status':
    case 'trading-sessions':
    case 'rank-categories':
      return [];
    case 'temperature':
      return [marketParameter];
    case 'temperature-history':
    case 'trading-days':
      return [marketParameter, ...dateWindowParameters];
    case 'unusual-items':
      return [
        marketParameter,
        { name: 'category', type: 'string', description: 'Upstream unusual-activity category code. Default 0 for all categories.', example: '0' },
      ];
    case 'top-movers':
      return [
        { name: 'markets', type: 'string', description: 'Comma-separated market codes. Default HK.', example: 'HK,US' },
        { name: 'limit', type: 'integer', description: 'Securities to return, 1 to 100. Default 20.', example: '20' },
        { name: 'sort', type: 'string', description: 'desc (default) or asc.', example: 'desc' },
        { name: 'date', type: 'date', description: 'Session to report in YYYY-MM-DD. Defaults to the latest session.', example: '2026-08-25' },
      ];
    case 'popularity':
      return [
        { name: 'key', type: 'string', required: true, description: 'Ranking key from Rank Categories. The ib_ prefix is optional.', example: 'hot_all-hk' },
        { name: 'includeArticles', type: 'boolean', description: 'Include the linked article for each ranked security. Default false.', example: 'false' },
      ];
    case 'broker-holdings':
      return [
        canonicalTickerParameter,
        { name: 'period', type: 'string', description: 'Change window: rct_1, rct_5, rct_20 (default), or rct_60.', example: 'rct_20' },
      ];
    case 'broker-holding-history':
      return [
        canonicalTickerParameter,
        { name: 'brokerId', type: 'string', required: true, description: 'Participant number from Broker Holdings.', example: 'B01130' },
      ];
    case 'ah-premium':
      return [
        canonicalTickerParameter,
        { name: 'period', type: 'string', description: '1m, 5m, 15m, 30m, 60m, day (default), week, month, or year. Upstream publishes 1m and day-and-above densely; the other intraday resolutions are often empty.', example: 'day' },
        { name: 'limit', type: 'integer', description: 'Bars to return, 1 to 1,000. Default 100.', example: '100' },
      ];
    default:
      return [canonicalTickerParameter];
  }
}

const canonicalTickerParameter: Parameter = {
  name: 'ticker',
  type: 'string',
  required: true,
  description: 'Canonical ticker. Use 700.HK, 000001.SZ, 600519.SH, or AAPL.US. HK:700 and zero-padded HK codes are normalized.',
  example: '700.HK',
};

/**
 * Longbridge routes whose U.S. coverage was verified against live responses.
 * Anything absent here returned an empty payload for every U.S. ticker tried,
 * or is structurally Hong Kong only: CCASS broker holdings, A/H premium,
 * warrants, index constituents, operating metrics, and the IPO and meeting
 * calendars. `/market/quotes` is excluded on purpose — U.S. quotes stay on the
 * dedicated U.S. routes.
 */
export const US_CAPABLE_LONGBRIDGE = new Set([
  'lb-security-static', 'lb-intraday', 'lb-candlesticks',
  'lb-analytics-capital-flow', 'lb-analytics-capital-distribution',
  'lb-analytics-calc-indexes', 'lb-analytics-filings',
  'lb-analytics-short-positions', 'lb-analytics-short-volume',
  'lb-fundamental-financial-report', 'lb-fundamental-analyst-ratings',
  'lb-fundamental-dividends', 'lb-fundamental-valuations',
  'lb-fundamental-company-profile', 'lb-fundamental-executives',
  'lb-fundamental-shareholders', 'lb-fundamental-fund-holdings',
  'lb-fundamental-corporate-actions', 'lb-fundamental-institution-rating',
  'lb-fundamental-institution-rating-detail', 'lb-fundamental-dividend-detail',
  'lb-fundamental-forecast-eps', 'lb-fundamental-financial-consensus',
  'lb-fundamental-valuation-history', 'lb-fundamental-industry-valuation',
  'lb-fundamental-industry-valuation-distribution', 'lb-fundamental-investment-relations',
  'lb-fundamental-buyback', 'lb-fundamental-business-segments',
  'lb-fundamental-business-segments-history', 'lb-fundamental-institutional-rating-views',
  'lb-fundamental-financial-report-snapshot', 'lb-fundamental-top-shareholders',
  'lb-fundamental-shareholder-detail', 'lb-fundamental-valuation-comparison',
  'lb-fundamental-etf-asset-allocation', 'lb-fundamental-industry-ranking',
  'lb-fundamental-macroeconomic-indicators', 'lb-fundamental-macroeconomic-history',
  'lb-market-status', 'lb-market-temperature', 'lb-market-temperature-history',
  'lb-market-trading-sessions', 'lb-market-trading-days', 'lb-market-top-movers',
  'lb-market-rank-categories', 'lb-market-popularity', 'lb-market-trade-statistics',
  'lb-calendar-earnings', 'lb-calendar-dividends', 'lb-calendar-splits',
  'lb-calendar-macro', 'lb-calendar-closures', 'lb-calendar-mergers',
  'lb-content-topics', 'lb-content-security-news',
]);

/**
 * A U.S. worked example for the cross-listed routes. The primary example stays
 * on a Hong Kong symbol because that is the catalog's home market, so a reader
 * who arrives from the U.S. sidebar needs one request they can run as-is.
 */
export function usExample(path: string, query: string): string {
  const usQuery = query
    .replace('tickers=700.HK,000001.SZ', 'tickers=AAPL.US,MSFT.US')
    .replace('tickers=700.HK', 'tickers=AAPL.US')
    .replace('ticker=700.HK', 'ticker=AAPL.US')
    .replace('ticker=000001.SZ', 'ticker=AAPL.US')
    .replace('key=hot_all-hk', 'key=hot_all-us')
    .replace('markets=HK,US', 'markets=US')
    .replace('market=HK', 'market=US')
    .replace('market=CN', 'market=US');
  return `U.S. example: /v1${path}${usQuery ? `?${usQuery}` : ''}`;
}

/** Human-readable market coverage, shown as an implementation note. */
export const LONGBRIDGE_MARKET_NOTE: Record<string, string> = {
  'lb-hk-quotes': 'Markets: HK and China A-shares. U.S. quotes stay on the U.S. stock routes.',
  'lb-market-index-constituents': 'Markets: HK and China A-shares. U.S. index constituents are not published upstream.',
  'lb-market-broker-holdings': 'Markets: HK only. Broker holdings come from HKEX CCASS.',
  'lb-market-broker-holding-detail': 'Markets: HK only. Broker holdings come from HKEX CCASS.',
  'lb-market-broker-holding-history': 'Markets: HK only. Broker holdings come from HKEX CCASS.',
  'lb-market-ah-premium': 'Markets: HK only, for issuers also listed in mainland China.',
  'lb-market-ah-premium-intraday': 'Markets: HK only, for issuers also listed in mainland China.',
  'lb-market-unusual-items': 'Markets: HK, CN, and US. The feed is empty outside an active anomaly.',
  'lb-fundamental-operating-metrics': 'Markets: HK and China A-shares. U.S. issuers return an empty list.',
  'lb-fundamental-industry-peer-hierarchy': 'Markets: HK and China A-shares, and only where the issuer publishes a hierarchy.',
  'lb-calendar-ipos': 'Markets: HK and China A-shares. No U.S. listings are published on this calendar.',
  'lb-calendar-meetings': 'Markets: HK and China A-shares. No U.S. meetings are published on this calendar.',
  'lb-warrant-quotes': 'Markets: HK only.',
  'lb-warrant-issuers': 'Markets: HK only.',
  'lb-warrant-screener': 'Markets: HK only.',
};

type LongbridgeDoc = {
  id: string;
  group: Endpoint['group'];
  title: string;
  path: string;
  summary: string;
  query?: string;
  parameters?: Parameter[];
  notes?: string[];
};

function longbridgeEndpoint(item: LongbridgeDoc): Endpoint {
  const queryString = item.query ?? (item.parameters?.some((parameter) => parameter.name === 'ticker') ? 'ticker=700.HK' : '');
  const usCapable = US_CAPABLE_LONGBRIDGE.has(item.id);
  const coverage = LONGBRIDGE_MARKET_NOTE[item.id]
    ?? (usCapable ? `Markets: HK, China A-shares, and US. ${usExample(item.path, queryString)}` : undefined);
  return {
    id: item.id,
    group: item.group,
    ...(usCapable && item.group !== 'U.S. Stocks' ? { markets: [item.group, 'U.S. Stocks' as const] } : {}),
    title: item.title,
    method: 'GET',
    path: item.path,
    summary: item.summary,
    description: `${item.summary} Eroya signs the private upstream request and returns the shared response envelope, so clients use only their Eroya API key.`,
    useCases: ['Market research', 'Screening', 'Data pipelines', 'Application integration'],
    parameters: [...(item.parameters ?? [canonicalTickerParameter]), languageParameter],
    fields: longbridgeEnvelopeFields,
    request: `https://api.eroya.co/v1${item.path}${queryString ? `?${queryString}` : ''}`,
    response: `{
  "status": "OK",
  "requestId": "42a04c8a-...",
  "results": { "ticker": "700.HK", "data": [] }
}`,
    notes: [
      'Every route in this catalog is read-only. Account, trading, and watchlist operations are not exposed.',
      ...(coverage ? [coverage] : []),
      ...(item.notes ?? []),
    ],
  };
}

const longbridgeEndpoints: Endpoint[] = [
  ...([
    ['lb-hk-quotes', 'Quotes', '/market/quotes', 'Real-time level-one quotes for HK and China A-share symbols.', 'tickers=700.HK,000001.SZ'],
    ['lb-security-static', 'Security Static Information', '/market/static', 'Names, exchange, currency, lot size, shares, EPS, and derivative flags.', 'tickers=700.HK,000001.SZ'],
    ['lb-intraday', 'Intraday Lines', '/market/intraday', 'Current-session intraday price, volume, turnover, and average-price lines.', 'ticker=700.HK'],
    ['lb-candlesticks', 'Historical Candlesticks', '/market/candlesticks', 'Historical OHLCV bars, including validated minute-level HK and China A-share history.', 'ticker=000001.SZ&period=1m&from=2026-08-24&to=2026-08-24'],
  ] as const).map(([id, title, path, summary, queryValue]) => longbridgeEndpoint({
    id, group: path === '/market/candlesticks' ? 'China A-shares' : 'Hong Kong Stocks', title, path, summary,
    query: queryValue,
    parameters: path === '/market/quotes' || path === '/market/static'
      ? [{ ...canonicalTickerParameter, name: 'tickers', description: 'Comma-separated canonical tickers; maximum 50.', example: '700.HK,000001.SZ' }]
      : [canonicalTickerParameter,
          ...(path === '/market/candlesticks' ? [
            { name: 'period', type: 'string', description: '1m, 2m, 3m, 5m, 10m, 15m, 20m, 30m, 45m, 60m (1h), 2h, 3h, 4h, day, week, month, quarter, or year. Default 1m.', example: '1m' },
            { name: 'from', type: 'date', description: 'History start in YYYY-MM-DD. Must be sent together with to.', example: '2026-08-24' },
            { name: 'to', type: 'date', description: 'History end in YYYY-MM-DD. Must be sent together with from.', example: '2026-08-24' },
            { name: 'limit', type: 'integer', description: 'Most recent bars to return when from and to are omitted, 1 to 1,000. Default 100.', example: '100' },
            { name: 'adjust', type: 'string', description: 'none (default) or forward for forward-adjusted prices.', example: 'none' },
            tradeSessionsParameter,
          ] : []),
          ...(path === '/market/intraday' ? [tradeSessionsParameter] : [])],
    notes: path === '/market/quotes' ? ['U.S. quotes remain on the existing U.S. stock routes.'] : undefined,
  })),

  ...([
    ['capital-flow', 'Capital Flow', 'Intraday net capital-flow time series.'],
    ['capital-distribution', 'Capital Distribution', 'Large, medium, and small order inflow and outflow distribution.'],
    ['calc-indexes', 'Calculated Indexes', 'Price change, turnover, valuation, flow, volatility, and derivative calculations.'],
    ['filings', 'Filings', 'Company filing and disclosure documents.'],
    ['short-positions', 'Short Positions', 'Reported short positions for HK and U.S. securities.'],
    ['short-volume', 'Daily Short Sale Volume', 'Daily short-sale trading records for HK and U.S. securities.'],
  ] as const).map(([slug, title, summary]) => longbridgeEndpoint({
    id: `lb-analytics-${slug}`,
    group: 'Hong Kong Stocks',
    title,
    path: `/market/analytics/${slug}`,
    summary,
    query: slug === 'calc-indexes' ? 'tickers=700.HK&indexes=last,changeRate,peTtm' : 'ticker=700.HK',
    parameters: slug === 'calc-indexes'
      ? [{ ...canonicalTickerParameter, name: 'tickers', description: 'Comma-separated canonical tickers.', example: '700.HK' },
          { name: 'indexes', type: 'string', description: 'Comma-separated calculated-index names.', example: 'last,changeRate,peTtm' }]
      : [canonicalTickerParameter],
  })),

  ...([
    ['financial-report', 'Financial Report', 'Income statements, balance sheets, and cash-flow reports.'],
    ['analyst-ratings', 'Analyst Ratings', 'Current analyst rating data.'],
    ['dividends', 'Dividends', 'Dividend history and key dates.'],
    ['valuations', 'Valuations', 'Current PE, PB, PS, and dividend-yield metrics.'],
    ['company-profile', 'Company Profile', 'Company identity, description, and profile data.'],
    ['executives', 'Executives', 'Executive and board-member information.'],
    ['shareholders', 'Shareholders', 'Major shareholder records.'],
    ['fund-holdings', 'Fund Holdings', 'Funds and ETFs holding the security.'],
    ['corporate-actions', 'Corporate Actions', 'Dividends, splits, buybacks, and other issuer events.'],
    ['institution-rating', 'Institution Rating', 'Latest institutional rating plus historical consensus summary.'],
    ['institution-rating-detail', 'Institution Rating Detail', 'Historical institution rating details.'],
    ['dividend-detail', 'Dividend Detail', 'Detailed dividend records.'],
    ['forecast-eps', 'Forecast EPS', 'Consensus EPS forecasts.'],
    ['financial-consensus', 'Financial Consensus', 'Detailed financial consensus estimates.'],
    ['valuation-history', 'Valuation History', 'Historical valuation observations.'],
    ['industry-valuation', 'Industry Valuation', 'Security valuation compared with its industry.'],
    ['industry-valuation-distribution', 'Industry Valuation Distribution', 'Valuation distribution within the industry.'],
    ['investment-relations', 'Investment Relations', 'Issuer investment-relations data.'],
    ['operating-metrics', 'Operating Metrics', 'Operating metrics and report summaries.'],
    ['buyback', 'Buyback', 'Issuer repurchase records.'],
    ['macroeconomic-indicators', 'Macroeconomic Indicators', 'Searchable macroeconomic indicator catalog.'],
    ['business-segments', 'Business Segments', 'Latest revenue and operating breakdown by segment.'],
    ['macroeconomic-history', 'Macroeconomic Historical Data', 'Historical observations for one macroeconomic indicator.'],
    ['business-segments-history', 'Business Segments History', 'Historical business-segment breakdowns.'],
    ['institutional-rating-views', 'Institutional Rating Views Timeline', 'Time series of institutional rating views.'],
    ['industry-ranking', 'Industry Ranking', 'Rank industries by a selected valuation or operating indicator.'],
    ['industry-peer-hierarchy', 'Industry Peer Hierarchy', 'Industry ancestry and peer relationships for a security.'],
    ['financial-report-snapshot', 'Financial Report Snapshot', 'Compact earnings and financial-report snapshot.'],
    ['top-shareholders', 'Top Shareholders', 'Ranked top-shareholder records.'],
    ['shareholder-detail', 'Shareholder Detail', 'Holding history for one shareholder object.'],
    ['valuation-comparison', 'Valuation Comparison', 'Compare valuation metrics with selected peers.'],
    ['etf-asset-allocation', 'ETF Asset Allocation', 'Holdings plus regional, asset-class, and industry allocation for a U.S., Hong Kong, or China-listed fund. Non-fund tickers return an empty set.'],
  ] as const).map(([slug, title, summary]) => {
    const noTicker = slug === 'macroeconomic-indicators' || slug === 'macroeconomic-history' || slug === 'industry-ranking';
    const summaryFor = slug === 'industry-ranking'
      ? 'Every industry in a market ranked by session change, with the leading security in each.'
      : summary;
    const parameters = noTicker
      ? slug === 'macroeconomic-history'
        ? [
            { name: 'indicatorId', type: 'string', required: true, description: 'Indicator ID from Macroeconomic Indicators.', example: '30771854' },
            { name: 'from', type: 'date', description: 'Start date in YYYY-MM-DD.', example: '2020-01-01' },
            { name: 'to', type: 'date', description: 'End date in YYYY-MM-DD.', example: '2026-08-31' },
            { name: 'limit', type: 'integer', description: 'Observations to return, 1 to 500. Default 100.', example: '50' },
            { name: 'sort', type: 'string', description: 'asc or desc by period. Default desc.', example: 'desc' },
          ]
        : slug === 'industry-ranking'
          ? [{ name: 'market', type: 'string', description: 'Market to rank: HK, CN, or US. Default HK.', example: 'HK' }]
          : [
              { name: 'market', type: 'string', description: 'Market or country filter such as HK, CN, US, or ALL. Default ALL.', example: 'CN' },
              { name: 'keyword', type: 'string', description: 'Free-text filter over indicator names.', example: 'PMI' },
              { name: 'limit', type: 'integer', description: 'Indicators to return, 1 to 500. Default 100.', example: '50' },
            ]
      : slug === 'financial-report'
        ? [canonicalTickerParameter,
            { name: 'kind', type: 'string', description: 'IS, BS, CF, or ALL. Default ALL.', example: 'ALL' },
            { name: 'period', type: 'string', description: 'af, saf, q1, q2, q3, qf, or 3q.', example: 'af' }]
        : slug === 'shareholder-detail'
          ? [canonicalTickerParameter,
              { name: 'shareholderId', type: 'string', required: true, description: 'Object ID from Top Shareholders.', example: '22785' }]
          : slug === 'valuation-comparison'
            ? [canonicalTickerParameter,
                { name: 'comparisonTickers', type: 'string', description: 'Comma-separated peer tickers.', example: '9988.HK,BABA.US' },
                { name: 'currency', type: 'string', description: 'Reporting currency. Defaults to the listing currency.', example: 'HKD' }]
            : [canonicalTickerParameter];
    return longbridgeEndpoint({
      id: `lb-fundamental-${slug}`,
      group: 'Global Markets',
      title,
      path: `/fundamentals/${slug}`,
      summary: summaryFor,
      query: slug === 'macroeconomic-history'
        ? 'indicatorId=30771854&limit=50'
        : slug === 'etf-asset-allocation'
          ? 'ticker=SPY.US'
          : slug === 'shareholder-detail'
            ? 'ticker=700.HK&shareholderId=22785'
            : slug === 'valuation-comparison'
              ? 'ticker=700.HK&comparisonTickers=9988.HK'
              : noTicker ? 'market=HK' : 'ticker=700.HK',
      parameters,
      notes: [
        'Financial reports and the fundamental routes accept HK, China A-share, and U.S. tickers.',
        ...(slug === 'industry-peer-hierarchy'
          ? ['Peer hierarchies are published per issuer; securities without one return an empty chain.']
          : []),
      ],
    });
  }),

  ...([
    ['status', 'Market Status', 'Current trading status for supported markets.'],
    ['temperature', 'Market Temperature', 'Current breadth and market-temperature measures.'],
    ['temperature-history', 'Historical Temperature', 'Historical market-temperature observations.'],
    ['trading-sessions', 'Trading Sessions', 'Daily trading-session time windows by market.'],
    ['trading-days', 'Trading Days', 'Full and half trading days in a date range.'],
    ['unusual-items', 'Unusual Items', 'Unusual price and volume events.'],
    ['top-movers', 'Top Movers', 'Market-wide securities with unusual movements.'],
    ['rank-categories', 'Rank Categories', 'Available popularity and ranking categories.'],
    ['popularity', 'Popularity Leaderboard', 'Ranked securities for a selected category.'],
    ['broker-holdings', 'Broker Holdings', 'Top broker holding changes for an HK security.'],
    ['broker-holding-detail', 'Broker Holding Detail', 'Full broker holding details for an HK security.'],
    ['broker-holding-history', 'Broker Holding History', 'Daily holding history for one broker.'],
    ['ah-premium', 'A/H Premium History', 'Historical A/H premium bars for a dual-listed Hong Kong security.'],
    ['ah-premium-intraday', 'A/H Premium Intraday', 'Current-session A/H premium observations for a dual-listed Hong Kong security.'],
    ['trade-statistics', 'Trade Statistics', 'Buy, sell, and neutral trade statistics.'],
    ['index-constituents', 'Index Constituents', 'Constituent securities for an index such as HSI.HK.'],
  ] as const).map(([slug, title, summary]) => longbridgeEndpoint({
    id: `lb-market-${slug}`,
    group: 'Global Markets',
    title,
    path: `/market/${slug}`,
    summary,
    query: slug === 'trading-days' || slug === 'temperature-history' ? 'market=HK&from=2026-08-01&to=2026-08-31'
      : slug === 'popularity' ? 'key=hot_all-hk'
        : slug === 'index-constituents' ? 'ticker=HSI.HK'
          : slug === 'broker-holding-history' ? 'ticker=700.HK&brokerId=B01130'
            : slug.startsWith('ah-premium') ? 'ticker=2318.HK'
              : slug.startsWith('broker-') || slug === 'trade-statistics' ? 'ticker=700.HK'
                : slug === 'trading-sessions' || slug === 'status' || slug === 'rank-categories' ? '' : 'market=HK',
    parameters: marketRouteParameters(slug),
    notes: slug === 'ah-premium' || slug === 'ah-premium-intraday'
      ? ['Pass the Hong Kong side of a dual-listed pair, such as 2318.HK. The A-share counter of the same issuer returns an empty series.']
      : undefined,
  })),

  ...([
    ['earnings', 'Earnings Calendar', 'Earnings releases'],
    ['dividends', 'Dividend Calendar', 'Dividend events'],
    ['splits', 'Split Calendar', 'Stock splits'],
    ['ipos', 'IPO Calendar', 'IPO listings'],
    ['macro', 'Macro Calendar', 'Macroeconomic releases'],
    ['closures', 'Market Closure Calendar', 'Exchange closure days'],
    ['meetings', 'Meeting Calendar', 'Shareholder and analyst meetings'],
    ['mergers', 'Merger Calendar', 'Mergers and consolidations'],
  ] as const).map(([slug, title, subject]) => longbridgeEndpoint({
    id: `lb-calendar-${slug}`,
    group: 'Global Markets',
    title,
    path: `/calendar/${slug}`,
    summary: `${subject} over a requested date window.`,
    query: 'from=2026-08-01&to=2026-08-31&markets=HK,US',
    parameters: [
      { name: 'from', type: 'date', description: 'Start date in YYYY-MM-DD. Defaults to today.', example: '2026-08-01' },
      { name: 'to', type: 'date', description: 'End date in YYYY-MM-DD. Defaults to the start date.', example: '2026-08-31' },
      { name: 'markets', type: 'string', description: 'Comma-separated market codes. Omit for every supported market.', example: 'HK,US' },
    ],
  })),

  // U.S. company and fund fundamentals are served from a dedicated U.S. data
  // centre, so they carry their own slug family and require .US tickers.
  ...([
    ['us-company-overview', 'U.S. Company Overview', 'Business summary, market capitalization, and ranking tags for a U.S. listing.', ''],
    ['us-valuation-overview', 'U.S. Valuation Overview', 'Valuation percentile, industry rank, and narrative valuation summary.', ''],
    ['us-financial-overview', 'U.S. Financial Overview', 'Multi-year revenue, income, margin, and balance-sheet summary.', 'period'],
    ['us-financial-statement', 'U.S. Financial Statement', 'Line-item income statement, balance sheet, or cash-flow statement.', 'kind'],
    ['us-key-metrics', 'U.S. Key Financial Metrics', 'Profitability, growth, solvency, and efficiency metric history.', 'period'],
    ['us-analyst-consensus', 'U.S. Analyst Consensus', 'Actual versus estimated EPS, revenue, and EBIT by reporting period.', 'period'],
    ['us-company-dividends', 'U.S. Company Dividends', 'Dividend and buyback yield history for a U.S. company.', ''],
    ['us-etf-dividends', 'U.S. ETF Dividends', 'Trailing and fiscal-year dividend history for a U.S. ETF.', 'fund'],
    ['us-etf-files', 'U.S. ETF Documents', 'Prospectus, annual, and semi-annual report links for a U.S. ETF.', 'files'],
  ] as const).map(([slug, title, summary, shape]) => {
    const usTicker: Parameter = {
      ...canonicalTickerParameter,
      description: 'Canonical U.S. ticker. These datasets are U.S.-only.',
      example: shape === 'fund' || shape === 'files' ? 'SPY.US' : 'AAPL.US',
    };
    const period: Parameter = {
      name: 'period',
      type: 'string',
      description: slug === 'us-financial-statement'
        ? 'Reporting period: af, saf, q1, q2, q3, qf, or 3q. The plain-language forms annual, quarterly, and ttm are also accepted. Default af.'
        : 'Reporting period: af, saf, q1, q2, q3, qf, or 3q. Default af.',
      example: 'af',
    };
    return longbridgeEndpoint({
      id: `lb-fundamental-${slug}`,
      group: 'U.S. Stocks',
      title,
      path: `/fundamentals/${slug}`,
      summary,
      query: shape === 'kind' ? 'ticker=AAPL.US&kind=IS&period=af'
        : shape === 'period' ? 'ticker=AAPL.US&period=af'
          : shape === 'files' ? 'ticker=SPY.US&limit=20'
            : `ticker=${shape === 'fund' ? 'SPY.US' : 'AAPL.US'}`,
      parameters: [
        usTicker,
        ...(shape === 'kind' ? [{ name: 'kind', type: 'string', description: 'IS, BS, or CF. Default IS.', example: 'IS' }, period] : []),
        ...(shape === 'period' ? [period] : []),
        ...(shape === 'files' ? [{ name: 'limit', type: 'integer', description: 'Documents to return, 1 to 100. Default 20.', example: '20' }] : []),
      ],
      notes: [
        'Requests with a non-U.S. ticker return 400. HK and China A-share fundamentals use the shared /fundamentals routes.',
        ...(slug === 'us-financial-statement'
          ? [
            'Eroya resolves this dataset across more than one upstream and returns the first that carries the security, so the response is a single latest-available answer rather than a merge.',
            'period=ttm returns a trailing-twelve-month restatement of the income or cash-flow statement. A balance sheet is a point-in-time statement, so kind=BS with period=ttm returns 400.',
            'The dedicated /fundamentals/us-income-statement, /us-balance-sheet, and /us-cash-flow routes return the same data with one statement per route.',
          ]
          : []),
      ],
    });
  }),

  // U.S. fundamentals that resolve across two upstreams. Eroya tries the
  // contracted feed first and consults the public one only when that returns
  // nothing, so a caller sees one shape and one latest-available answer.
  ...([
    ['us-income-statement', 'U.S. Income Statement', 'Line-item income statement by fiscal period, including a trailing-twelve-month restatement.', 'statement'],
    ['us-balance-sheet', 'U.S. Balance Sheet', 'Line-item balance sheet by fiscal period.', 'balance'],
    ['us-cash-flow', 'U.S. Cash Flow Statement', 'Line-item cash-flow statement by fiscal period, including a trailing-twelve-month restatement.', 'statement'],
    ['us-earnings', 'U.S. Earnings Summary', 'Reported versus estimated EPS by quarter, with the revenue and earnings history behind it.', 'plain'],
    ['us-earnings-calendar', 'U.S. Earnings Calendar', 'Next scheduled earnings date and call, with the consensus EPS and revenue around it.', 'plain'],
    ['us-earnings-dates', 'U.S. Earnings Dates', 'Past and scheduled earnings events with estimated EPS, reported EPS, and surprise.', 'limit'],
    ['us-earnings-history', 'U.S. Earnings History', 'Reported EPS against estimate, with the difference and surprise percentage, by quarter.', 'plain'],
    ['us-sec-filings', 'U.S. SEC Filings', 'Recent SEC filings with the filed exhibit documents for each.', 'limit'],
    ['us-recommendations', 'U.S. Analyst Recommendations', 'Current analyst recommendation distribution with the consensus rating.', 'plain'],
    ['us-recommendation-summary', 'U.S. Recommendation Summary', 'Recommendation distribution over the trailing months, for tracking how a consensus moved.', 'plain'],
    ['us-upgrades-downgrades', 'U.S. Upgrades and Downgrades', 'Dated analyst rating actions by firm, with the price target before and after.', 'limit'],
    ['us-analyst-price-targets', 'U.S. Analyst Price Targets', 'Low, mean, median, and high price targets against the current price.', 'plain'],
    ['us-earnings-estimate', 'U.S. Earnings Estimate', 'Consensus EPS estimate by forecast period, with the analyst count and spread.', 'plain'],
    ['us-revenue-estimate', 'U.S. Revenue Estimate', 'Consensus revenue estimate by forecast period, with the analyst count and spread.', 'plain'],
    ['us-eps-trend', 'U.S. EPS Trend', 'How the consensus EPS estimate for each forecast period moved over the last 90 days.', 'plain'],
    ['us-eps-revisions', 'U.S. EPS Revisions', 'Count of upward and downward EPS estimate revisions by forecast period.', 'plain'],
    ['us-growth-estimates', 'U.S. Growth Estimates', 'Expected earnings growth rate by forecast period.', 'plain'],
    ['us-sustainability', 'U.S. Sustainability Scores', 'Environmental, social, and governance risk scores and controversy levels.', 'plain'],
    ['us-insider-transactions', 'U.S. Insider Transactions', 'Dated insider buys and sells with filer, relationship, share count, and value.', 'limit'],
    ['us-insider-purchases', 'U.S. Insider Purchase Activity', 'Aggregate insider buying and selling over the trailing six months.', 'plain'],
    ['us-insider-roster', 'U.S. Insider Roster', 'Registered insiders with their role, latest transaction, and holdings.', 'plain'],
    ['us-major-holders', 'U.S. Major Holders Breakdown', 'Share of the register held by insiders and by institutions.', 'plain'],
    ['us-institutional-holders', 'U.S. Institutional Holders', 'Institutional holders with position size, value, and period change.', 'limit'],
    ['us-mutualfund-holders', 'U.S. Mutual Fund Holders', 'Mutual fund holders with position size, value, and period change.', 'limit'],
    ['us-fund-profile', 'U.S. Fund Profile and Holdings', 'ETF or mutual fund family, category, fees, asset allocation, and top holdings.', 'fund'],
  ] as const).map(([slug, title, summary, shape]) => {
    const usTicker: Parameter = {
      ...canonicalTickerParameter,
      description: 'Canonical U.S. ticker. These datasets are U.S.-only.',
      example: shape === 'fund' ? 'SPY.US' : 'AAPL.US',
    };
    const statementPeriod: Parameter = {
      name: 'period',
      type: 'string',
      description: shape === 'balance'
        ? 'Reporting period: annual (default) or quarterly. A balance sheet is a point-in-time statement, so ttm is rejected.'
        : 'Reporting period: annual (default), quarterly, or ttm for a trailing-twelve-month restatement.',
      example: 'annual',
    };
    const limit: Parameter = {
      name: 'limit',
      type: 'integer',
      description: 'Rows to return, 1 to 200.',
      example: '50',
    };
    const hasPeriod = shape === 'statement' || shape === 'balance';
    return longbridgeEndpoint({
      id: `us-fundamental-${slug}`,
      group: 'U.S. Stocks',
      title,
      path: `/fundamentals/${slug}`,
      summary,
      query: hasPeriod ? 'ticker=AAPL.US&period=annual'
        : shape === 'limit' ? 'ticker=AAPL.US&limit=50'
          : `ticker=${shape === 'fund' ? 'SPY.US' : 'AAPL.US'}`,
      parameters: [
        usTicker,
        ...(hasPeriod ? [statementPeriod] : []),
        ...(shape === 'limit' ? [limit] : []),
      ],
      notes: [
        'Markets: US only. A non-U.S. ticker returns 400.',
        'Eroya resolves this dataset across more than one upstream and returns the first that carries the security, so the response is a single latest-available answer rather than a merge.',
        ...(slug === 'us-sustainability'
          ? ['ESG scoring is currently withdrawn upstream, so this route returns an empty result for most securities until it is republished.']
          : []),
        ...(slug === 'us-fund-profile'
          ? ['Requires a fund ticker. A non-fund security returns an empty result.']
          : []),
      ],
    });
  }),

  longbridgeEndpoint({
    id: 'lb-content-topics', group: 'Global Markets', title: 'Topics by Symbol', path: '/content/topics',
    summary: 'Read-only content topics associated with a security.', query: 'ticker=700.HK',
    notes: ['My Topics is intentionally not exposed because it is account-scoped.'],
  }),
  longbridgeEndpoint({
    id: 'lb-content-security-news',
    group: 'Global Markets',
    title: 'Security News',
    path: '/content/security-news',
    summary: 'Single-upstream company news for one security, in the same normalized article shape as /reference/news.',
    query: 'ticker=700.HK&limit=50',
    parameters: [
      canonicalTickerParameter,
      { name: 'limit', type: 'integer', description: 'Articles to return, 1 to 100. Default 50.', example: '50' },
    ],
    notes: ['Use /reference/news to merge every upstream that covers the security into one deduplicated list.'],
  }),
  ...([
    ['quotes', 'Warrant Quotes', 'Real-time quotes for one or more HK warrants.', 'tickers=12345.HK'],
    ['issuers', 'Warrant Issuers', 'Reference list of Hong Kong warrant issuers.', ''],
    ['screener', 'Warrant Screener', 'Filter and rank warrants for an HK underlying.', 'ticker=700.HK&limit=50'],
  ] as const).map(([slug, title, summary, queryValue]) => longbridgeEndpoint({
    id: `lb-warrant-${slug}`,
    group: 'Hong Kong Stocks',
    title,
    path: `/warrants/${slug}`,
    summary,
    query: queryValue,
    parameters: slug === 'issuers' ? [] : [
      { ...canonicalTickerParameter, name: slug === 'quotes' ? 'tickers' : 'ticker', example: slug === 'quotes' ? '12345.HK' : '700.HK' },
      ...(slug === 'screener' ? [{ name: 'limit', type: 'integer', description: 'Maximum screener rows; zero requests the provider default.', example: '50' }] : []),
    ],
  })),
];

/**
 * Streaming pages.
 *
 * The WebSocket feed is its own product rather than a section of the REST
 * catalog: it is a different transport, a different connection lifecycle, and a
 * different entitlement, and mixing it into the REST tree would put a
 * long-lived socket next to a request/response call as if a reader could
 * substitute one for the other.
 */
const WS_STOCKS = 'wss://delayed.eroya.co/stocks';
const WS_OPTIONS = 'wss://delayed.eroya.co/options';
const WS_INDICES = 'wss://delayed.eroya.co/indices';

const streamUseCases = ['Real-time monitoring', 'Dynamic charting', 'Intraday strategy development', 'Automated trading'];

/** Every stream takes the same one parameter, described per asset class. */
function tickerParameter(market: 'stocks' | 'options'): Parameter[] {
  return [
    {
      name: 'ticker',
      type: 'string',
      required: true,
      description: market === 'stocks'
        ? 'A stock ticker, a comma-separated list of them, or * for every ticker. Subscriptions are sent after authentication as {"action":"subscribe","params":"EVENT.TICKER"}.'
        : 'An O: option contract, a comma-separated list of them, or * for every contract. Subscriptions are sent after authentication as {"action":"subscribe","params":"EVENT.CONTRACT"}.',
      example: market === 'stocks' ? '*' : 'O:SPY251219C00650000',
    },
  ];
}

/** Shared OHLC members of both aggregate events, in the order the feed sends them. */
function streamAggregateFields(market: 'stocks' | 'options', event: 'A' | 'AM'): Field[] {
  const fractional: Field[] = market === 'stocks'
    ? [
      { name: 'dv', type: 'string', description: 'The tick volume including fractional shares, as a decimal string.' },
      { name: 'dav', type: 'string', description: "Today's accumulated volume including fractional shares, as a decimal string." },
    ]
    : [];
  return [
    { name: 'ev', type: `enum (${event})`, description: 'The event type.' },
    { name: 'sym', type: 'string', description: market === 'stocks' ? 'The ticker symbol.' : 'The option contract symbol.' },
    { name: 'v', type: 'integer', description: 'The tick volume.' },
    ...fractional.slice(0, 1),
    { name: 'av', type: 'integer', description: "Today's accumulated volume." },
    ...fractional.slice(1),
    { name: 'op', type: 'number', description: "Today's official opening price." },
    { name: 'vw', type: 'number', description: "The tick's volume weighted average price." },
    { name: 'o', type: 'number', description: 'The opening tick price for this aggregate window.' },
    { name: 'c', type: 'number', description: 'The closing tick price for this aggregate window.' },
    { name: 'h', type: 'number', description: 'The highest tick price for this aggregate window.' },
    { name: 'l', type: 'number', description: 'The lowest tick price for this aggregate window.' },
    { name: 'a', type: 'number', description: "Today's volume weighted average price." },
    { name: 'z', type: 'integer', description: 'The average trade size for this aggregate window.' },
    { name: 's', type: 'integer', description: 'The start timestamp of this aggregate window in Unix milliseconds.' },
    { name: 'e', type: 'integer', description: 'The end timestamp of this aggregate window in Unix milliseconds.' },
    ...(market === 'stocks'
      ? [{ name: 'otc', type: 'boolean', description: 'Whether this aggregate is for an OTC ticker. Omitted when false.' }]
      : []),
  ];
}

/** The three-step handshake, rendered per stream so each page is self-contained. */
function streamExamples(url: string, topic: string): Partial<Record<'curl' | 'python' | 'typescript', string>> {
  return {
    curl: [
      '# Connect',
      'npx wscat -c ' + url,
      '',
      '# Authenticate',
      '{"action":"auth","params":"YOUR_EROYA_API_KEY"}',
      '',
      '# Subscribe',
      '{"action":"subscribe","params":"' + topic + '"}',
    ].join('\n'),
    python: [
      'import asyncio, json, os',
      'import websockets',
      '',
      '',
      'async def stream():',
      '    async with websockets.connect("' + url + '") as socket:',
      '        await socket.send(json.dumps({"action": "auth", "params": os.environ["EROYA_API_KEY"]}))',
      '        await socket.send(json.dumps({"action": "subscribe", "params": "' + topic + '"}))',
      '        async for message in socket:',
      '            for event in json.loads(message):',
      '                print(event)',
      '',
      '',
      'asyncio.run(stream())',
    ].join('\n'),
    typescript: [
      'import WebSocket from "ws";',
      '',
      'const socket = new WebSocket("' + url + '");',
      '',
      'socket.on("open", () => {',
      '  socket.send(JSON.stringify({ action: "auth", params: process.env.EROYA_API_KEY }));',
      '  socket.send(JSON.stringify({ action: "subscribe", params: "' + topic + '" }));',
      '});',
      '',
      'socket.on("message", (data) => {',
      '  for (const event of JSON.parse(data.toString())) console.log(event);',
      '});',
    ].join('\n'),
  };
}

const streamNotes = [
  'Every server message is a JSON array, including status messages and single events. Iterate the array rather than treating the frame as one object.',
  'Authenticate before subscribing. A subscribe sent first is answered with {"ev":"status","status":"error","message":"not authenticated"}, and a connection that never authenticates is closed.',
  'One connection can hold subscriptions to several event types at once: {"action":"subscribe","params":"A.*,AM.*,T.*"}.',
  'Stocks stream in real time (A, AM, T and Q). Options (A, AM) and indices (AM, V) are 15-minute delayed. Event timestamps always retain the original market time.',
  'The existing delayed.eroya.co hostname also serves real-time stocks; delivery timing depends on the market, not the hostname.',
  'Each client has independent topic filters. The server continuously receives the configured feeds; subscribing or disconnecting does not change other clients. New subscriptions receive subsequent updates without historical replay.',
  'Reconnect with backoff and re-subscribe: subscriptions live on the connection, not on the key.',
];

const indexStreamEndpoints: Endpoint[] = (['AM', 'A', 'V'] as const).map((event) => {
  const value = event === 'V';
  return {
    id: `ws-indices-${value ? 'value' : event === 'AM' ? 'aggregates-minute' : 'aggregates-second'}`,
    group: 'WebSocket', title: value ? 'Index Values' : `Index Aggregates (Per ${event === 'AM' ? 'Minute' : 'Second'})`,
    method: 'GET', path: '/indices', resourceLabel: 'WS', resourcePath: WS_INDICES,
    summary: value ? 'Index value updates with the I: ticker and market timestamp.' : 'Index OHLC bars derived from index values.',
    description: value ? 'Stream index values for I:SPX, I:DJI, I:NDX, I:VIX or any subscribed index. Each value event identifies its ticker in T and its value in val.'
      : 'Stream index OHLC aggregates derived from index values rather than trades. A window without an index update emits no bar. Timestamps are Unix milliseconds.',
    useCases: streamUseCases,
    parameters: [{name:'ticker', type:'string', required:true,
      description:'Use an I: index ticker, a comma-separated list, or *. Prefix each subscription with the event, for example AM.I:SPX,AM.I:NDX.', example:'I:SPX'}],
    fields: value ? [
      {name:'ev',type:'enum (V)',description:'The value event type.'},
      {name:'T',type:'string',description:'The index ticker, including I: prefix.'},
      {name:'val',type:'number',description:'The index value.'},
      {name:'t',type:'integer',description:'Market timestamp in Unix milliseconds.'},
    ] : [
      {name:'ev',type:`enum (${event})`,description:'The aggregate event type.'},
      {name:'sym',type:'string',description:'The index ticker, including I: prefix.'},
      {name:'op',type:'number',description:"Today’s official opening index value."},
      ...(['o','c','h','l'] as const).map((name) => ({name,type:'number',description:({o:'Opening',c:'Closing',h:'Highest',l:'Lowest'})[name] + ' index value for the window.'})),
      {name:'s',type:'integer',description:'Window start in Unix milliseconds.'},
      {name:'e',type:'integer',description:'Window end in Unix milliseconds.'},
    ],
    request: WS_INDICES,
    response: JSON.stringify(value ? {ev:event,T:'I:SPX',val:3988.5,t:1678220098130}
      : {ev:event,sym:'I:SPX',op:3985.67,o:3985.67,c:3988.5,h:3988.5,l:3985.67,s:1678220040000,e:1678220100000},null,2),
    codeExamples: streamExamples(WS_INDICES, `${event}.I:SPX`),
    notes: [event === 'A' ? 'Index second bars are not currently available. The indices service carries AM minute bars and V values with a 15-minute delay.'
      : 'Included on Indices Starter with a 15-minute delay. The gateway defaults to AM and V.',
      'Wildcards: AM.* and V.*. Multiple tickers: V.I:SPX,V.I:NDX. Values use T; aggregate bars use sym.', ...streamNotes],
  };
});

const additionalTickStreams: Endpoint[] = ([['stocks','Q'], ['options','T'], ['options','Q']] as const).map(([market,event]) => {
  const quote = event === 'Q', url = market === 'stocks' ? WS_STOCKS : WS_OPTIONS;
  return {
    id:`ws-${market}-${quote ? 'quotes' : 'trades'}`, group:'WebSocket',
    title:`${market === 'stocks' ? 'Stock' : 'Option'} ${quote ? 'Quotes' : 'Trades'}`,
    method:'GET', path:`/${market}`, resourceLabel:'WS', resourcePath:url,
    summary:quote ? 'Best bid and ask updates for subscribed symbols.' : 'Tick-level option trades.',
    description:quote ? 'Stream bid and ask prices, sizes, exchange identifiers and timestamps for subscribed symbols. Channel access requires a matching upstream entitlement.'
      : 'Stream option trade prices, sizes, exchange identifiers, conditions and timestamps. Delayed trades require Options Developer or higher.',
    useCases:streamUseCases, parameters:tickerParameter(market),
    fields:[{name:'ev',type:`enum (${event})`,description:'The event type.'},
      {name:'sym',type:'string',description:'The stock or O: contract ticker.'},
      ...(quote ? ['bp','bs','bx','ap','as','ax'] : ['p','s','x']).map((name) => ({name,type:'number',description:({bp:'Bid price',bs:'Bid size',bx:'Bid exchange',ap:'Ask price',as:'Ask size',ax:'Ask exchange',p:'Trade price',s:'Trade size',x:'Trade exchange'} as Record<string,string>)[name]})),
      {name:'t',type:'integer',description:'Timestamp in Unix milliseconds.'},
      {name:'q',type:'integer',description:'Sequence number.'},
      ...(!quote ? [{name:'c',type:'array (integer)',description:'Trade conditions.'}] : []),
    ],
    request:url,
    response:JSON.stringify(quote ? {ev:event,sym:market === 'stocks' ? 'AAPL' : 'O:SPY261218C00650000',bp:1.5,bs:2,bx:65,ap:1.6,as:3,ax:65,t:1678220098130,q:1}
      : {ev:event,sym:'O:SPY261218C00650000',p:1.54,s:1,x:65,c:[],t:1678220098130,q:1},null,2),
    codeExamples:streamExamples(url,`${event}.${market === 'stocks' ? 'AAPL' : 'O:SPY261218C00650000'}`),
    notes:[market === 'stocks' ? 'Stock quotes are available in real time. Customer trades and quotes require Premium.' : 'Option trades and quotes are not currently enabled on this WebSocket service. Check /api/ws/status for available channels. Customer tick streams require Premium.',
      ...(market === 'options' && quote ? ['Option quotes require explicit active contracts from the server-configured list, limited to 1,000 contracts. Q.* is unavailable.'] : []),
      'Option contract examples are illustrative. Resolve an active contract through the Options Contracts REST endpoint.',...streamNotes],
  };
});

const websocketEndpoints: Endpoint[] = [
  {
    id: 'ws-overview',
    group: 'WebSocket',
    title: 'Streaming Overview',
    method: 'GET',
    path: '/stream',
    resourceLabel: 'WS',
    resourcePath: 'wss://delayed.eroya.co/stream',
    summary: 'One connection for stocks, options and indices, authenticated with your Eroya key.',
    description: 'Eroya streams U.S. stocks, options and indices together at /stream. Prefix options with O:: and indices with I::; unprefixed topics select stocks. The connection lifecycle is three steps: connect, authenticate with your Eroya API key, then subscribe to topics such as AM.AAPL,O::AM.*,I::V.*. The server replies to every command with a status frame, and from then on pushes arrays of events as they are published. Stocks stream in real time; options and indices remain 15-minute delayed.',
    useCases: ['Live dashboards', 'Intraday signals', 'Streaming ingestion', 'Order-flow monitoring'],
    parameters: [
      { name: 'action', type: 'string', required: true, description: 'auth, subscribe, or unsubscribe.', example: 'subscribe' },
      { name: 'params', type: 'string', required: true, description: 'For auth, your Eroya API key. For subscribe and unsubscribe, comma-separated topics: AM.AAPL for stocks, O::AM.* for options, I::V.* for indices.', example: 'I::V.*,O::AM.*' },
    ],
    fields: [
      { name: 'ev', type: 'enum (status)', description: 'Status frames carry the string "status"; every other value is market data.' },
      { name: 'status', type: 'string', description: 'connected, auth_success, auth_failed, auth_timeout, success, error, or max_connections.' },
      { name: 'message', type: 'string', description: 'Human-readable detail, such as "subscribed to: AM.*".' },
    ],
    request: 'wss://delayed.eroya.co/stream',
    response: `[{"ev":"status","status":"connected","message":"Connected Successfully"}]
[{"ev":"status","status":"auth_success","message":"authenticated"}]
[{"ev":"status","status":"success","message":"subscribed to: AM.*"}]`,
    codeExamples: streamExamples('wss://delayed.eroya.co/stream', 'I::V.*,O::AM.*'),
    notes: [
      'Use wss://delayed.eroya.co/stream for mixed topics. Existing /stocks, /options and /indices endpoints continue accepting their original EVENT.SYMBOL syntax.',
      'Event types include A (second bars), AM (minute bars), V (index values), T (trades), Q (quotes), subject to product entitlements. Indices default to I::V.*; an individual index updates only when its source publishes a value.',
      ...streamNotes,
      'The gateway pings every 25 seconds. Client libraries answer automatically; a hand-rolled client must reply with a pong or it will be dropped.',
    ],
  },
  {
    id: 'ws-stocks-aggregates-minute',
    group: 'WebSocket',
    title: 'Stock Aggregates (Per Minute)',
    method: 'GET',
    path: '/stocks',
    resourceLabel: 'WS',
    resourcePath: WS_STOCKS,
    summary: 'Minute OHLCV bars for U.S. equities, streamed as each minute closes.',
    description: 'Stream minute-by-minute aggregated OHLC and volume data for the tickers you subscribe to. Bars are built from qualifying trades in Eastern Time and cover pre-market, regular, and after-hours sessions. A minute with no eligible trades emits no bar, so the stream is sparse for illiquid names.',
    useCases: streamUseCases,
    parameters: tickerParameter('stocks'),
    fields: streamAggregateFields('stocks', 'AM'),
    request: WS_STOCKS,
    response: `{
  "ev": "AM",
  "sym": "GTE",
  "v": 4110,
  "av": 9470157,
  "op": 0.4372,
  "vw": 0.4488,
  "o": 0.4488,
  "c": 0.4486,
  "h": 0.4489,
  "l": 0.4486,
  "a": 0.4352,
  "z": 685,
  "s": 1610144640000,
  "e": 1610144700000
}`,
    codeExamples: streamExamples(WS_STOCKS, 'AM.*'),
    notes: [
      'Subscribe to AM.* for every ticker, or name them: AM.MSFT,AM.AAPL.',
      ...streamNotes,
    ],
  },
  {
    id: 'ws-stocks-aggregates-second',
    group: 'WebSocket',
    title: 'Stock Aggregates (Per Second)',
    method: 'GET',
    path: '/stocks',
    resourceLabel: 'WS',
    resourcePath: WS_STOCKS,
    summary: 'Second-by-second OHLCV bars for U.S. equities.',
    description: 'Stream second-by-second aggregated OHLC and volume data for the tickers you subscribe to. The bar shape is identical to the per-minute stream; only the window differs. Seconds with no qualifying trade emit nothing, so a full-market subscription is bursty around the open and thin overnight.',
    useCases: streamUseCases,
    parameters: tickerParameter('stocks'),
    fields: streamAggregateFields('stocks', 'A'),
    request: WS_STOCKS,
    response: `{
  "ev": "A",
  "sym": "SPCE",
  "v": 200,
  "av": 8642007,
  "op": 25.66,
  "vw": 25.3981,
  "o": 25.39,
  "c": 25.39,
  "h": 25.39,
  "l": 25.39,
  "a": 25.3714,
  "z": 50,
  "s": 1610144868000,
  "e": 1610144869000
}`,
    codeExamples: streamExamples(WS_STOCKS, 'A.*'),
    notes: [
      'Full-market subscriptions can be high volume. Subscribe to the tickers you need unless you are ingesting the full tape.',
      ...streamNotes,
    ],
  },
  {
    id: 'ws-stocks-trades',
    group: 'WebSocket',
    title: 'Stock Trades',
    method: 'GET',
    path: '/stocks',
    resourceLabel: 'WS',
    resourcePath: WS_STOCKS,
    summary: 'Tick-level U.S. equity executions with exchange, conditions, and timestamps.',
    description: 'Stream individual trades as they are reported: price, size, exchange, tape, trade conditions, and both SIP and participant timestamps. This is the raw tape the aggregate streams are built from, and the right subscription when conditions or venue matter.',
    useCases: ['Live monitoring', 'Algorithmic trading', 'Market microstructure analysis', 'Data visualization'],
    parameters: tickerParameter('stocks'),
    fields: [
      { name: 'ev', type: 'enum (T)', description: 'The event type.' },
      { name: 'sym', type: 'string', description: 'The ticker symbol.' },
      { name: 'x', type: 'integer', description: 'The exchange ID.' },
      { name: 'i', type: 'string', description: 'The trade ID.' },
      { name: 'z', type: 'integer', description: 'The tape: 1 = NYSE, 2 = AMEX, 3 = Nasdaq.' },
      { name: 'p', type: 'number', description: 'The trade price.' },
      { name: 's', type: 'integer', description: 'The trade size.' },
      { name: 'ds', type: 'string', description: 'The trade size including fractional shares, as a string.' },
      { name: 'c', type: 'array (integer)', description: 'The trade condition codes.' },
      { name: 't', type: 'integer', description: 'The SIP timestamp in Unix milliseconds.' },
      { name: 'pt', type: 'integer', description: 'The participant timestamp in Unix milliseconds. Always at or before t.' },
      { name: 'q', type: 'integer', description: 'The sequence number. Increasing and unique per symbol, but not contiguous.' },
      { name: 'trfi', type: 'integer', description: 'The ID of the Trade Reporting Facility that carried the trade.' },
      { name: 'trft', type: 'integer', description: 'The TRF timestamp in Unix milliseconds.' },
    ],
    request: WS_STOCKS,
    response: `{
  "ev": "T",
  "sym": "MSFT",
  "x": 4,
  "i": "12345",
  "z": 3,
  "p": 114.125,
  "s": 100,
  "c": [0, 12],
  "t": 1536036818784,
  "pt": 1536036818763,
  "q": 3681328
}`,
    codeExamples: streamExamples(WS_STOCKS, 'T.*'),
    notes: [
      'Full-market trades and quotes can be high volume during regular hours. Size your consumer for bursts or subscribe per ticker.',
      'A client that cannot keep up is disconnected with {"ev":"status","status":"error","message":"slow consumer, disconnecting"} rather than silently buffered.',
      ...streamNotes,
    ],
  },
  {
    id: 'ws-options-aggregates-minute',
    group: 'WebSocket',
    title: 'Option Aggregates (Per Minute)',
    method: 'GET',
    path: '/options',
    resourceLabel: 'WS',
    resourcePath: WS_OPTIONS,
    summary: 'Minute OHLCV bars for option contracts, streamed as each minute closes.',
    description: 'Stream minute-by-minute aggregated OHLC and volume data for the option contracts you subscribe to. Bars are built from qualifying trades in Eastern Time, and a minute with no eligible trade emits no bar — which is most minutes for most contracts.',
    useCases: streamUseCases,
    parameters: tickerParameter('options'),
    fields: streamAggregateFields('options', 'AM'),
    request: WS_OPTIONS,
    response: `{
  "ev": "AM",
  "sym": "O:ONEM220121C00025000",
  "v": 2,
  "av": 8,
  "op": 2.2,
  "vw": 2.05,
  "o": 2.05,
  "c": 2.05,
  "h": 2.05,
  "l": 2.05,
  "a": 2.1312,
  "z": 2,
  "s": 1632419640000,
  "e": 1632419700000
}`,
    codeExamples: streamExamples(WS_OPTIONS, 'AM.*'),
    notes: [
      'Name the contracts you want. The options feed opens its upstream subscriptions from what clients ask for, so a specific contract list starts delivering immediately.',
      'Contract symbols use the O: prefix: O:SPY251219C00650000 is SPY, 19 Dec 2025, call, strike 650.',
      ...streamNotes,
    ],
  },
  {
    id: 'ws-options-aggregates-second',
    group: 'WebSocket',
    title: 'Option Aggregates (Per Second)',
    method: 'GET',
    path: '/options',
    resourceLabel: 'WS',
    resourcePath: WS_OPTIONS,
    summary: 'Second-by-second OHLCV bars for option contracts.',
    description: 'Stream second-by-second aggregated OHLC and volume data for the option contracts you subscribe to. The bar shape matches the per-minute stream; only the window differs.',
    useCases: streamUseCases,
    parameters: tickerParameter('options'),
    fields: streamAggregateFields('options', 'A'),
    request: WS_OPTIONS,
    response: `{
  "ev": "A",
  "sym": "O:ONEM220121C00025000",
  "v": 2,
  "av": 8,
  "op": 2.2,
  "vw": 2.05,
  "o": 2.05,
  "c": 2.05,
  "h": 2.05,
  "l": 2.05,
  "a": 2.1312,
  "z": 2,
  "s": 1632419640000,
  "e": 1632419700000
}`,
    codeExamples: streamExamples(WS_OPTIONS, 'A.*'),
    notes: [
      'Per-second option bars are sparse: a contract that does not trade in a given second emits nothing.',
      ...streamNotes,
    ],
  },
  ...additionalTickStreams,
  ...indexStreamEndpoints,
];

export const endpoints: Endpoint[] = [
  ...longbridgeEndpoints,
  {
    id: 'us-stock-tickers',
    group: 'U.S. Stocks',
    title: 'All stock tickers',
    method: 'GET',
    path: '/reference/tickers',
    summary: 'Search the current or historical U.S. stock security directory.',
    description: 'Retrieve stock ticker records with exchange, CIK, composite FIGI, share-class FIGI, active status, and delisting date. The directory defaults to active securities. Set active=false for delisted securities, and supply date when a ticker has been reused so the record is resolved at that point in time.',
    useCases: ['Security master lookup', 'Delisted ticker discovery', 'Identifier mapping', 'Ticker reuse disambiguation'],
    parameters: [
      { name: 'ticker', type: 'string', description: 'Exact ticker symbol. Supports .gt, .gte, .lt, and .lte range modifiers.', example: 'ATVI' },
      { name: 'type', type: 'string', description: 'Ticker type code, such as CS for common stock.', example: 'CS' },
      { name: 'market', type: 'string', description: 'Market filter. Set to stocks for the U.S. equity directory.', example: 'stocks' },
      { name: 'exchange', type: 'string', description: 'Primary exchange MIC, such as XNAS or XNYS.', example: 'XNAS' },
      { name: 'cusip', type: 'string', description: 'Filter by CUSIP. The CUSIP itself is not returned in results.', example: '037833100' },
      { name: 'cik', type: 'string', description: 'Filter by SEC Central Index Key.', example: '0000718877' },
      { name: 'date', type: 'date', description: 'Point-in-time directory date. Use this to distinguish different companies that held the same ticker.', example: '2023-10-16' },
      { name: 'search', type: 'string', description: 'Case-insensitive search within ticker symbols and company names.', example: 'Activision' },
      { name: 'active', type: 'boolean', description: 'Whether securities were active on the queried date. Defaults to true; set false for delisted records.', example: 'false' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 1,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Sort field, normally ticker.', example: 'ticker' },
    ],
    fields: [
      { name: 'count', type: 'integer', description: 'Number of results in this page.' },
      { name: 'request_id', type: 'string', description: 'Unique upstream request identifier.' },
      { name: 'results[].ticker', type: 'string', description: 'Case-sensitive exchange symbol.' },
      { name: 'results[].name', type: 'string', description: 'Registered security or company name for this point-in-time record.' },
      { name: 'results[].market', type: 'string', description: 'Market type.' },
      { name: 'results[].locale', type: 'string', description: 'Geographic locale.' },
      { name: 'results[].primary_exchange', type: 'string', description: 'Primary exchange MIC.' },
      { name: 'results[].type', type: 'string', description: 'Ticker type code.' },
      { name: 'results[].currency_name', type: 'string', description: 'Trading currency.' },
      { name: 'results[].active', type: 'boolean', description: 'Whether this record was active on the queried date.' },
      { name: 'results[].cik', type: 'string', description: 'SEC Central Index Key.' },
      { name: 'results[].composite_figi', type: 'string', description: 'Composite FIGI for this security.' },
      { name: 'results[].share_class_figi', type: 'string', description: 'Share-class FIGI.' },
      { name: 'results[].delisted_utc', type: 'string', description: 'Upstream delisting timestamp for this ticker record.' },
      { name: 'results[].last_updated_utc', type: 'string', description: 'Last reference-data update timestamp.' },
      { name: 'next_url', type: 'string', description: 'Next result page, when present.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/reference/tickers?ticker=ATVI&market=stocks&active=false',
    response: `{
  "count": 1,
  "results": [{
    "ticker": "ATVI",
    "name": "Activision Blizzard Inc.",
    "market": "stocks",
    "active": false,
    "cik": "0000718877",
    "composite_figi": "BBG000CVWGS6",
    "share_class_figi": "BBG001S6C009",
    "delisted_utc": "2023-10-16T04:00:00Z"
  }],
  "status": "OK"
}`,
    notes: [
      'active defaults to true. A delisted-only lookup must set active=false.',
      'All Tickers can filter by ticker, CUSIP, or CIK; it has no composite-FIGI or share-class-FIGI filter.',
      'A ticker is not a permanent company identifier. Combine ticker with date when a symbol may have been reused.',
      'delisted_utc is the upstream security-master date; it is not inferred from the final aggregate bar.',
      'next_url is rewritten to api.eroya.co and carries the upstream cursor, which encodes the filters of the first request. Follow it as issued with your Eroya credential; rebuilding it from the original query drops the cursor and restarts the collection.',
      'The directory is the upstream security master in full, including when-issued lines, which are a listing that has not begun trading rather than a security that can be named. Eroya forwards the table as the upstream keeps it and does not drop or relabel these rows.',
      'Filter them on symbol and name together, because neither field alone is complete. Of the 23,415 inactive stock records on 2026-09-22, 929 are when-issued: 735 carry a symbol ending in a lowercase w (ABDw, YUMw), and 680 say so in the name (When Issued, WI, W.I.). The symbol rule alone misses 194, almost all of them the uppercase V ex-distribution form such as ANABV or ALKSV; the name rule alone misses 249, including YUMw, whose name is just Yum! Brands, Inc.',
      'type does not identify them: those 929 rows carry no type at all (461), CS (341), RIGHT (98), ADRC (15), WARRANT (5), PFD (3), SP, ADRR, UNIT, and ETF. A when-issued row typed CS is not a nameable common stock, and Ticker Overview answers many of them with error: not_found, so a bulk job that names every row in the page will book failures that are not failures.',
    ],
  },
  {
    id: 'us-stock-ticker-overview',
    group: 'U.S. Stocks',
    title: 'Ticker overview',
    method: 'GET',
    path: '/reference/tickers/{ticker}',
    summary: 'Detailed company and security reference data for one ticker.',
    description: 'Retrieve detailed reference data for a case-sensitive ticker symbol. When the current upstream overview reports a delisted ticker as missing, Eroya resolves the exact inactive record through All Tickers and enriches it with the overview from the day before delisting. The result keeps active=false and the directory delisted_utc. Supply date to resolve a specific historical holder of a reused ticker.',
    useCases: ['Company research', 'Delisted security lookup', 'Historical security identity', 'Due diligence'],
    parameters: [
      { name: 'ticker', type: 'string', required: true, description: 'Case-sensitive ticker symbol. This path does not accept CIK, CUSIP, composite FIGI, or share-class FIGI.', example: 'ATVI' },
      { name: 'date', type: 'date', description: 'Point-in-time date. Required in practice when the same ticker has represented more than one company.', example: '2023-10-16' },
    ],
    fields: [
      { name: 'count', type: 'integer', description: 'One when Eroya resolved the record through the inactive directory.' },
      { name: 'request_id', type: 'string', description: 'Unique upstream request identifier.' },
      { name: 'results.ticker', type: 'string', description: 'Case-sensitive exchange symbol.' },
      { name: 'results.name', type: 'string', description: 'Registered security or company name.' },
      { name: 'results.active', type: 'boolean', description: 'Whether the security was active on the requested date.' },
      { name: 'results.cik', type: 'string', description: 'SEC Central Index Key.' },
      { name: 'results.composite_figi', type: 'string', description: 'Composite FIGI.' },
      { name: 'results.share_class_figi', type: 'string', description: 'Share-class FIGI.' },
      { name: 'results.primary_exchange', type: 'string', description: 'Primary exchange MIC.' },
      { name: 'results.list_date', type: 'date', description: 'Initial listing date.' },
      { name: 'results.delisted_utc', type: 'string', description: 'Upstream delisting timestamp for the resolved record.' },
      { name: 'results.description', type: 'string', description: 'Company description as of the last available active overview.' },
      { name: 'results.homepage_url', type: 'string', description: 'Company homepage, when available.' },
      { name: 'results.market_cap', type: 'number', description: 'Most recent point-in-time market capitalization, when available.' },
      { name: 'results.sic_code', type: 'string', description: 'SEC industry code.' },
      { name: 'results.sic_description', type: 'string', description: 'SEC industry description.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/reference/tickers/ATVI',
    response: `{
  "count": 1,
  "results": {
    "ticker": "ATVI",
    "name": "Activision Blizzard Inc.",
    "active": false,
    "cik": "0000718877",
    "composite_figi": "BBG000CVWGS6",
    "share_class_figi": "BBG001S6C009",
    "delisted_utc": "2023-10-16T04:00:00Z",
    "description": "Activision Blizzard was formed in 2008..."
  },
  "status": "OK"
}`,
    notes: [
      'The path parameter is a ticker symbol only. Use the events endpoint for CUSIP or composite-FIGI identifiers.',
      'For a reused ticker, date selects the company that held the symbol then; omitting date selects the latest record.',
      'list_date is scoped to the symbol, not to the security. A reused symbol returns the same list_date for every holder, which may be the listing date of none of them; confirm a security\'s own first trading day from its earliest aggregate bar.',
      'The inactive directory record is authoritative for active, identifiers, and delisted_utc; no delisting date is inferred from price bars.',
      'A 404 carries a stable error field so the two kinds of miss can be told apart without parsing message. no_coverage means the symbol is in the security master but no archive record exists for the requested date; not_found means no directory, active or inactive, holds the symbol on any date. The same value is repeated in the X-Eroya-Reference-Resolution header.',
      'no_coverage does not distinguish a security that was not listed on that date from one the archive does not reach. The upstream answers both with the same 404, and Eroya does not infer which it was.',
    ],
  },
  {
    id: 'us-stock-ticker-events',
    group: 'U.S. Stocks',
    title: 'Ticker events',
    method: 'GET',
    path: '/reference/tickers/{id}/events',
    summary: 'Experimental ticker-change timeline for a security identifier.',
    description: 'Retrieve stored ticker-change events for a case-sensitive ticker, CUSIP, or composite FIGI. When the experimental upstream explicitly reports that no events were found for the ID, Eroya returns HTTP 200 with results.events as an empty array.',
    useCases: ['Ticker-change history', 'Security identity continuity', 'Symbol migration', 'Reference-data maintenance'],
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'Case-sensitive ticker, CUSIP, or composite FIGI. Share-class FIGI is not supported.', example: 'BBG000CVWGS6' },
      { name: 'types', type: 'string', description: 'Comma-separated event types. ticker_change is currently the only supported type.', example: 'ticker_change' },
    ],
    fields: [
      { name: 'request_id', type: 'string', description: 'Unique upstream request identifier.' },
      { name: 'results.name', type: 'string', description: 'Security name when the upstream has an event record.' },
      { name: 'results.events', type: 'array[object]', description: 'Matching events; empty when the upstream reports no events for the ID.' },
      { name: 'results.events[].type', type: 'string', description: 'Event type.' },
      { name: 'results.events[].date', type: 'date', description: 'Effective event date.' },
      { name: 'results.events[].ticker_change.ticker', type: 'string', description: 'Ticker effective at this event.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/reference/tickers/BBG000CVWGS6/events?types=ticker_change',
    response: `{
  "results": {
    "events": []
  },
  "status": "OK"
}`,
    notes: [
      'This upstream endpoint is experimental and currently exposes ticker_change only.',
      'When id is a ticker, it refers to the entity currently represented by that ticker. For an earlier holder, use a dated ticker overview to obtain that entity\'s composite FIGI.',
      'An empty array means no stored event matched the identifier and types filter; it does not independently validate the identifier.',
      'The oldest event is a first-seen marker, not a rename. A security already listed when upstream history began carries 2003-09-10; one added later carries the date its reference record was created.',
      'A class-suffixed symbol gets the root timeline with the suffix dropped: BRK.B stores BRK, HEI.A stores HEI, and a warrant or unit stores its underlying common stock. Measured on 2026-09-10, none of the 139 active class-suffixed symbols has a stored event naming a suffixed symbol, and all 139 oldest rows name a symbol that belonged to a different security that day (78) or did not exist at all (61).',
      'A dated ticker overview is authoritative where the two disagree. Before slicing aggregates at an event date, request the dated overview for that symbol and date and require its composite_figi to match the identifier; an event that fails this check must not be used to stitch a price series.',
      'An identifier with no stored events is not an error and is not a 404: it answers HTTP 200 with an empty results.events and no error field. A 404 from this route carries error: not_found, meaning the identifier itself did not resolve. Branch on error, not on the upstream message.',
      'The identifier is forwarded to the store exactly as given. Eroya does not resolve a composite FIGI or CUSIP to a current symbol first, so an identifier belonging to a security whose old symbol has since been reissued is never silently answered with the new holder\'s timeline.',
    ],
  },
  {
    id: 'us-stocks-custom-bars',
    group: 'U.S. Stocks',
    title: 'Custom Bars',
    method: 'GET',
    path: '/aggs/ticker/{stocksTicker}/range/{multiplier}/{timespan}/{from}/{to}',
    summary: 'Historical OHLC and volume over a custom interval.',
    description: 'Retrieve split-adjustable aggregate bars for a U.S. stock across pre-market, regular, and after-hours sessions. Empty intervals indicate that no qualifying trades occurred.',
    useCases: ['Charting', 'Technical analysis', 'Backtesting', 'Market research'],
    parameters: aggregateParameters,
    fields: aggregateFields,
    request: 'https://api.eroya.co/v1/aggs/ticker/AAPL/range/1/day/2025-01-02/2025-01-10?adjusted=true&sort=asc',
    response: `{
  "ticker": "AAPL",
  "adjusted": true,
  "queryCount": 2,
  "resultsCount": 2,
  "status": "OK",
  "results": [
    {
      "o": 243.36,
      "h": 244.18,
      "l": 241.89,
      "c": 243.85,
      "v": 55740731,
      "vw": 243.21,
      "n": 612482,
      "t": 1735813800000
    }
  ]
}`,
  },
  {
    id: 'us-options-custom-bars',
    group: 'U.S. Options',
    title: 'Custom Bars',
    method: 'GET',
    path: '/aggs/ticker/{optionsTicker}/range/{multiplier}/{timespan}/{from}/{to}',
    summary: 'Historical aggregate bars for an options contract.',
    description: 'Retrieve OHLC, volume, trade count, and VWAP for a specific options contract over a custom interval. Use the canonical O: contract ticker.',
    useCases: ['Options charting', 'Volatility research', 'Backtesting', 'Contract analysis'],
    parameters: [
      { ...aggregateParameters[0], name: 'optionsTicker', description: 'Canonical options contract ticker.', example: 'O:AAPL250117C00250000' },
      ...aggregateParameters.slice(1),
    ],
    fields: aggregateFields,
    request: 'https://api.eroya.co/v1/aggs/ticker/O:AAPL250117C00250000/range/1/day/2025-01-02/2025-01-10?sort=asc',
    response: `{
  "ticker": "O:AAPL250117C00250000",
  "adjusted": true,
  "queryCount": 2,
  "resultsCount": 2,
  "status": "OK",
  "results": [
    {
      "o": 3.25,
      "h": 3.70,
      "l": 3.05,
      "c": 3.48,
      "v": 281,
      "vw": 3.41,
      "n": 93,
      "t": 1735813800000
    }
  ]
}`,
  },
  {
    id: 'us-options-trades',
    group: 'U.S. Options',
    title: 'Trades',
    method: 'GET',
    path: '/trades/{optionsTicker}',
    summary: 'Tick-level trade prints for an options contract.',
    description: 'Retrieve comprehensive, tick-level trade data for a specified options ticker within a defined time range. Each record includes price, size, exchange, trade conditions, and precise timestamp information. These records support intraday analysis, market-microstructure research, backtesting, and an auditable history of market activity.',
    useCases: ['Intraday analysis', 'Algorithmic trading', 'Market microstructure research', 'Data integrity and compliance'],
    parameters: [
      { name: 'optionsTicker', type: 'string', required: true, description: 'Canonical options contract ticker with the O: prefix.', example: 'O:SPY280121C00750000' },
      { name: 'timestamp', type: 'string', description: 'Query by trade timestamp: a YYYY-MM-DD date or a nanosecond timestamp. Supports .gt, .gte, .lt, and .lte modifiers.', example: '2025-01-02' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 1,000; maximum 50,000.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to timestamp.', example: 'timestamp' },
    ],
    fields: [
      { name: 'results[].conditions', type: 'array[integer]', description: 'A list of trade condition codes.' },
      { name: 'results[].correction', type: 'integer', description: 'The trade correction indicator.' },
      { name: 'results[].exchange', type: 'integer', description: 'The exchange ID where the trade executed.' },
      { name: 'results[].participant_timestamp', type: 'integer', description: 'Nanosecond exchange Unix timestamp when the trade was generated.' },
      { name: 'results[].price', type: 'number', description: 'The actual dollar price per whole share of the trade.' },
      { name: 'results[].sequence_number', type: 'integer', description: 'Increasing sequence of events for the ticker; values need not be consecutive.' },
      { name: 'results[].sip_timestamp', type: 'integer', description: 'Nanosecond SIP Unix timestamp when the SIP received the trade.' },
      { name: 'results[].size', type: 'number', description: 'The trade size (volume).' },
      { name: 'request_id', type: 'string', description: 'A request id assigned by the upstream service.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
      { name: 'status', type: 'string', description: 'The request status.' },
    ],
    request: 'https://api.eroya.co/v1/trades/O:SPY280121C00750000?order=asc&limit=10&sort=timestamp',
    response: `{
  "request_id": "a47d1beb8c11b6ae897ab76cdbbf35a3",
  "results": [
    {
      "exchange": 46,
      "participant_timestamp": 1401715883806000000,
      "price": 6.91,
      "sequence_number": 1,
      "sip_timestamp": 1401715883806000000,
      "size": 1
    },
    {
      "conditions": [209],
      "exchange": 67,
      "participant_timestamp": 1401716547786000000,
      "price": 7.2,
      "sequence_number": 2,
      "sip_timestamp": 1401716547786000000,
      "size": 1
    }
  ],
  "status": "OK"
}`,
    notes: ['Served in real time by the configured Options Advanced upstream.', 'Full-history records date back to June 2, 2014.', 'Timestamps are nanosecond-accuracy Unix timestamps.'],
  },
  {
    id: 'us-options-quotes',
    group: 'U.S. Options',
    title: 'Quotes',
    method: 'GET',
    path: '/quotes/{optionsTicker}',
    summary: 'Historical bid and ask quotes for an options contract.',
    description: 'Retrieve historical quotes for a specified options contract over a defined time range. Each record provides bid and ask prices, sizes, exchange identifiers, and precise timestamps, reflecting the options market conditions at each captured moment.',
    useCases: ['Historical quote analysis', 'Market interest evaluation', 'Algorithmic backtesting', 'Strategy refinement'],
    parameters: [
      { name: 'optionsTicker', type: 'string', required: true, description: 'Canonical options contract ticker with the O: prefix.', example: 'O:SPY280121C00750000' },
      { name: 'timestamp', type: 'string', description: 'Query by quote timestamp: a YYYY-MM-DD date or a nanosecond timestamp. Supports .gt, .gte, .lt, and .lte modifiers.', example: '2025-01-02' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 1,000; maximum 50,000.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to timestamp.', example: 'timestamp' },
    ],
    fields: [
      { name: 'results[].ask_exchange', type: 'integer', description: 'The exchange ID for the ask.' },
      { name: 'results[].ask_price', type: 'number', description: 'The ask price.' },
      { name: 'results[].ask_size', type: 'number', description: 'The ask size in round lots.' },
      { name: 'results[].bid_exchange', type: 'integer', description: 'The exchange ID for the bid.' },
      { name: 'results[].bid_price', type: 'number', description: 'The bid price.' },
      { name: 'results[].bid_size', type: 'number', description: 'The bid size in round lots.' },
      { name: 'results[].sequence_number', type: 'integer', description: 'Increasing sequence of quote events for the ticker; values need not be consecutive.' },
      { name: 'results[].sip_timestamp', type: 'integer', description: 'Nanosecond SIP Unix timestamp when the SIP received the quote.' },
      { name: 'request_id', type: 'string', description: 'A request id assigned by the upstream service.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
      { name: 'status', type: 'string', description: 'The request status.' },
    ],
    request: 'https://api.eroya.co/v1/quotes/O:SPY280121C00750000?order=asc&limit=10&sort=timestamp',
    response: `{
  "request_id": "a47d1beb8c11b6ae897ab76cdbbf35a3",
  "results": [
    {
      "ask_exchange": 323,
      "ask_price": 282,
      "ask_size": 10,
      "bid_exchange": 316,
      "bid_price": 277.5,
      "bid_size": 1,
      "sequence_number": 789539218,
      "sip_timestamp": 1645119125346243600
    },
    {
      "ask_exchange": 301,
      "ask_price": 282,
      "ask_size": 1,
      "bid_exchange": 323,
      "bid_price": 277.5,
      "bid_size": 10,
      "sequence_number": 788994206,
      "sip_timestamp": 1645119118474271000
    }
  ],
  "status": "OK"
}`,
    notes: ['Served in real time by the configured Options Advanced upstream.', 'Full-history records date back to March 7, 2022.', 'Quote sizes are expressed in round lots; the normal round lot is 100 shares.'],
  },
  {
    id: 'indices-custom-bars',
    group: 'U.S. Indices',
    title: 'Custom bars (OHLC)',
    method: 'GET',
    path: '/aggs/ticker/{indicesTicker}/range/{multiplier}/{timespan}/{from}/{to}',
    summary: 'Historical index values aggregated over a custom interval.',
    description: 'Retrieve aggregated open, high, low, and close values for an index over a custom date range and interval in Eastern Time. Unlike stocks and options, index aggregates are derived from published index values rather than from individual trades, so an interval with no new index value produces no bar at all.',
    useCases: ['Benchmark comparison', 'Market trend analysis', 'Data visualization', 'Research and modeling'],
    parameters: [
      { name: 'indicesTicker', type: 'string', required: true, description: 'Index ticker with the canonical I: prefix.', example: 'I:SPX' },
      { name: 'multiplier', type: 'integer', required: true, description: 'Size of the timespan multiplier.', example: '1' },
      { name: 'timespan', type: 'string', required: true, description: 'Aggregate window: minute, hour, day, week, month, quarter, or year.', example: 'day' },
      { name: 'from', type: 'string', required: true, description: 'Window start as YYYY-MM-DD or a Unix millisecond timestamp.', example: '2026-03-02' },
      { name: 'to', type: 'string', required: true, description: 'Window end as YYYY-MM-DD or a Unix millisecond timestamp.', example: '2026-03-06' },
      { name: 'sort', type: 'string', description: 'Timestamp order: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Maximum base aggregates queried. Default 5,000; maximum 50,000.', example: '5000' },
    ],
    fields: indexAggregateFields,
    request: 'https://api.eroya.co/v1/aggs/ticker/I:SPX/range/1/day/2026-03-02/2026-03-06?sort=asc',
    response: `{
  "ticker": "I:SPX",
  "queryCount": 5,
  "resultsCount": 5,
  "status": "OK",
  "results": [
    {
      "o": 5930.85,
      "h": 5961.16,
      "l": 5920.35,
      "c": 5942.47,
      "t": 1772427600000
    }
  ]
}`,
    notes: [
      'Index tickers use the canonical I: prefix; the request routes to the indices entitlement automatically.',
      'Index bars carry no volume, VWAP, or transaction count because they are not built from trades.',
    ],
  },
  {
    id: 'indices-previous-day-bar',
    group: 'U.S. Indices',
    title: 'Previous day bar',
    method: 'GET',
    path: '/aggs/ticker/{indicesTicker}/prev',
    summary: 'Previous trading session OHLC for one index.',
    description: "Retrieve the previous trading day's open, high, low, and close values for an index. This is the cheapest way to establish a prior-session baseline without computing a date range yourself.",
    useCases: ['Baseline comparison', 'Daily reporting', 'Technical analysis', 'Market research'],
    parameters: [
      { name: 'indicesTicker', type: 'string', required: true, description: 'Index ticker with the canonical I: prefix.', example: 'I:SPX' },
    ],
    fields: [
      { name: 'ticker', type: 'string', description: 'Index symbol for the bar.' },
      { name: 'queryCount', type: 'integer', description: 'Base aggregates used for the response.' },
      { name: 'resultsCount', type: 'integer', description: 'Number of returned bars.' },
      { name: 'results[].T', type: 'string', description: 'Index symbol echoed on the bar.' },
      { name: 'results[].o', type: 'number', description: 'Opening index value for the session.' },
      { name: 'results[].h', type: 'number', description: 'Highest index value in the session.' },
      { name: 'results[].l', type: 'number', description: 'Lowest index value in the session.' },
      { name: 'results[].c', type: 'number', description: 'Closing index value for the session.' },
      { name: 'results[].t', type: 'integer', description: 'Session start as Unix milliseconds.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
    ],
    request: 'https://api.eroya.co/v1/aggs/ticker/I:SPX/prev',
    response: `{
  "ticker": "I:SPX",
  "queryCount": 1,
  "resultsCount": 1,
  "status": "OK",
  "results": [
    {
      "T": "I:SPX",
      "o": 5930.85,
      "h": 5961.16,
      "l": 5920.35,
      "c": 5942.47,
      "t": 1772427600000
    }
  ]
}`,
    notes: ['"Previous day" is the last completed session, so the value does not change intraday.'],
  },
  {
    id: 'indices-daily-summary',
    group: 'U.S. Indices',
    title: 'Daily ticker summary',
    method: 'GET',
    path: '/open-close/{indicesTicker}/{date}',
    summary: 'Open and close values for one index on one date.',
    description: 'Retrieve the opening and closing values for an index on a given date, together with the session high and low and any values calculated during pre-market and after-hours sessions. Some index methodologies publish outside regular hours; those that do not simply omit the extended-session fields.',
    useCases: ['Daily performance analysis', 'Historical data collection', 'After-hours insights', 'Portfolio tracking'],
    parameters: [
      { name: 'indicesTicker', type: 'string', required: true, description: 'Index ticker with the canonical I: prefix.', example: 'I:SPX' },
      { name: 'date', type: 'string', required: true, description: 'Session date as YYYY-MM-DD.', example: '2026-03-06' },
    ],
    fields: [
      { name: 'symbol', type: 'string', description: 'Index symbol for the session.' },
      { name: 'from', type: 'string', description: 'Session date as YYYY-MM-DD.' },
      { name: 'open', type: 'number', description: 'Opening index value.' },
      { name: 'high', type: 'number', description: 'Highest index value of the session.' },
      { name: 'low', type: 'number', description: 'Lowest index value of the session.' },
      { name: 'close', type: 'number', description: 'Closing index value.' },
      { name: 'preMarket', type: 'number', description: 'Pre-market value, when the index publishes one.' },
      { name: 'afterHours', type: 'number', description: 'After-hours value, when the index publishes one.' },
      { name: 'status', type: 'string', description: 'The request status.' },
    ],
    request: 'https://api.eroya.co/v1/open-close/I:SPX/2026-03-06',
    response: `{
  "symbol": "I:SPX",
  "from": "2026-03-06",
  "open": 5930.85,
  "high": 5961.16,
  "low": 5920.35,
  "close": 5942.47,
  "afterHours": 5943.02,
  "status": "OK"
}`,
    notes: ['Regular index hours are 9:30 AM to 4:00 PM ET, Monday through Friday.'],
  },
  {
    id: 'indices-snapshot',
    group: 'U.S. Indices',
    title: 'Indices snapshot',
    method: 'GET',
    path: '/snapshot/indices',
    summary: 'Current value and session performance for one or more indices.',
    description: 'Retrieve snapshot data for a set of indices, including the current value, session open, high, low, previous close, and the absolute and percentage change. One request covers a whole watchlist, which makes it the right endpoint for a dashboard that has to stay current without polling each index separately.',
    useCases: ['Market condition assessment', 'Economic sentiment tracking', 'Dashboard tiles', 'Portfolio context'],
    parameters: [
      { name: 'ticker.any_of', type: 'string', description: 'Comma-separated list of index tickers to return.', example: 'I:SPX,I:NDX,I:DJI' },
      { name: 'ticker', type: 'string', description: 'A single index ticker. Supports .gt, .gte, .lt, and .lte range modifiers for symbol ranges.', example: 'I:SPX' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 10; maximum 250.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to ticker.', example: 'ticker' },
    ],
    fields: [
      { name: 'results[].ticker', type: 'string', description: 'Index symbol.' },
      { name: 'results[].name', type: 'string', description: 'Index name.' },
      { name: 'results[].type', type: 'string', description: 'Asset class of the entry. Always indices.' },
      { name: 'results[].market_status', type: 'string', description: 'Market status for the index: open, closed, early_trading, or late_trading.' },
      { name: 'results[].value', type: 'number', description: 'Most recent published index value.' },
      { name: 'results[].session.open', type: 'number', description: 'Opening value of the current session.' },
      { name: 'results[].session.high', type: 'number', description: 'Highest value of the current session.' },
      { name: 'results[].session.low', type: 'number', description: 'Lowest value of the current session.' },
      { name: 'results[].session.close', type: 'number', description: 'Closing value of the most recent completed session.' },
      { name: 'results[].session.previous_close', type: 'number', description: 'Closing value of the prior session.' },
      { name: 'results[].session.change', type: 'number', description: 'Absolute change against the previous close.' },
      { name: 'results[].session.change_percent', type: 'number', description: 'Percentage change against the previous close.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/snapshot/indices?ticker.any_of=I:SPX,I:NDX,I:DJI',
    response: `{
  "request_id": "f7c1a0d2-...",
  "results": [
    {
      "ticker": "I:SPX",
      "name": "S&P 500",
      "type": "indices",
      "market_status": "closed",
      "value": 5942.47,
      "session": {
        "open": 5930.85,
        "high": 5961.16,
        "low": 5920.35,
        "close": 5942.47,
        "previous_close": 5930.13,
        "change": 12.34,
        "change_percent": 0.21
      }
    }
  ],
  "status": "OK"
}`,
    notes: ['Pass ticker.any_of to snapshot a whole watchlist in one request.'],
  },
  {
    id: 'indices-tickers',
    group: 'U.S. Indices',
    title: 'All index tickers',
    method: 'GET',
    path: '/reference/tickers',
    summary: 'Searchable directory of supported index symbols.',
    description: 'Retrieve the index symbols Eroya supports, with the index name, source feed, and active status. Filter with market=indices to restrict the shared ticker directory to indices; the coverage spans more than 10,000 indices from S&P, Nasdaq, Dow Jones, FTSE Russell, MSCI, Morningstar, Cboe Global Indices, and Societe Generale.',
    useCases: ['Index discovery', 'Symbol validation', 'Watchlist construction', 'Data integration'],
    parameters: [
      { name: 'market', type: 'string', required: true, description: 'Set to indices to list index symbols.', example: 'indices' },
      { name: 'ticker', type: 'string', description: 'Return a specific index symbol. Supports .gt, .gte, .lt, and .lte modifiers.', example: 'I:SPX' },
      { name: 'search', type: 'string', description: 'Case-insensitive substring match against the index name.', example: 'S&P' },
      { name: 'active', type: 'boolean', description: 'Whether the symbol is actively published. Defaults to true.', example: 'true' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 1,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to ticker.', example: 'ticker' },
    ],
    fields: [
      { name: 'count', type: 'integer', description: 'Number of results in this page.' },
      { name: 'results[].ticker', type: 'string', description: 'Index symbol, prefixed with I:.' },
      { name: 'results[].name', type: 'string', description: 'Index name.' },
      { name: 'results[].market', type: 'string', description: 'Market of the symbol. Always indices here.' },
      { name: 'results[].locale', type: 'string', description: 'Geographic locale of the index.' },
      { name: 'results[].active', type: 'boolean', description: 'Whether the index is actively published.' },
      { name: 'results[].source_feed', type: 'string', description: 'Index provider supplying the values.' },
      { name: 'results[].last_updated_utc', type: 'string', description: 'RFC3339 timestamp of the last reference update.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/reference/tickers?market=indices&search=S%26P&active=true&limit=100',
    response: `{
  "count": 1,
  "request_id": "9d0b2f31-...",
  "results": [
    {
      "ticker": "I:SPX",
      "name": "S&P 500",
      "market": "indices",
      "locale": "us",
      "active": true,
      "source_feed": "S&P Dow Jones Indices",
      "last_updated_utc": "2026-03-06T21:15:04Z"
    }
  ],
  "status": "OK"
}`,
    notes: ['market=indices is what routes the request to the indices entitlement; without it the directory answers from the stock catalog.'],
  },
  {
    id: 'us-trades',
    group: 'U.S. Stocks',
    title: 'Trades',
    method: 'GET',
    path: '/trades/{stockTicker}',
    summary: 'Tick-level trade prints for a U.S. stock.',
    description: 'Retrieve comprehensive, tick-level trade data for a stock ticker within a time range. Each record includes price, size, exchange, trade conditions, and precise nanosecond timestamps. This granular data is foundational for constructing aggregated bars and for market-microstructure analysis, capturing every eligible trade that contributes to open, high, low, and close (OHLC) values.',
    useCases: ['Intraday analysis', 'Algorithmic trading', 'Market microstructure research', 'Data integrity and compliance'],
    parameters: [
      { name: 'stockTicker', type: 'string', required: true, description: 'Case-sensitive ticker symbol.', example: 'AAPL' },
      { name: 'timestamp', type: 'string', description: 'Query by trade timestamp: a YYYY-MM-DD date or a nanosecond timestamp. Supports .gt, .gte, .lt, and .lte modifiers.', example: '2025-01-02' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 1,000; maximum 50,000.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to timestamp.', example: 'timestamp' },
    ],
    fields: [
      { name: 'results[].conditions', type: 'array[integer]', description: 'A list of trade condition codes.' },
      { name: 'results[].correction', type: 'integer', description: 'The trade correction indicator.' },
      { name: 'results[].exchange', type: 'integer', description: 'The exchange ID where the trade executed.' },
      { name: 'results[].id', type: 'string', description: 'Trade ID, unique per ticker, exchange, and TRF.' },
      { name: 'results[].price', type: 'number', description: 'The actual dollar price per share of the trade.' },
      { name: 'results[].size', type: 'number', description: 'The size (volume) of the trade.' },
      { name: 'results[].sequence_number', type: 'integer', description: 'Increasing per-ticker sequence of trade events; resets each session.' },
      { name: 'results[].sip_timestamp', type: 'integer', description: 'Nanosecond SIP Unix timestamp when the SIP received the trade.' },
      { name: 'results[].participant_timestamp', type: 'integer', description: 'Nanosecond exchange Unix timestamp when the trade was generated.' },
      { name: 'results[].trf_id', type: 'integer', description: 'ID of the Trade Reporting Facility, when applicable.' },
      { name: 'results[].trf_timestamp', type: 'integer', description: 'Nanosecond TRF Unix timestamp, when applicable.' },
      { name: 'results[].tape', type: 'integer', description: 'Tape the ticker is listed on: 1=A (NYSE), 2=B (NYSE Arca/American), 3=C (Nasdaq).' },
      { name: 'request_id', type: 'string', description: 'A request id assigned by the server.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/trades/AAPL?timestamp=2025-01-02&order=asc&limit=10&sort=timestamp',
    response: `{
  "request_id": "a1b2c3d4-...",
  "results": [
    {
      "conditions": [12, 37],
      "exchange": 11,
      "id": "52983575722108",
      "price": 243.36,
      "size": 100,
      "sequence_number": 1063799,
      "sip_timestamp": 1735826400123456789,
      "participant_timestamp": 1735826400123400000,
      "tape": 3
    }
  ],
  "status": "OK"
}`,
    notes: ['Records date back to September 10, 2003 on the highest plan tier.', 'Timestamps are nanosecond-accuracy Unix timestamps.'],
  },
  {
    id: 'us-stocks-last-trade',
    group: 'U.S. Stocks',
    title: 'Last Trade',
    method: 'GET',
    path: '/last/trade/{stocksTicker}',
    summary: 'The latest available trade for a U.S. stock.',
    description: 'Retrieve the most recent trade for a specified stock ticker, including its price, size, exchange, conditions, identifiers, and exchange, SIP, and TRF timestamps when available. Use this compact endpoint for current-price displays when a full snapshot is unnecessary.',
    useCases: ['Trade monitoring', 'Price updates', 'Market snapshots', 'Dashboard refreshes'],
    parameters: [
      { name: 'stocksTicker', type: 'string', required: true, description: 'Case-sensitive U.S. stock ticker.', example: 'AAPL' },
    ],
    fields: [
      { name: 'results.T', type: 'string', description: 'Exchange ticker symbol.' },
      { name: 'results.c', type: 'array[integer]', description: 'Trade condition codes.' },
      { name: 'results.ds', type: 'string', description: 'Trade size including its fractional component, represented as a decimal string.' },
      { name: 'results.e', type: 'integer', description: 'Trade correction indicator.' },
      { name: 'results.f', type: 'integer', description: 'Nanosecond TRF Unix timestamp.' },
      { name: 'results.i', type: 'string', description: 'Trade identifier, unique per ticker, exchange, and TRF.' },
      { name: 'results.p', type: 'number', description: 'Trade price per whole share.' },
      { name: 'results.q', type: 'integer', description: 'Per-ticker event sequence number.' },
      { name: 'results.r', type: 'integer', description: 'Trade Reporting Facility identifier.' },
      { name: 'results.s', type: 'number', description: 'Trade size in shares.' },
      { name: 'results.t', type: 'integer', description: 'Nanosecond SIP Unix timestamp.' },
      { name: 'results.x', type: 'integer', description: 'Exchange identifier.' },
      { name: 'results.y', type: 'integer', description: 'Nanosecond participant/exchange Unix timestamp.' },
      { name: 'results.z', type: 'integer', description: 'Listing tape: 1=A, 2=B, or 3=C.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/last/trade/AAPL',
    response: `{
  "request_id": "f05562305bd26ced64b98ed68b3c5d96",
  "results": {
    "T": "AAPL",
    "c": [37],
    "ds": "25.0",
    "f": 1617901342969796400,
    "i": "118749",
    "p": 129.8473,
    "q": 3135876,
    "r": 202,
    "s": 25,
    "t": 1617901342969834000,
    "x": 4,
    "y": 1617901342968000000,
    "z": 3
  },
  "status": "OK"
}`,
    notes: ['This endpoint returns one latest trade rather than a historical series.', 'Timestamp fields are nanosecond-accuracy Unix timestamps.'],
  },
  {
    id: 'us-stocks-quotes',
    group: 'U.S. Stocks',
    title: 'Quotes',
    method: 'GET',
    path: '/quotes/{stockTicker}',
    summary: 'Historical National Best Bid and Offer quotes for a U.S. stock.',
    description: 'Retrieve NBBO quotes for a specified stock ticker over a defined time range. Each record contains the prevailing best bid and ask prices, sizes, exchanges, conditions, and nanosecond timestamps, providing a historical top-of-book view for liquidity and market-microstructure analysis.',
    useCases: ['Historical quote analysis', 'Liquidity evaluation', 'Algorithmic backtesting', 'Strategy refinement'],
    parameters: [
      { name: 'stockTicker', type: 'string', required: true, description: 'Case-sensitive U.S. stock ticker.', example: 'AAPL' },
      { name: 'timestamp', type: 'string', description: 'Query by quote timestamp: a YYYY-MM-DD date or a nanosecond timestamp. Supports .gt, .gte, .lt, and .lte modifiers.', example: '2025-01-02' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 1,000; maximum 50,000.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to timestamp.', example: 'timestamp' },
    ],
    fields: [
      { name: 'results[].ask_exchange', type: 'integer', description: 'Exchange identifier for the ask.' },
      { name: 'results[].ask_price', type: 'number', description: 'Best ask price.' },
      { name: 'results[].ask_size', type: 'number', description: 'Shares offered at the current best ask.' },
      { name: 'results[].bid_exchange', type: 'integer', description: 'Exchange identifier for the bid.' },
      { name: 'results[].bid_price', type: 'number', description: 'Best bid price.' },
      { name: 'results[].bid_size', type: 'number', description: 'Shares bid at the current best bid.' },
      { name: 'results[].conditions', type: 'array[integer]', description: 'Quote condition codes.' },
      { name: 'results[].indicators', type: 'array[integer]', description: 'Quote indicator codes.' },
      { name: 'results[].participant_timestamp', type: 'integer', description: 'Nanosecond exchange Unix timestamp when the quote was generated.' },
      { name: 'results[].sequence_number', type: 'integer', description: 'Increasing per-ticker quote sequence; values need not be consecutive and reset each session.' },
      { name: 'results[].sip_timestamp', type: 'integer', description: 'Nanosecond SIP Unix timestamp when the quote was received.' },
      { name: 'results[].tape', type: 'integer', description: 'Listing tape: 1=A, 2=B, or 3=C.' },
      { name: 'results[].trf_timestamp', type: 'integer', description: 'Nanosecond TRF Unix timestamp, when present.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/quotes/AAPL?timestamp=2025-01-02&order=asc&limit=10&sort=timestamp',
    response: `{
  "next_url": "https://api.eroya.co/v1/quotes/AAPL?cursor=...",
  "request_id": "a47d1beb8c11b6ae897ab76cdbbf35a3",
  "results": [
    {
      "ask_exchange": 0,
      "ask_price": 0,
      "ask_size": 0,
      "bid_exchange": 11,
      "bid_price": 102.7,
      "bid_size": 60,
      "conditions": [1],
      "participant_timestamp": 1517562000065321200,
      "sequence_number": 2060,
      "sip_timestamp": 1517562000065700400,
      "tape": 3
    }
  ],
  "status": "OK"
}`,
    notes: ['Records date back to September 10, 2003 on the highest plan tier.', 'Follow next_url until it is absent when collecting more than one page. Preserve authentication on every page request.'],
  },
  {
    id: 'us-sma',
    group: 'U.S. Stocks',
    title: 'SMA',
    method: 'GET',
    path: '/indicators/sma/{stockTicker}',
    summary: 'Simple moving average over a defined window.',
    description: 'Retrieve the Simple Moving Average (SMA) for a ticker over a time range. The SMA averages price across a set number of periods, smoothing fluctuations to reveal underlying trends and potential signals.',
    useCases: ['Trend analysis', 'Trading-signal generation (SMA crossovers)', 'Support/resistance identification', 'Entry/exit timing'],
    parameters: [
      { name: 'stockTicker', type: 'string', required: true, description: 'Case-sensitive ticker symbol.', example: 'AAPL' },
      { name: 'timestamp', type: 'string', description: 'Query by timestamp: a YYYY-MM-DD date or a millisecond timestamp. Supports .gt, .gte, .lt, and .lte modifiers.', example: '2025-01-02' },
      { name: 'timespan', type: 'string', description: 'Size of the aggregate time window: minute, hour, day, week, month, quarter, or year.', example: 'day' },
      { name: 'adjusted', type: 'boolean', description: 'Whether the aggregates used are split-adjusted. Defaults to true.', example: 'true' },
      { name: 'window', type: 'integer', description: 'Window size used to calculate the SMA. Defaults to 50.', example: '50' },
      { name: 'series_type', type: 'string', description: 'Price used in each aggregate: open, high, low, or close. Defaults to close.', example: 'close' },
      { name: 'expand_underlying', type: 'boolean', description: 'Include the aggregates used to calculate the indicator in the response.', example: 'false' },
      { name: 'order', type: 'string', description: 'Order results by timestamp: asc or desc. Defaults to desc.', example: 'desc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 10; maximum 5,000.', example: '10' },
    ],
    fields: [
      { name: 'results.values[].timestamp', type: 'integer', description: 'Unix millisecond timestamp for the indicator value.' },
      { name: 'results.values[].value', type: 'number', description: 'The SMA value at the timestamp.' },
      { name: 'results.underlying.aggregates', type: 'array[object]', description: 'The aggregates used, when expand_underlying is true.' },
      { name: 'results.underlying.url', type: 'string', description: 'URL of the underlying aggregates request.' },
      { name: 'request_id', type: 'string', description: 'A request id assigned by the server.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/indicators/sma/AAPL?timespan=day&window=50&series_type=close&order=desc&limit=10',
    response: `{
  "request_id": "b2c3d4e5-...",
  "results": {
    "values": [
      { "timestamp": 1735826400000, "value": 238.42 },
      { "timestamp": 1735740000000, "value": 237.91 }
    ]
  },
  "status": "OK"
}`,
    notes: ['A window of 10 with daily aggregates produces a 10-day moving average.'],
  },
  {
    id: 'us-news',
    group: 'U.S. Stocks',
    title: 'News',
    method: 'GET',
    path: '/reference/news',
    summary: 'Normalized company news merged across every upstream that covers the security.',
    description: 'Retrieve recent company news in one Eroya response shape. News is the one dataset where Eroya merges rather than chooses: every upstream carrying the security is queried in parallel and the articles are combined into a single time-ordered list, de-duplicated on canonical URL and title so a wire story syndicated by several outlets appears once. A partial upstream failure is reported in errors without discarding the articles that did arrive.',
    useCases: ['Market sentiment analysis', 'Investment research', 'Automated monitoring', 'Portfolio strategy refinement'],
    parameters: [
      { name: 'ticker', type: 'string', required: true, description: 'Canonical ticker: AAPL.US, 700.HK, 000001.SZ, or 600519.SH.', example: 'AAPL.US' },
      { name: 'limit', type: 'integer', description: 'Combined results after sorting and de-duplication. Default 50; maximum 100.', example: '10' },
    ],
    fields: [
      { name: 'results[].id', type: 'string', description: 'Unique identifier for the article.' },
      { name: 'results[].ticker', type: 'string', description: 'Canonical related ticker.' },
      { name: 'results[].title', type: 'string', description: 'The title of the news article.' },
      { name: 'results[].author', type: 'string', description: "The article's author." },
      { name: 'results[].url', type: 'string', description: 'A link to the news article.' },
      { name: 'results[].description', type: 'string', description: 'A plain-text description of the article.' },
      { name: 'results[].publishedAt', type: 'string', description: 'RFC3339 UTC publish date-time.' },
      { name: 'results[].publisher', type: 'string', description: 'The newsroom that wrote the story, when it is attributable.' },
      { name: 'results[].insights', type: 'array[object]', description: 'Sentiment insights related to the article.' },
      { name: 'errors[]', type: 'object', description: 'Partial failures that did not empty the result set.' },
    ],
    request: 'https://api.eroya.co/v1/reference/news?ticker=AAPL.US&limit=10',
    response: `{
  "status": "OK",
  "requestId": "c3d4e5f6-...",
  "results": [
    {
      "id": "nJ2f...",
      "title": "Apple announces quarterly results",
      "author": "Newsroom",
      "ticker": "AAPL.US",
      "url": "https://example.com/apple-results",
      "publishedAt": "2025-01-02T13:30:00Z",
      "publisher": "Example Wire",
      "insights": [{ "ticker": "AAPL", "sentiment": "positive" }]
    }
  ]
}`,
    notes: [
      'A U.S. request draws on more upstreams than an HK or China one, so the merged list is widest for U.S. securities.',
      'The response never names the upstream an article came from. Article URLs point at the publishing outlet and are returned unmodified.',
    ],
  },
  {
    id: 'us-options-contracts',
    group: 'U.S. Options',
    title: 'All Contracts',
    method: 'GET',
    path: '/reference/options/contracts',
    summary: 'Index of active and expired options contracts.',
    description: 'Retrieve a comprehensive index of options contracts, both active and expired. The list can be broad or narrowed to a specific underlying ticker. Each entry includes contract type (call/put), exercise style, expiration date, and strike price, enabling market-availability analysis and options-strategy research.',
    useCases: ['Market availability analysis', 'Strategy development', 'Research and modeling', 'Contract exploration'],
    parameters: [
      { name: 'underlying_ticker', type: 'string', description: 'Query for contracts relating to an underlying stock ticker. Supports filter modifiers.', example: 'AAPL' },
      { name: 'contract_type', type: 'string', description: 'Filter by contract type: call, put, or other.', example: 'call' },
      { name: 'expiration_date', type: 'string', description: 'Filter by contract expiration in YYYY-MM-DD. Supports filter modifiers.', example: '2025-01-17' },
      { name: 'as_of', type: 'string', description: 'Point in time for contracts as of this date (YYYY-MM-DD). Defaults to today.', example: '2025-01-02' },
      { name: 'strike_price', type: 'number', description: 'Filter by strike price. Supports filter modifiers.', example: '250' },
      { name: 'expired', type: 'boolean', description: 'Query for expired contracts. Defaults to false.', example: 'false' },
      { name: 'order', type: 'string', description: 'Order results by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 10; maximum 1,000.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field used for ordering. Defaults to ticker.', example: 'ticker' },
    ],
    fields: [
      { name: 'results[].ticker', type: 'string', description: 'The ticker for the option contract.' },
      { name: 'results[].underlying_ticker', type: 'string', description: 'The underlying ticker the contract relates to.' },
      { name: 'results[].contract_type', type: 'string', description: 'Contract type: put, call, or other.' },
      { name: 'results[].exercise_style', type: 'string', description: 'Exercise style: american, bermudan, or european.' },
      { name: 'results[].expiration_date', type: 'string', description: "The contract's expiration date in YYYY-MM-DD." },
      { name: 'results[].strike_price', type: 'number', description: 'The strike price of the option contract.' },
      { name: 'results[].shares_per_contract', type: 'number', description: 'Number of shares per contract.' },
      { name: 'results[].primary_exchange', type: 'string', description: 'MIC code of the primary listing exchange.' },
      { name: 'results[].cfi', type: 'string', description: 'The 6-letter CFI code (ISO 10962).' },
      { name: 'results[].correction', type: 'integer', description: 'The correction number for this contract.' },
      { name: 'results[].additional_underlyings', type: 'array[object]', description: 'Additional underlyings or deliverables, when present.' },
      { name: 'request_id', type: 'string', description: 'A request id assigned by the server.' },
      { name: 'next_url', type: 'string', description: 'URL for the next result page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/reference/options/contracts?underlying_ticker=AAPL&contract_type=call&limit=10&sort=ticker',
    response: `{
  "request_id": "d4e5f6a7-...",
  "results": [
    {
      "ticker": "O:AAPL250117C00250000",
      "underlying_ticker": "AAPL",
      "contract_type": "call",
      "exercise_style": "american",
      "expiration_date": "2025-01-17",
      "strike_price": 250,
      "shares_per_contract": 100,
      "primary_exchange": "BATO"
    }
  ],
  "status": "OK"
}`,
    notes: ['The deprecated ticker parameter is not supported; query a single contract via the options-contract endpoint instead.'],
  },
  {
    id: 'us-balance-sheets',
    group: 'U.S. Stocks',
    title: 'Balance Sheets',
    method: 'GET',
    path: '/stocks/financials/balance-sheets',
    summary: 'Quarterly and annual financial-position snapshots.',
    description: 'Retrieve normalized assets, liabilities, and equity reported by public companies. Records are point-in-time snapshots for a fiscal period, not cumulative flows.',
    useCases: ['Fundamental analysis', 'Valuation', 'Debt analysis', 'Equity research'],
    parameters: [
      { name: 'cik', type: 'string', description: 'SEC Central Index Key.', example: '0000320193' },
      { name: 'tickers', type: 'string', description: 'Filter for records whose ticker array contains this symbol.', example: 'AAPL' },
      { name: 'period_end', type: 'date', description: 'Period end in YYYY-MM-DD. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2025-09-27' },
      { name: 'filing_date', type: 'date', description: 'Most recent SEC filing date containing the period. Supports comparison suffixes.', example: '2025-10-31' },
      { name: 'fiscal_year', type: 'number', description: 'Fiscal year. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2025' },
      { name: 'fiscal_quarter', type: 'number', description: 'Fiscal quarter from 1 to 4.', example: '4' },
      { name: 'timeframe', type: 'string', description: 'Reporting period: quarterly or annual.', example: 'annual' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 50,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Comma-separated fields with .asc or .desc.', example: 'period_end.desc' },
    ],
    fields: [
      { name: 'results[].cik', type: 'string', description: 'SEC Central Index Key.' },
      { name: 'results[].tickers', type: 'array[string]', description: 'Listed ticker symbols.' },
      { name: 'results[].period_end', type: 'date', description: 'Financial reporting period end.' },
      { name: 'results[].filing_date', type: 'date', description: 'Most recent filing date containing this record.' },
      { name: 'results[].fiscal_year', type: 'number', description: 'Fiscal year.' },
      { name: 'results[].fiscal_quarter', type: 'number', description: 'Fiscal quarter.' },
      { name: 'results[].timeframe', type: 'string', description: 'Quarterly or annual.' },
      { name: 'results[].cash_and_equivalents', type: 'number', description: 'Cash and highly liquid short-term investments.' },
      { name: 'results[].total_current_assets', type: 'number', description: 'Assets expected to convert to cash within one year.' },
      { name: 'results[].total_assets', type: 'number', description: 'Total reported assets.' },
      { name: 'results[].total_current_liabilities', type: 'number', description: 'Obligations expected to settle within one year.' },
      { name: 'results[].total_liabilities', type: 'number', description: 'Total reported liabilities.' },
      { name: 'results[].total_equity', type: 'number', description: 'Total shareholders’ equity.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/financials/balance-sheets?tickers=AAPL&timeframe=annual&sort=period_end.desc&limit=1',
    response: `{
  "request_id": "e1a9b4f3-...",
  "results": [
    {
      "cik": "0000320193",
      "tickers": ["AAPL"],
      "period_end": "2025-09-27",
      "filing_date": "2025-10-31",
      "fiscal_year": 2025,
      "fiscal_quarter": 4,
      "timeframe": "annual",
      "cash_and_equivalents": 35934000000,
      "total_assets": 359241000000,
      "total_liabilities": 285508000000,
      "total_equity": 73733000000
    }
  ]
    }`,
  },
  {
    id: 'us-financial-ratios',
    group: 'U.S. Stocks',
    title: 'Ratios',
    method: 'GET',
    path: '/stocks/financials/ratios',
    summary: 'Valuation, profitability, liquidity, and leverage ratios.',
    description: 'Retrieve company ratios calculated for the most recent trading day from trailing-twelve-month financial statements and daily market prices.',
    useCases: ['Company valuation', 'Peer comparison', 'Financial health', 'Investment screening'],
    parameters: [
      { name: 'ticker', type: 'string', description: 'Stock ticker symbol. Supports filter modifiers.', example: 'AAPL' },
      { name: 'cik', type: 'string', description: 'SEC Central Index Key. Supports filter modifiers.', example: '320193' },
      { name: 'price', type: 'number', description: 'Stock price used in the calculations.', example: '228.87' },
      { name: 'average_volume', type: 'number', description: 'Average volume over the last 30 trading days.', example: '47500000' },
      { name: 'market_cap', type: 'number', description: 'Market capitalization.', example: '3479770835190' },
      { name: 'earnings_per_share', type: 'number', description: 'Net income available to common shareholders per weighted share.', example: '6.57' },
      { name: 'price_to_earnings', type: 'number', description: 'Price divided by positive earnings per share.', example: '34.84' },
      { name: 'price_to_book', type: 'number', description: 'Price divided by book value per share.', example: '52.16' },
      { name: 'price_to_sales', type: 'number', description: 'Price divided by revenue per share.', example: '9.02' },
      { name: 'price_to_cash_flow', type: 'number', description: 'Price divided by positive operating cash flow per share.', example: '30.78' },
      { name: 'price_to_free_cash_flow', type: 'number', description: 'Price divided by positive free cash flow per share.', example: '33.35' },
      { name: 'dividend_yield', type: 'number', description: 'Annual dividends per share divided by price.', example: '0.0044' },
      { name: 'return_on_assets', type: 'number', description: 'Net income divided by total assets.', example: '0.3075' },
      { name: 'return_on_equity', type: 'number', description: 'Net income divided by shareholders’ equity.', example: '1.5284' },
      { name: 'debt_to_equity', type: 'number', description: 'Current and long-term debt divided by shareholders’ equity.', example: '1.52' },
      { name: 'current', type: 'number', description: 'Current assets divided by current liabilities.', example: '0.68' },
      { name: 'quick', type: 'number', description: 'Current assets less inventories, divided by current liabilities.', example: '0.63' },
      { name: 'cash', type: 'number', description: 'Cash and equivalents divided by current liabilities.', example: '0.19' },
      { name: 'ev_to_sales', type: 'number', description: 'Enterprise value divided by revenue.', example: '9.22' },
      { name: 'ev_to_ebitda', type: 'number', description: 'Enterprise value divided by EBITDA.', example: '26.98' },
      { name: 'enterprise_value', type: 'number', description: 'Market cap plus debt less cash and equivalents.', example: '3555509835190' },
      { name: 'free_cash_flow', type: 'number', description: 'Operating cash flow less capital expenditure.', example: '104339000000' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 50,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Comma-separated fields with .asc or .desc. Defaults to ticker.asc.', example: 'ticker.asc' },
    ],
    fields: [
      { name: 'results[].ticker', type: 'string', description: 'Stock ticker symbol.' },
      { name: 'results[].cik', type: 'string', description: 'SEC Central Index Key.' },
      { name: 'results[].date', type: 'date', description: 'Trading date used for ratio calculation.' },
      { name: 'results[].price', type: 'number', description: 'Stock price used in calculations.' },
      { name: 'results[].average_volume', type: 'number', description: 'Average 30-day trading volume.' },
      { name: 'results[].market_cap', type: 'number', description: 'Market capitalization.' },
      { name: 'results[].earnings_per_share', type: 'number', description: 'Earnings per share.' },
      { name: 'results[].price_to_earnings', type: 'number', description: 'Price-to-earnings ratio.' },
      { name: 'results[].price_to_book', type: 'number', description: 'Price-to-book ratio.' },
      { name: 'results[].price_to_sales', type: 'number', description: 'Price-to-sales ratio.' },
      { name: 'results[].price_to_cash_flow', type: 'number', description: 'Price-to-operating-cash-flow ratio.' },
      { name: 'results[].price_to_free_cash_flow', type: 'number', description: 'Price-to-free-cash-flow ratio.' },
      { name: 'results[].dividend_yield', type: 'number', description: 'Dividend yield.' },
      { name: 'results[].return_on_assets', type: 'number', description: 'Return on assets.' },
      { name: 'results[].return_on_equity', type: 'number', description: 'Return on equity.' },
      { name: 'results[].debt_to_equity', type: 'number', description: 'Debt-to-equity ratio.' },
      { name: 'results[].current', type: 'number', description: 'Current ratio.' },
      { name: 'results[].quick', type: 'number', description: 'Quick ratio.' },
      { name: 'results[].cash', type: 'number', description: 'Cash ratio.' },
      { name: 'results[].enterprise_value', type: 'number', description: 'Enterprise value.' },
      { name: 'results[].ev_to_sales', type: 'number', description: 'Enterprise-value-to-sales ratio.' },
      { name: 'results[].ev_to_ebitda', type: 'number', description: 'Enterprise-value-to-EBITDA ratio.' },
      { name: 'results[].free_cash_flow', type: 'number', description: 'Free cash flow.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/financials/ratios?ticker=AAPL&price=228.87&limit=1&sort=ticker.asc',
    response: `{
  "count": 1,
  "request_id": "8f5374516fec4a819070e53609f47fab",
  "results": [
    {
      "ticker": "AAPL",
      "cik": "320193",
      "date": "2024-09-19",
      "price": 228.87,
      "market_cap": 3479770835190,
      "price_to_earnings": 34.84,
      "price_to_book": 52.16,
      "return_on_assets": 0.3075,
      "return_on_equity": 1.5284,
      "debt_to_equity": 1.52,
      "current": 0.68,
      "quick": 0.63
    }
  ],
  "status": "OK"
}`,
    notes: ['Numeric query fields support comparison modifiers such as .gt, .gte, .lt, and .lte.'],
  },
  {
    id: 'us-income-statements',
    group: 'U.S. Stocks',
    title: 'Income Statements',
    method: 'GET',
    path: '/stocks/financials/income-statements',
    summary: 'Quarterly and annual revenue, expense, and earnings flows.',
    description: 'Retrieve normalized income statements reported by public companies. Unlike the balance sheet, these records are flows measured across a fiscal period, so a quarterly row and an annual row for the same year describe different windows and must not be mixed in one series.',
    useCases: ['Fundamental analysis', 'Earnings-trend research', 'Margin analysis', 'Screening and modeling'],
    parameters: [
      { name: 'cik', type: 'string', description: 'SEC Central Index Key.', example: '0000320193' },
      { name: 'tickers', type: 'string', description: 'Filter for records whose ticker array contains this symbol.', example: 'AAPL' },
      { name: 'period_end', type: 'date', description: 'Period end in YYYY-MM-DD. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2025-09-27' },
      { name: 'filing_date', type: 'date', description: 'Most recent SEC filing date containing the period. Supports comparison suffixes.', example: '2025-10-31' },
      { name: 'fiscal_year', type: 'number', description: 'Fiscal year. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2025' },
      { name: 'fiscal_quarter', type: 'number', description: 'Fiscal quarter from 1 to 4.', example: '4' },
      { name: 'timeframe', type: 'string', description: 'Reporting period: quarterly or annual.', example: 'annual' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 50,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Comma-separated fields with .asc or .desc.', example: 'period_end.desc' },
    ],
    fields: [
      { name: 'results[].cik', type: 'string', description: 'SEC Central Index Key.' },
      { name: 'results[].tickers', type: 'array[string]', description: 'Listed ticker symbols.' },
      { name: 'results[].period_end', type: 'date', description: 'Financial reporting period end.' },
      { name: 'results[].filing_date', type: 'date', description: 'Most recent filing date containing this record.' },
      { name: 'results[].fiscal_year', type: 'number', description: 'Fiscal year.' },
      { name: 'results[].fiscal_quarter', type: 'number', description: 'Fiscal quarter.' },
      { name: 'results[].timeframe', type: 'string', description: 'Quarterly or annual.' },
      { name: 'results[].revenues', type: 'number', description: 'Total revenue recognized in the period.' },
      { name: 'results[].cost_of_revenue', type: 'number', description: 'Direct cost of goods and services sold.' },
      { name: 'results[].gross_profit', type: 'number', description: 'Revenue less cost of revenue.' },
      { name: 'results[].operating_expenses', type: 'number', description: 'Total operating expenses for the period.' },
      { name: 'results[].operating_income', type: 'number', description: 'Gross profit less operating expenses.' },
      { name: 'results[].income_tax_expense', type: 'number', description: 'Income tax expense or benefit.' },
      { name: 'results[].net_income', type: 'number', description: 'Net income attributable to the company.' },
      { name: 'results[].basic_earnings_per_share', type: 'number', description: 'Basic earnings per share.' },
      { name: 'results[].diluted_earnings_per_share', type: 'number', description: 'Diluted earnings per share.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/financials/income-statements?tickers=AAPL&timeframe=annual&sort=period_end.desc&limit=1',
    response: `{
  "request_id": "7c4a1de8-...",
  "results": [
    {
      "cik": "0000320193",
      "tickers": ["AAPL"],
      "period_end": "2025-09-27",
      "filing_date": "2025-10-31",
      "fiscal_year": 2025,
      "fiscal_quarter": 4,
      "timeframe": "annual",
      "revenues": 416161000000,
      "cost_of_revenue": 219000000000,
      "gross_profit": 197161000000,
      "operating_income": 127364000000,
      "net_income": 112010000000,
      "diluted_earnings_per_share": 7.46
    }
  ],
  "status": "OK"
}`,
    notes: ['Quarterly and annual rows cover different windows; filter with timeframe before building a time series.'],
  },
  {
    id: 'us-cash-flow-statements',
    group: 'U.S. Stocks',
    title: 'Cash Flow Statements',
    method: 'GET',
    path: '/stocks/financials/cash-flow-statements',
    summary: 'Operating, investing, and financing cash movement per period.',
    description: 'Retrieve normalized cash flow statements reported by public companies. Cash flow is the statement least distorted by accounting policy, which makes it the usual cross-check on reported earnings quality.',
    useCases: ['Earnings-quality checks', 'Free-cash-flow modeling', 'Capital-allocation research', 'Valuation'],
    parameters: [
      { name: 'cik', type: 'string', description: 'SEC Central Index Key.', example: '0000320193' },
      { name: 'tickers', type: 'string', description: 'Filter for records whose ticker array contains this symbol.', example: 'AAPL' },
      { name: 'period_end', type: 'date', description: 'Period end in YYYY-MM-DD. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2025-09-27' },
      { name: 'filing_date', type: 'date', description: 'Most recent SEC filing date containing the period. Supports comparison suffixes.', example: '2025-10-31' },
      { name: 'fiscal_year', type: 'number', description: 'Fiscal year. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2025' },
      { name: 'fiscal_quarter', type: 'number', description: 'Fiscal quarter from 1 to 4.', example: '4' },
      { name: 'timeframe', type: 'string', description: 'Reporting period: quarterly or annual.', example: 'annual' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 50,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Comma-separated fields with .asc or .desc.', example: 'period_end.desc' },
    ],
    fields: [
      { name: 'results[].cik', type: 'string', description: 'SEC Central Index Key.' },
      { name: 'results[].tickers', type: 'array[string]', description: 'Listed ticker symbols.' },
      { name: 'results[].period_end', type: 'date', description: 'Financial reporting period end.' },
      { name: 'results[].filing_date', type: 'date', description: 'Most recent filing date containing this record.' },
      { name: 'results[].fiscal_year', type: 'number', description: 'Fiscal year.' },
      { name: 'results[].fiscal_quarter', type: 'number', description: 'Fiscal quarter.' },
      { name: 'results[].timeframe', type: 'string', description: 'Quarterly or annual.' },
      { name: 'results[].net_cash_flow_from_operating_activities', type: 'number', description: 'Cash generated by operations.' },
      { name: 'results[].net_cash_flow_from_investing_activities', type: 'number', description: 'Cash used in or provided by investing.' },
      { name: 'results[].net_cash_flow_from_financing_activities', type: 'number', description: 'Cash used in or provided by financing.' },
      { name: 'results[].capital_expenditure', type: 'number', description: 'Purchases of property, plant, and equipment.' },
      { name: 'results[].free_cash_flow', type: 'number', description: 'Operating cash flow less capital expenditure.' },
      { name: 'results[].net_change_in_cash', type: 'number', description: 'Net movement in cash and equivalents for the period.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/financials/cash-flow-statements?tickers=AAPL&timeframe=annual&sort=period_end.desc&limit=1',
    response: `{
  "request_id": "2b7f5a90-...",
  "results": [
    {
      "cik": "0000320193",
      "tickers": ["AAPL"],
      "period_end": "2025-09-27",
      "filing_date": "2025-10-31",
      "fiscal_year": 2025,
      "fiscal_quarter": 4,
      "timeframe": "annual",
      "net_cash_flow_from_operating_activities": 122151000000,
      "net_cash_flow_from_investing_activities": -13668000000,
      "net_cash_flow_from_financing_activities": -103510000000,
      "capital_expenditure": -12720000000,
      "free_cash_flow": 109431000000
    }
  ],
  "status": "OK"
}`,
  },
  {
    id: 'us-short-interest',
    group: 'U.S. Stocks',
    title: 'Short Interest',
    method: 'GET',
    path: '/stocks/short-interest',
    summary: 'Bi-monthly reported short positions and days to cover.',
    description: 'Retrieve reported short interest per security by settlement date, along with the average daily volume used to derive days to cover. Exchanges collect these positions twice a month, so the series is a settlement-date snapshot rather than a daily measure.',
    useCases: ['Crowding and squeeze analysis', 'Sentiment research', 'Risk screening', 'Event studies'],
    parameters: [
      { name: 'ticker', type: 'string', description: 'Stock ticker symbol. Supports filter modifiers.', example: 'AAPL' },
      { name: 'settlement_date', type: 'date', description: 'Settlement date in YYYY-MM-DD. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2026-02-27' },
      { name: 'days_to_cover', type: 'number', description: 'Filter by days to cover. Supports comparison suffixes.', example: '2.5' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 50,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Comma-separated fields with .asc or .desc. Defaults to ticker.asc.', example: 'settlement_date.desc' },
    ],
    fields: [
      { name: 'results[].ticker', type: 'string', description: 'Stock ticker symbol.' },
      { name: 'results[].settlement_date', type: 'date', description: 'Settlement date the position was reported for.' },
      { name: 'results[].short_interest', type: 'number', description: 'Shares held short on the settlement date.' },
      { name: 'results[].avg_daily_volume', type: 'number', description: 'Average daily volume used for the days-to-cover ratio.' },
      { name: 'results[].days_to_cover', type: 'number', description: 'Short interest divided by average daily volume.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/short-interest?ticker=AAPL&sort=settlement_date.desc&limit=2',
    response: `{
  "request_id": "c9f42a17-...",
  "results": [
    {
      "ticker": "AAPL",
      "settlement_date": "2026-02-27",
      "short_interest": 118420361,
      "avg_daily_volume": 52318994,
      "days_to_cover": 2.26
    }
  ],
  "status": "OK"
}`,
    notes: ['Positions are reported twice a month and publish several settlement days in arrears.'],
  },
  {
    id: 'us-short-volume',
    group: 'U.S. Stocks',
    title: 'Short Volume',
    method: 'GET',
    path: '/stocks/short-volume',
    summary: 'Daily short-sale volume and its share of total volume.',
    description: 'Retrieve daily short-sale volume per security with the total volume it is measured against, so the daily short ratio can be computed directly. This is a trading-activity measure and is unrelated to the settlement-date short interest reported twice a month.',
    useCases: ['Daily sentiment tracking', 'Flow analysis', 'Signal construction', 'Liquidity research'],
    parameters: [
      { name: 'ticker', type: 'string', description: 'Stock ticker symbol. Supports filter modifiers.', example: 'AAPL' },
      { name: 'date', type: 'date', description: 'Trading date in YYYY-MM-DD. Supports .gt, .gte, .lt, and .lte suffixes.', example: '2026-03-06' },
      { name: 'short_volume_ratio', type: 'number', description: 'Filter by the short-volume ratio. Supports comparison suffixes.', example: '0.45' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 100; maximum 50,000.', example: '100' },
      { name: 'sort', type: 'string', description: 'Comma-separated fields with .asc or .desc. Defaults to ticker.asc.', example: 'date.desc' },
    ],
    fields: [
      { name: 'results[].ticker', type: 'string', description: 'Stock ticker symbol.' },
      { name: 'results[].date', type: 'date', description: 'Trading date of the measurement.' },
      { name: 'results[].short_volume', type: 'number', description: 'Shares sold short during the session.' },
      { name: 'results[].total_volume', type: 'number', description: 'Total reported volume for the session.' },
      { name: 'results[].short_volume_ratio', type: 'number', description: 'Short volume as a share of total volume.' },
      { name: 'results[].exempt_volume', type: 'number', description: 'Short-exempt volume for the session.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/short-volume?ticker=AAPL&sort=date.desc&limit=2',
    response: `{
  "request_id": "5a1c8d63-...",
  "results": [
    {
      "ticker": "AAPL",
      "date": "2026-03-06",
      "short_volume": 24118370,
      "total_volume": 54903118,
      "short_volume_ratio": 0.4393
    }
  ],
  "status": "OK"
}`,
    notes: ['A high short-volume ratio reflects intraday market-making as well as directional selling; it is not by itself a bearish signal.'],
  },
  {
    id: 'us-stocks-daily-summary',
    group: 'U.S. Stocks',
    title: 'Daily Ticker Summary',
    method: 'GET',
    path: '/open-close/{stocksTicker}/{date}',
    summary: 'Opening, closing, and extended-hours prices for one trading day.',
    description: 'Retrieve the opening and closing prices for a U.S. stock on a specified date, together with pre-market and after-hours trade prices when available. The result provides a compact view of the full trading day.',
    useCases: ['Daily performance analysis', 'Historical data collection', 'After-hours insights', 'Portfolio tracking'],
    parameters: [
      { name: 'stocksTicker', type: 'string', required: true, description: 'Case-sensitive stock ticker symbol.', example: 'AAPL' },
      { name: 'date', type: 'date', required: true, description: 'Trading date in YYYY-MM-DD format.', example: '2025-01-02' },
      { name: 'adjusted', type: 'boolean', description: 'Adjust the result for splits. Defaults to true.', example: 'true' },
    ],
    fields: [
      { name: 'symbol', type: 'string', description: 'Ticker symbol for the result.' },
      { name: 'from', type: 'date', description: 'Trading date represented by the summary.' },
      { name: 'open', type: 'number', description: 'Regular-session opening price.' },
      { name: 'high', type: 'number', description: 'Highest regular-session price.' },
      { name: 'low', type: 'number', description: 'Lowest regular-session price.' },
      { name: 'close', type: 'number', description: 'Regular-session closing price.' },
      { name: 'volume', type: 'number', description: 'Regular-session trading volume.' },
      { name: 'preMarket', type: 'number', description: 'Latest pre-market trade price, when available.' },
      { name: 'afterHours', type: 'number', description: 'Latest after-hours trade price, when available.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/open-close/AAPL/2025-01-02?adjusted=true',
    response: `{
  "afterHours": 243.12,
  "close": 243.85,
  "from": "2025-01-02",
  "high": 244.18,
  "low": 241.89,
  "open": 243.36,
  "preMarket": 242.71,
  "status": "OK",
  "symbol": "AAPL",
  "volume": 55740731
}`,
  },
  {
    id: 'us-stocks-previous-day-bar',
    group: 'U.S. Stocks',
    title: 'Previous Day Bar',
    method: 'GET',
    path: '/aggs/ticker/{stocksTicker}/prev',
    summary: 'The previous trading day’s OHLC and volume for a stock.',
    description: 'Retrieve the previous trading day’s open, high, low, and close data for a specified stock ticker, including volume, VWAP, and transaction count when available.',
    useCases: ['Baseline comparison', 'Technical analysis', 'Market research', 'Daily reporting'],
    parameters: [
      { name: 'stocksTicker', type: 'string', required: true, description: 'Case-sensitive stock ticker symbol.', example: 'AAPL' },
      { name: 'adjusted', type: 'boolean', description: 'Adjust results for splits. Defaults to true.', example: 'true' },
    ],
    fields: aggregateFields,
    request: 'https://api.eroya.co/v1/aggs/ticker/AAPL/prev?adjusted=true',
    response: `{
  "adjusted": true,
  "queryCount": 1,
  "request_id": "6a7e466379af0a71039d60cc78e72282",
  "results": [
    {
      "T": "AAPL",
      "c": 115.97,
      "h": 117.59,
      "l": 114.13,
      "o": 115.55,
      "t": 1605042000000,
      "v": 131704427,
      "vw": 116.3058
    }
  ],
  "resultsCount": 1,
  "status": "OK",
  "ticker": "AAPL"
}`,
  },
  {
    id: 'us-stocks-full-market-snapshot',
    group: 'U.S. Stocks',
    title: 'Full Market Snapshot',
    method: 'GET',
    path: '/snapshot/locale/us/markets/stocks/tickers',
    summary: 'Current snapshot data for the full U.S. stock market.',
    description: 'Retrieve a comprehensive snapshot of more than 10,000 actively traded U.S. tickers in one response. Each ticker can include its latest daily and minute bars, previous-day bar, last trade, last quote, and day-over-day change. Snapshot state clears daily before pre-market and repopulates as exchanges report new data.',
    useCases: ['Market overview', 'Bulk data processing', 'Heat maps and dashboards', 'Automated monitoring'],
    parameters: [
      { name: 'tickers', type: 'array[string]', description: 'Case-sensitive comma-separated tickers. Omit or pass an empty value for the full market.', example: 'AAPL,TSLA,GOOG' },
      { name: 'include_otc', type: 'boolean', description: 'Include OTC securities. Defaults to false.', example: 'false' },
    ],
    fields: [
      { name: 'count', type: 'integer', description: 'Total number of returned ticker snapshots.' },
      { name: 'tickers[].ticker', type: 'string', description: 'Exchange symbol for the security.' },
      { name: 'tickers[].day', type: 'object', description: 'Most recent daily aggregate bar.' },
      { name: 'tickers[].min', type: 'object', description: 'Most recent minute aggregate bar.' },
      { name: 'tickers[].prevDay', type: 'object', description: 'Previous trading day’s aggregate bar.' },
      { name: 'tickers[].lastTrade', type: 'object', description: 'Most recent trade, when included by the plan.' },
      { name: 'tickers[].lastQuote', type: 'object', description: 'Most recent quote, when included by the plan.' },
      { name: 'tickers[].fmv', type: 'number', description: 'Fair market value, when included by the plan.' },
      { name: 'tickers[].todaysChange', type: 'number', description: 'Price change from the previous day.' },
      { name: 'tickers[].todaysChangePerc', type: 'number', description: 'Percentage change from the previous day.' },
      { name: 'tickers[].updated', type: 'integer', description: 'Nanosecond timestamp of the latest update.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/snapshot/locale/us/markets/stocks/tickers?include_otc=false',
    response: `{
  "count": 1,
  "status": "OK",
  "tickers": [
    {
      "day": { "o": 20.64, "h": 20.64, "l": 20.506, "c": 20.506, "v": 37216, "vw": 20.616 },
      "lastQuote": { "P": 20.6, "S": 22, "p": 20.5, "s": 13, "t": 1605192959994246100 },
      "lastTrade": { "p": 20.506, "s": 2416, "t": 1605192894630916600, "x": 4 },
      "prevDay": { "o": 20.79, "h": 21, "l": 20.5, "c": 20.63, "v": 292738, "vw": 20.6939 },
      "ticker": "BCAT",
      "todaysChange": -0.124,
      "todaysChangePerc": -0.601,
      "updated": 1605192894630916600
    }
  ]
}`,
  },
  {
    id: 'us-disclosure-categories',
    group: 'U.S. Stocks',
    title: 'Disclosure Categories',
    method: 'GET',
    path: '/stocks/taxonomies/disclosures',
    summary: 'The standardized taxonomy used to classify Form 8-K disclosures.',
    description: 'Access the full hierarchical taxonomy behind standardized Form 8-K disclosure classifications. Primary, secondary, and tertiary categories provide a controlled vocabulary for filtering and comparing material corporate events.',
    useCases: ['Event-classification models', 'Category analytics and dashboards', 'Cross-company comparisons', 'Thematic monitoring'],
    parameters: [
      { name: 'taxonomy', type: 'string', description: 'Taxonomy version defining the classification. Supports filter modifiers.', example: '1.0' },
      { name: 'primary_category', type: 'string', description: 'Top-level disclosure category. Supports filter modifiers.', example: 'Capital Markets' },
      { name: 'secondary_category', type: 'string', description: 'Mid-level disclosure category. Supports filter modifiers.' },
      { name: 'tertiary_category', type: 'string', description: 'Most-specific disclosure category. Supports filter modifiers.' },
      { name: 'limit', type: 'integer', description: 'Maximum results. Default 200; maximum 1,000.', example: '200' },
      { name: 'sort', type: 'string', description: 'Comma-separated sort columns with .asc or .desc. Defaults to taxonomy.desc.', example: 'taxonomy.desc' },
    ],
    fields: [
      { name: 'results[].taxonomy', type: 'string', description: 'Taxonomy version defining the classification.' },
      { name: 'results[].primary_category', type: 'string', description: 'Top-level disclosure category.' },
      { name: 'results[].secondary_category', type: 'string', description: 'Mid-level disclosure category.' },
      { name: 'results[].tertiary_category', type: 'string', description: 'Most-specific disclosure category.' },
      { name: 'results[].description', type: 'string', description: 'Detailed explanation and typical examples.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/stocks/taxonomies/disclosures?taxonomy=1.0&limit=200&sort=taxonomy.desc',
    response: `{
  "request_id": "f07c4a31-...",
  "results": [
    {
      "description": "A material agreement entered into outside the ordinary course of business.",
      "primary_category": "Business and Operations",
      "secondary_category": "Material Agreements",
      "taxonomy": "1.0",
      "tertiary_category": "Entry into a Material Definitive Agreement"
    }
  ],
  "status": "OK"
}`,
    notes: ['This early-access endpoint may change while the upstream taxonomy is in beta.'],
  },
  {
    id: 'us-options-daily-summary',
    group: 'U.S. Options',
    title: 'Daily Ticker Summary',
    method: 'GET',
    path: '/open-close/{optionsTicker}/{date}',
    summary: 'Opening and closing prices for an options contract on one date.',
    description: 'Retrieve the opening and closing prices for a specified options contract on a trading date, together with extended-hours prices when available.',
    useCases: ['Daily performance analysis', 'Historical data collection', 'Contract research', 'Portfolio tracking'],
    parameters: [
      { name: 'optionsTicker', type: 'string', required: true, description: 'Canonical options contract ticker with the O: prefix.', example: 'O:SPY280121C00750000' },
      { name: 'date', type: 'date', required: true, description: 'Trading date in YYYY-MM-DD format.', example: '2025-01-02' },
      { name: 'adjusted', type: 'boolean', description: 'Adjust the result for splits. Defaults to true.', example: 'true' },
    ],
    fields: [
      { name: 'symbol', type: 'string', description: 'Options contract ticker.' },
      { name: 'from', type: 'date', description: 'Trading date represented by the summary.' },
      { name: 'open', type: 'number', description: 'Opening price.' },
      { name: 'high', type: 'number', description: 'Highest price.' },
      { name: 'low', type: 'number', description: 'Lowest price.' },
      { name: 'close', type: 'number', description: 'Closing price.' },
      { name: 'volume', type: 'number', description: 'Contract volume.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/open-close/O:SPY280121C00750000/2025-01-02?adjusted=true',
    response: `{
  "close": 7.20,
  "from": "2025-01-02",
  "high": 7.45,
  "low": 6.80,
  "open": 6.91,
  "status": "OK",
  "symbol": "O:SPY280121C00750000",
  "volume": 128
}`,
  },
  {
    id: 'us-options-previous-day-bar',
    group: 'U.S. Options',
    title: 'Previous Day Bar',
    method: 'GET',
    path: '/aggs/ticker/{optionsTicker}/prev',
    summary: 'The previous trading day’s OHLC and volume for an options contract.',
    description: 'Retrieve the previous trading day’s open, high, low, and close data for a specified options contract, including volume, VWAP, and transaction count when available.',
    useCases: ['Baseline comparison', 'Technical analysis', 'Contract research', 'Daily reporting'],
    parameters: [
      { name: 'optionsTicker', type: 'string', required: true, description: 'Canonical options contract ticker with the O: prefix.', example: 'O:SPY280121C00750000' },
      { name: 'adjusted', type: 'boolean', description: 'Adjust results for splits. Defaults to true.', example: 'true' },
    ],
    fields: aggregateFields,
    request: 'https://api.eroya.co/v1/aggs/ticker/O:SPY280121C00750000/prev?adjusted=true',
    response: `{
  "adjusted": true,
  "queryCount": 1,
  "results": [
    { "T": "O:SPY280121C00750000", "o": 6.91, "h": 7.45, "l": 6.80, "c": 7.20, "v": 128, "t": 1735813800000 }
  ],
  "resultsCount": 1,
  "status": "OK",
  "ticker": "O:SPY280121C00750000"
}`,
  },
  {
    id: 'us-options-chain-snapshot',
    group: 'U.S. Options',
    title: 'Option Chain Snapshot',
    method: 'GET',
    path: '/snapshot/options/{underlyingAsset}',
    summary: 'Current market data, greeks, and open interest across an option chain.',
    description: 'Retrieve a comprehensive snapshot of options contracts associated with an underlying ticker. Results consolidate contract details, pricing, greeks, implied volatility, quotes, trades, open interest, the underlying price, and break-even calculations.',
    useCases: ['Market overview', 'Strategy comparison', 'Research and modeling', 'Portfolio refinement'],
    parameters: [
      { name: 'underlyingAsset', type: 'string', required: true, description: 'Ticker symbol of the underlying asset.', example: 'SPY' },
      { name: 'strike_price', type: 'number', description: 'Filter by strike price. Supports filter modifiers.', example: '750' },
      { name: 'expiration_date', type: 'date', description: 'Filter by expiration date in YYYY-MM-DD. Supports filter modifiers.', example: '2028-01-21' },
      { name: 'contract_type', type: 'string', description: 'Filter by contract type: call or put.', example: 'call' },
      { name: 'order', type: 'string', description: 'Order by the sort field: asc or desc.', example: 'asc' },
      { name: 'limit', type: 'integer', description: 'Results per page. Default 10; maximum 250.', example: '10' },
      { name: 'sort', type: 'string', description: 'Sort field. Defaults to ticker.', example: 'ticker' },
    ],
    fields: [
      { name: 'results[].break_even_price', type: 'number', description: 'Underlying price at which the contract breaks even.' },
      { name: 'results[].day', type: 'object', description: 'Most recent daily bar for the contract.' },
      { name: 'results[].details', type: 'object', description: 'Contract type, expiration, strike, and exercise details.' },
      { name: 'results[].fmv', type: 'number', description: 'Fair market value, when included by the plan.' },
      { name: 'results[].fmv_last_updated', type: 'integer', description: 'Nanosecond timestamp of the latest FMV calculation.' },
      { name: 'results[].greeks', type: 'object', description: 'Delta, gamma, theta, and vega, when available.' },
      { name: 'results[].implied_volatility', type: 'number', description: 'Market-implied forecast of underlying volatility.' },
      { name: 'results[].last_quote', type: 'object', description: 'Most recent contract quote, when included by the plan.' },
      { name: 'results[].last_trade', type: 'object', description: 'Most recent contract trade, when included by the plan.' },
      { name: 'results[].open_interest', type: 'number', description: 'Contracts held at the end of the previous trading day.' },
      { name: 'results[].underlying_asset', type: 'object', description: 'Market information for the underlying stock.' },
      { name: 'next_url', type: 'string', description: 'URL for the next page, when present.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/snapshot/options/SPY?contract_type=call&limit=10&sort=ticker',
    response: `{
  "request_id": "2f67f8b4-...",
  "results": [
    {
      "break_even_price": 755.15,
      "details": { "contract_type": "call", "expiration_date": "2028-01-21", "strike_price": 750, "ticker": "O:SPY280121C00750000" },
      "greeks": { "delta": 0.42, "gamma": 0.01, "theta": -0.03, "vega": 0.71 },
      "implied_volatility": 0.24,
      "open_interest": 1320,
      "underlying_asset": { "ticker": "SPY", "price": 742.60 }
    }
  ],
  "status": "OK"
}`,
    notes: ['Greeks may be absent for contracts where the upstream cannot produce a stable calculation, including some deep-in-the-money contracts.'],
  },
  {
    id: 'us-options-last-trade',
    group: 'U.S. Options',
    title: 'Last Trade',
    method: 'GET',
    path: '/last/trade/{optionsTicker}',
    summary: 'The latest available trade for an options contract.',
    description: 'Retrieve the most recent trade for a specified options contract, including price, size, exchange, condition codes, identifiers, and exchange, SIP, and TRF timestamps when available.',
    useCases: ['Trade monitoring', 'Price updates', 'Market snapshots'],
    parameters: [
      { name: 'optionsTicker', type: 'string', required: true, description: 'Canonical options contract ticker with the O: prefix.', example: 'O:SPY280121C00750000' },
    ],
    fields: [
      { name: 'results.T', type: 'string', description: 'Options contract ticker.' },
      { name: 'results.c', type: 'array[integer]', description: 'Trade condition codes.' },
      { name: 'results.e', type: 'integer', description: 'Trade correction indicator.' },
      { name: 'results.f', type: 'integer', description: 'Nanosecond TRF Unix timestamp.' },
      { name: 'results.i', type: 'string', description: 'Trade identifier, unique per ticker, exchange, and TRF.' },
      { name: 'results.p', type: 'number', description: 'Trade price per whole share.' },
      { name: 'results.q', type: 'integer', description: 'Per-ticker event sequence number.' },
      { name: 'results.r', type: 'integer', description: 'Trade Reporting Facility identifier.' },
      { name: 'results.s', type: 'number', description: 'Trade size.' },
      { name: 'results.t', type: 'integer', description: 'Nanosecond SIP Unix timestamp.' },
      { name: 'results.x', type: 'integer', description: 'Exchange identifier.' },
      { name: 'results.y', type: 'integer', description: 'Nanosecond participant/exchange Unix timestamp.' },
      { name: 'results.z', type: 'integer', description: 'Listing tape: 1=A, 2=B, or 3=C.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/last/trade/O:SPY280121C00750000',
    response: `{
  "request_id": "a47d1beb8c11b6ae897ab76cdbbf35a3",
  "results": {
    "T": "O:SPY280121C00750000",
    "c": [209],
    "i": "71675577320245",
    "p": 7.20,
    "q": 821,
    "s": 1,
    "t": 1645119125346243600,
    "x": 67,
    "y": 1645119125346200000
  },
  "status": "OK"
}`,
  },
  {
    id: 'us-options-exchanges',
    group: 'U.S. Options',
    title: 'Exchanges',
    method: 'GET',
    path: '/reference/exchanges',
    summary: 'Known U.S. options exchanges and their identifiers.',
    description: 'Retrieve known exchanges with their venue identifiers, names, market types, MICs, participant IDs, and related metadata. Pass asset_class=options and locale=us so the request is signed with the options entitlement.',
    useCases: ['Data mapping', 'Market coverage analysis', 'Application development', 'Regulatory compliance'],
    parameters: [
      { name: 'asset_class', type: 'string', required: true, description: 'Asset class to return. Use options for this endpoint.', example: 'options' },
      { name: 'locale', type: 'string', required: true, description: 'Geographic locale. Use us for U.S. options exchanges.', example: 'us' },
    ],
    fields: [
      { name: 'count', type: 'integer', description: 'Total number of returned exchanges.' },
      { name: 'results[].id', type: 'integer', description: 'Unique exchange identifier used in market-data records.' },
      { name: 'results[].name', type: 'string', description: 'Exchange name.' },
      { name: 'results[].acronym', type: 'string', description: 'Common exchange abbreviation.' },
      { name: 'results[].asset_class', type: 'string', description: 'Asset class traded by the exchange.' },
      { name: 'results[].locale', type: 'string', description: 'Geographic locale.' },
      { name: 'results[].mic', type: 'string', description: 'ISO 10383 Market Identifier Code.' },
      { name: 'results[].operating_mic', type: 'string', description: 'MIC of the operating entity.' },
      { name: 'results[].participant_id', type: 'string', description: 'Identifier used by the SIP.' },
      { name: 'results[].type', type: 'string', description: 'Venue type: SIP, TRF, or exchange.' },
      { name: 'results[].url', type: 'string', description: 'Exchange website, when available.' },
      { name: 'request_id', type: 'string', description: 'Unique request identifier.' },
      { name: 'status', type: 'string', description: 'Request status.' },
    ],
    request: 'https://api.eroya.co/v1/reference/exchanges?asset_class=options&locale=us',
    response: `{
  "count": 1,
  "request_id": "d10f6e21-...",
  "results": [
    {
      "acronym": "CBOE",
      "asset_class": "options",
      "id": 302,
      "locale": "us",
      "mic": "XCBO",
      "name": "Cboe Options Exchange",
      "operating_mic": "XCBO",
      "type": "exchange",
      "url": "https://www.cboe.com"
    }
  ],
  "status": "OK"
}`,
  },
  {
    id: 'cn-daily',
    group: 'China A-shares',
    title: 'Daily prices',
    method: 'GET',
    path: '/china/daily',
    summary: 'Unadjusted A-share daily OHLC and trading activity.',
    description: 'Daily A-share prices published after the market close. Suspended instruments have no row. Prices are unadjusted; use the adjustment-factor dataset when required.',
    useCases: ['Daily screening', 'Historical analysis', 'Market breadth', 'Backtesting'],
    parameters: chinaCommon,
    fields: [
      { name: 'ts_code', type: 'string', description: 'TS instrument code.' },
      { name: 'trade_date', type: 'string', description: 'Trading date in YYYYMMDD format.' },
      { name: 'open', type: 'number', description: 'Open price.' },
      { name: 'high', type: 'number', description: 'High price.' },
      { name: 'low', type: 'number', description: 'Low price.' },
      { name: 'close', type: 'number', description: 'Close price.' },
      { name: 'pre_close', type: 'number', description: 'Previous split-adjusted close.' },
      { name: 'change', type: 'number', description: 'Absolute price change.' },
      { name: 'pct_chg', type: 'number', description: 'Percentage change based on adjusted previous close.' },
      { name: 'vol', type: 'number', description: 'Trading volume in board lots.' },
      { name: 'amount', type: 'number', description: 'Turnover in CNY thousands.' },
      { name: 'ah_vol', type: 'number', description: 'After-hours volume in board lots, when available.' },
      { name: 'ah_amount', type: 'number', description: 'After-hours turnover in CNY thousands, when available.' },
    ],
    request: 'https://api.eroya.co/v1/china/daily?ts_code=000001.SZ&start_date=20180701&end_date=20180718',
    response: `{
  "code": 0,
  "msg": null,
  "data": {
    "fields": ["ts_code", "trade_date", "open", "high", "low", "close", "pre_close", "change", "pct_chg", "vol", "amount"],
    "items": [
      ["000001.SZ", "20180718", 8.75, 8.85, 8.69, 8.70, 8.72, -0.02, -0.23, 525152.77, 460697.377]
    ]
  }
}`,
    notes: ['For full-market extraction, iterate trading dates instead of instrument codes.', 'Dates use YYYYMMDD.', ...chinaEnvelopeNotes],
  },
  {
    id: 'cn-cyq-perf',
    group: 'China A-shares',
    title: 'Chip cost & winner rate',
    method: 'GET',
    path: '/china/cyq_perf',
    summary: 'Daily position-cost distribution and winner rate.',
    description: 'Retrieve daily position-cost percentiles, historical range, weighted average cost, and the percentage of profitable holdings. Coverage starts in 2018.',
    useCases: ['Cost-basis analysis', 'Positioning signals', 'Support/resistance', 'Daily screening'],
    parameters: chinaCommon.map((item) => item.name === 'ts_code' ? { ...item, required: true } : item),
    fields: [
      { name: 'ts_code', type: 'string', description: 'TS instrument code.' },
      { name: 'trade_date', type: 'string', description: 'Trading date in YYYYMMDD format.' },
      { name: 'his_low', type: 'number', description: 'Historical low price.' },
      { name: 'his_high', type: 'number', description: 'Historical high price.' },
      { name: 'cost_5pct', type: 'number', description: '5th-percentile position cost.' },
      { name: 'cost_15pct', type: 'number', description: '15th-percentile position cost.' },
      { name: 'cost_50pct', type: 'number', description: 'Median position cost.' },
      { name: 'cost_85pct', type: 'number', description: '85th-percentile position cost.' },
      { name: 'cost_95pct', type: 'number', description: '95th-percentile position cost.' },
      { name: 'weight_avg', type: 'number', description: 'Weighted average position cost.' },
      { name: 'winner_rate', type: 'number', description: 'Percentage of holdings currently profitable.' },
    ],
    request: 'https://api.eroya.co/v1/china/cyq_perf?ts_code=600000.SH&start_date=20220101&end_date=20220429',
    response: `{
  "code": 0,
  "msg": null,
  "data": {
    "fields": ["ts_code", "trade_date", "his_low", "his_high", "cost_5pct", "cost_95pct", "weight_avg", "winner_rate"],
    "items": [
      ["600000.SH", "20220429", 0.72, 12.16, 8.18, 11.34, 9.76, 3.52]
    ]
  }
}`,
    notes: ['Maximum 6,000 rows per call. Paginate or divide the requested date range.', ...chinaEnvelopeNotes],
  },
  {
    id: 'cn-technical-factors',
    group: 'China A-shares',
    title: 'Technical factors',
    method: 'GET',
    path: '/china/stk_factor',
    summary: 'Daily adjusted prices and quantitative technical indicators.',
    description: 'Retrieve full-history daily price factors and technical indicators produced for China A-shares. Indicators are calculated from forward-adjusted prices.',
    useCases: ['Factor research', 'Signal generation', 'Trend analysis', 'Quantitative screening'],
    parameters: chinaCommon,
    fields: [
      { name: 'ts_code', type: 'string', description: 'TS stock code.' },
      { name: 'trade_date', type: 'string', description: 'Trading date in YYYYMMDD format.' },
      { name: 'open', type: 'number', description: 'Unadjusted open price.' },
      { name: 'high', type: 'number', description: 'Unadjusted high price.' },
      { name: 'low', type: 'number', description: 'Unadjusted low price.' },
      { name: 'close', type: 'number', description: 'Unadjusted close price.' },
      { name: 'pre_close', type: 'number', description: 'Previous close price.' },
      { name: 'change', type: 'number', description: 'Absolute price change.' },
      { name: 'pct_change', type: 'number', description: 'Percentage price change.' },
      { name: 'vol', type: 'number', description: 'Trading volume in board lots.' },
      { name: 'amount', type: 'number', description: 'Turnover in CNY thousands.' },
      { name: 'adj_factor', type: 'number', description: 'Price adjustment factor.' },
      { name: 'open_hfq', type: 'number', description: 'Backward-adjusted open.' },
      { name: 'open_qfq', type: 'number', description: 'Forward-adjusted open.' },
      { name: 'close_hfq', type: 'number', description: 'Backward-adjusted close.' },
      { name: 'close_qfq', type: 'number', description: 'Forward-adjusted close.' },
      { name: 'high_hfq', type: 'number', description: 'Backward-adjusted high.' },
      { name: 'high_qfq', type: 'number', description: 'Forward-adjusted high.' },
      { name: 'low_hfq', type: 'number', description: 'Backward-adjusted low.' },
      { name: 'low_qfq', type: 'number', description: 'Forward-adjusted low.' },
      { name: 'pre_close_hfq', type: 'number', description: 'Backward-adjusted previous close.' },
      { name: 'pre_close_qfq', type: 'number', description: 'Forward-adjusted previous close.' },
      { name: 'macd_dif', type: 'number', description: 'MACD difference line from forward-adjusted prices.' },
      { name: 'macd_dea', type: 'number', description: 'MACD signal line.' },
      { name: 'macd', type: 'number', description: 'MACD histogram.' },
      { name: 'kdj_k', type: 'number', description: 'KDJ K value.' },
      { name: 'kdj_d', type: 'number', description: 'KDJ D value.' },
      { name: 'kdj_j', type: 'number', description: 'KDJ J value.' },
      { name: 'rsi_6', type: 'number', description: '6-period RSI.' },
      { name: 'rsi_12', type: 'number', description: '12-period RSI.' },
      { name: 'rsi_24', type: 'number', description: '24-period RSI.' },
      { name: 'boll_upper', type: 'number', description: 'Bollinger upper band.' },
      { name: 'boll_mid', type: 'number', description: 'Bollinger middle band.' },
      { name: 'boll_lower', type: 'number', description: 'Bollinger lower band.' },
      { name: 'cci', type: 'number', description: 'Commodity Channel Index.' },
    ],
    request: 'https://api.eroya.co/v1/china/stk_factor?ts_code=600000.SH&start_date=20220501&end_date=20220520&fields=ts_code,trade_date,macd,kdj_k,kdj_d,kdj_j',
    response: `{
  "code": 0,
  "msg": null,
  "data": {
    "fields": ["ts_code", "trade_date", "macd", "kdj_k", "kdj_d", "kdj_j"],
    "items": [
      ["600000.SH", "20220520", 0.027, 72.966, 64.718, 89.46],
      ["600000.SH", "20220519", 0.015, 63.615, 60.594, 69.656]
    ]
  }
}`,
    notes: [
      'Maximum 10,000 rows per call. Paginate or divide the requested range.',
      'Forward-adjusted history is a daily snapshot anchored to the latest trading day and is not retrospectively updated.',
      'Dynamic pro_bar adjustment can differ because it anchors adjustment to the requested end_date.',
      'Technical indicators are calculated from forward-adjusted prices.',
      ...chinaEnvelopeNotes,
    ],
  },
  {
    id: 'cn-balance-sheet',
    group: 'China A-shares',
    title: 'Balance sheets',
    method: 'GET',
    path: '/china/balancesheet',
    summary: 'Reported balance sheets for a listed A-share company.',
    description: 'Retrieve historical balance-sheet line items for a listed company, including general industrial, bank, insurance, and securities-company report formats.',
    useCases: ['Fundamental analysis', 'Solvency research', 'Quarterly screening', 'Company valuation'],
    parameters: [
      { name: 'ts_code', type: 'string', required: true, description: 'TS stock code. This dataset accepts one company per request.', example: '600000.SH' },
      { name: 'ann_date', type: 'string', description: 'Announcement date in YYYYMMDD format.', example: '20260430' },
      { name: 'start_date', type: 'string', description: 'Announcement-date range start.', example: '20250101' },
      { name: 'end_date', type: 'string', description: 'Announcement-date range end.', example: '20261231' },
      { name: 'period', type: 'string', description: 'Reporting period end, such as 20251231 for an annual report.', example: '20251231' },
      { name: 'report_type', type: 'string', description: 'Statement report type.', example: '1' },
      { name: 'comp_type', type: 'string', description: 'Company type: 1 industrial, 2 bank, 3 insurance, 4 securities.', example: '1' },
      { name: 'fields', type: 'string', description: 'Comma-separated output fields.', example: 'ts_code,ann_date,end_date,total_assets,total_liab,total_hldr_eqy_exc_min_int' },
    ],
    fields: [
      { name: 'ts_code', type: 'string', description: 'TS stock code.' },
      { name: 'ann_date', type: 'string', description: 'Announcement date.' },
      { name: 'f_ann_date', type: 'string', description: 'Actual announcement date.' },
      { name: 'end_date', type: 'string', description: 'Reporting period end.' },
      { name: 'report_type', type: 'string', description: 'Statement report type.' },
      { name: 'comp_type', type: 'string', description: 'Company type.' },
      { name: 'end_type', type: 'string', description: 'Reporting-period type.' },
      { name: 'money_cap', type: 'number', description: 'Cash and cash equivalents.' },
      { name: 'accounts_receiv', type: 'number', description: 'Accounts receivable.' },
      { name: 'inventories', type: 'number', description: 'Inventories.' },
      { name: 'total_cur_assets', type: 'number', description: 'Total current assets.' },
      { name: 'fix_assets', type: 'number', description: 'Fixed assets.' },
      { name: 'intan_assets', type: 'number', description: 'Intangible assets.' },
      { name: 'goodwill', type: 'number', description: 'Goodwill.' },
      { name: 'total_assets', type: 'number', description: 'Total assets.' },
      { name: 'st_borr', type: 'number', description: 'Short-term borrowings.' },
      { name: 'lt_borr', type: 'number', description: 'Long-term borrowings.' },
      { name: 'acct_payable', type: 'number', description: 'Accounts payable.' },
      { name: 'total_cur_liab', type: 'number', description: 'Total current liabilities.' },
      { name: 'total_liab', type: 'number', description: 'Total liabilities.' },
      { name: 'total_hldr_eqy_exc_min_int', type: 'number', description: 'Equity attributable to parent shareholders.' },
      { name: 'update_flag', type: 'string', description: 'Record update flag.' },
    ],
    request: 'https://api.eroya.co/v1/china/balancesheet?ts_code=600000.SH&period=20251231',
    response: `{
  "code": 0,
  "msg": null,
  "data": {
    "fields": ["ts_code", "ann_date", "end_date", "total_assets", "total_liab", "total_hldr_eqy_exc_min_int"],
    "items": [
      ["600000.SH", "20260425", "20251231", 9462875000000, 8701429000000, 754210000000]
    ]
  }
}`,
    notes: [
      'ts_code is mandatory. A request without it returns upstream error 50101 with the message "必填参数, ts_code" rather than an empty result set.',
      'One company per request. For a whole quarter across every listed company, use /china/balancesheet_vip, which takes the same parameters.',
      ...chinaEnvelopeNotes.slice(1),
    ],
  },
  {
    id: 'cn-disclosure-date',
    group: 'China A-shares',
    title: 'Disclosure schedule',
    method: 'GET',
    path: '/china/disclosure_date',
    summary: 'Planned and actual financial-report disclosure dates.',
    description: 'Retrieve the disclosure schedule for periodic financial reports, including the predicted disclosure date, the latest announced date, and the actual disclosure date for each reporting period. Useful for anticipating earnings releases and building event calendars.',
    useCases: ['Earnings calendars', 'Event-driven strategies', 'Coverage monitoring', 'Reporting-season planning'],
    parameters: [
      { name: 'ts_code', type: 'string', description: 'TS stock code.', example: '300619.SZ' },
      { name: 'end_date', type: 'string', description: 'Reporting period end in YYYYMMDD (e.g. 20181231 for the 2018 annual report, 20180630 for the interim report).', example: '20181231' },
      { name: 'pre_date', type: 'string', description: 'Planned disclosure date in YYYYMMDD.', example: '20190122' },
      { name: 'ann_date', type: 'string', description: 'Latest disclosure announcement date in YYYYMMDD.', example: '20181228' },
      { name: 'actual_date', type: 'string', description: 'Actual disclosure date in YYYYMMDD.', example: '20190122' },
    ],
    fields: [
      { name: 'ts_code', type: 'string', description: 'TS stock code.' },
      { name: 'ann_date', type: 'string', description: 'Latest disclosure announcement date.' },
      { name: 'end_date', type: 'string', description: 'Reporting period end.' },
      { name: 'pre_date', type: 'string', description: 'Predicted disclosure date.' },
      { name: 'actual_date', type: 'string', description: 'Actual disclosure date.' },
      { name: 'modify_date', type: 'string', description: 'Record of disclosure-date revisions.' },
    ],
    request: 'https://api.eroya.co/v1/china/disclosure_date?end_date=20181231',
    response: `{
  "code": 0,
  "msg": null,
  "data": {
    "fields": ["ts_code", "ann_date", "end_date", "pre_date", "actual_date"],
    "items": [
      ["300619.SZ", "20181228", "20181231", "20190122", "20190122"],
      ["601619.SH", "20181227", "20181231", "20190129", "20190129"]
    ]
  }
}`,
    notes: ['Maximum 6,000 rows per call.', 'Dates use YYYYMMDD.', ...chinaEnvelopeNotes],
  },
  {
    id: 'flatfiles-stocks-overview',
    group: 'Flat Files',
    title: 'Stocks Overview',
    method: 'GET',
    path: '/flatfiles/datasets',
    summary: 'Full-market U.S. stock history as one compressed CSV per session.',
    description: 'Use stock Flat Files for large historical pulls that would otherwise require thousands of ticker-by-ticker REST requests. The catalog is intentionally fixed to four archives: daily aggregates, minute aggregates, tick trades, and NBBO quotes, all sourced from U.S. exchanges, FINRA reporting facilities, and dark pools.',
    useCases: ['Full-market backtesting', 'Quantitative research', 'Data lake ingestion', 'Historical model training'],
    parameters: [
      { name: 'dataset', type: 'string', description: 'Choose one of the four hard-coded stock dataset slugs below.', example: 'us-stocks-day-aggs' },
      { name: 'date range', type: 'date', description: 'List published sessions in windows of no more than 366 days, then download each returned date.' },
    ],
    fields: [
      { name: 'day aggregates', type: 'csv.gz', description: 'One unadjusted OHLCV candle per ticker and trading day.' },
      { name: 'minute aggregates', type: 'csv.gz', description: 'One unadjusted OHLCV candle per ticker and minute.' },
      { name: 'trades', type: 'csv.gz', description: 'Tick-level U.S. equity executions with nanosecond timestamps.' },
      { name: 'quotes', type: 'csv.gz', description: 'Tick-level top-of-book bid and ask updates with nanosecond timestamps.' },
    ],
    request: 'https://api.eroya.co/v1/flatfiles/datasets',
    response: `{
  "datasets": [
    { "slug": "us-stocks-day-aggs", "path": "us_stocks_sip/day_aggs_v1" },
    { "slug": "us-stocks-minute-aggs", "path": "us_stocks_sip/minute_aggs_v1" },
    { "slug": "us-stocks-trades", "path": "us_stocks_sip/trades_v1" },
    { "slug": "us-stocks-quotes", "path": "us_stocks_sip/quotes_v1" }
  ]
}`,
    relatedDatasets: [
      { id: 'flatfiles-stocks-day-aggregates', title: 'Day Aggregates', path: 'us_stocks_sip/day_aggs_v1', description: 'Daily OHLCV across all U.S. equities.', minPlan: 'Pro' },
      { id: 'flatfiles-stocks-minute-aggregates', title: 'Minute Aggregates', path: 'us_stocks_sip/minute_aggs_v1', description: 'Minute OHLCV across all U.S. equities.', minPlan: 'Pro' },
      { id: 'flatfiles-stocks-trades', title: 'Trades', path: 'us_stocks_sip/trades_v1', description: 'Nanosecond tick trades from exchanges, TRFs, and dark pools.', minPlan: 'Premium' },
      { id: 'flatfiles-stocks-quotes', title: 'Quotes', path: 'us_stocks_sip/quotes_v1', description: 'Nanosecond NBBO quote updates across the consolidated market.', minPlan: 'Premium' },
    ],
    notes: [
      'Every stock Flat File is an unadjusted gzip-compressed CSV with a header row. Prices and volumes are not adjusted for splits or dividends.',
      'Files cover pre-market (4:00–9:30 AM ET), regular trading (9:30 AM–4:00 PM ET), and after-hours trading (4:00–8:00 PM ET), then normally publish by about 11:00 AM ET the following day.',
      'For programs and AI coding agents: list manifests first, partition date ranges into at most 366 days, stream downloads to .part files, verify files[].sizeBytes, rename atomically, checkpoint completed dates, and retry transient failures with bounded concurrency.',
      'Use REST with adjusted=true for smaller adjusted aggregate queries, or apply split factors separately after ingestion.',
    ],
  },
  ...stockFlatFilePages,
  {
    id: 'flatfiles-options-overview',
    group: 'Flat Files',
    title: 'Options Overview',
    method: 'GET',
    path: '/flatfiles/datasets',
    summary: 'Complete U.S. options history as one OPRA CSV archive per session.',
    description: 'Use Options Flat Files for full-market historical work that would otherwise require contract-by-contract API pagination. Four datasets cover daily aggregates, minute aggregates, tick trades, and top-of-book quotes from all 17 U.S. options exchanges consolidated by OPRA.',
    useCases: ['Options backtesting', 'Volatility research', 'Risk modeling', 'Market-microstructure analysis'],
    parameters: [
      { name: 'dataset', type: 'string', description: 'Choose one of the four fixed options dataset slugs below.', example: 'us-options-day-aggs' },
      { name: 'date range', type: 'date', description: 'List published sessions in windows of no more than 366 days, then download each returned date.' },
    ],
    fields: [
      { name: 'day aggregates', type: 'csv.gz', description: 'One OHLCV candle per option contract and trading day.' },
      { name: 'minute aggregates', type: 'csv.gz', description: 'One OHLCV candle per option contract and minute.' },
      { name: 'trades', type: 'csv.gz', description: 'Tick-level OPRA executions with nanosecond timestamps.' },
      { name: 'quotes', type: 'csv.gz', description: 'Tick-level best bid and ask updates with nanosecond timestamps.' },
    ],
    request: 'https://api.eroya.co/v1/flatfiles/datasets',
    response: `{
  "datasets": [
    { "slug": "us-options-day-aggs", "path": "us_options_opra/day_aggs_v1" },
    { "slug": "us-options-minute-aggs", "path": "us_options_opra/minute_aggs_v1" },
    { "slug": "us-options-trades", "path": "us_options_opra/trades_v1" },
    { "slug": "us-options-quotes", "path": "us_options_opra/quotes_v1" }
  ]
}`,
    relatedDatasets: [
      { id: 'flatfiles-options-day-aggregates', title: 'Day Aggregates', path: 'us_options_opra/day_aggs_v1', description: 'Daily OHLCV across all OPRA option contracts.', minPlan: 'Pro' },
      { id: 'flatfiles-options-minute-aggregates', title: 'Minute Aggregates', path: 'us_options_opra/minute_aggs_v1', description: 'Minute OHLCV across all OPRA option contracts.', minPlan: 'Pro' },
      { id: 'flatfiles-options-trades', title: 'Trades', path: 'us_options_opra/trades_v1', description: 'Nanosecond tick trades from all 17 U.S. options exchanges.', minPlan: 'Premium' },
      { id: 'flatfiles-options-quotes', title: 'Quotes', path: 'us_options_opra/quotes_v1', description: 'Nanosecond top-of-book quote updates consolidated by OPRA.', minPlan: 'Premium' },
    ],
    notes: [
      'Options Flat Files are session-sized gzip-compressed CSVs with a header row and canonical O: contract tickers.',
      'The history begins June 2, 2014. Finalized sessions normally publish by about 11:00 AM ET on the following day.',
      'List manifests first, stream downloads to temporary files, verify the returned byte size, and checkpoint completed dates before processing.',
    ],
  },
  ...optionFlatFilePages,
  {
    id: 'flatfiles-indices-overview',
    group: 'Flat Files',
    title: 'Indices Overview',
    method: 'GET',
    path: '/flatfiles/datasets',
    summary: 'Historical values and aggregates for more than 10,000 indices.',
    description: 'Use Indices Flat Files for session-wide benchmark and index-family research without thousands of individual API calls. Three datasets cover daily OHLC, minute OHLC, and every published value from leading global index providers.',
    useCases: ['Benchmark research', 'Portfolio attribution', 'Index backtesting', 'Data lake ingestion'],
    parameters: [
      { name: 'dataset', type: 'string', description: 'Choose one of the three fixed index dataset slugs below.', example: 'us-indices-day-aggs' },
      { name: 'date range', type: 'date', description: 'List published sessions in windows of no more than 366 days, then download each returned date.' },
    ],
    fields: [
      { name: 'day aggregates', type: 'csv.gz', description: 'One daily OHLC record per index.' },
      { name: 'minute aggregates', type: 'csv.gz', description: 'One minute OHLC record per index when a value updates.' },
      { name: 'values', type: 'csv.gz', description: 'Every tick-by-tick index value published during the session.' },
    ],
    request: 'https://api.eroya.co/v1/flatfiles/datasets',
    response: `{
  "datasets": [
    { "slug": "us-indices-day-aggs", "path": "us_indices/day_aggs_v1" },
    { "slug": "us-indices-minute-aggs", "path": "us_indices/minute_aggs_v1" },
    { "slug": "us-indices-values", "path": "us_indices/values_v1" }
  ]
}`,
    relatedDatasets: [
      { id: 'flatfiles-indices-day-aggregates', title: 'Day Aggregates', path: 'us_indices/day_aggs_v1', description: 'Daily OHLC values across more than 10,000 indices.', minPlan: 'Pro' },
      { id: 'flatfiles-indices-minute-aggregates', title: 'Minute Aggregates', path: 'us_indices/minute_aggs_v1', description: 'Minute OHLC values aggregated from published index updates.', minPlan: 'Pro' },
      { id: 'flatfiles-indices-values', title: 'Values', path: 'us_indices/values_v1', description: 'Every published index value in a session-wide archive.', minPlan: 'Premium' },
    ],
    notes: [
      'Index history begins February 14, 2023 and normally publishes by about 11:00 AM ET on the following day.',
      'Index aggregates contain values rather than trades and therefore do not carry volume, VWAP, or transaction counts.',
      'Value archives can be several gigabytes per day. Check manifest sizes before transfer and begin with one concurrent download.',
    ],
  },
  ...indexFlatFilePages,
  {
    id: 'flatfiles-datasets',
    group: 'Flat Files',
    title: 'List flat file datasets',
    method: 'GET',
    path: '/flatfiles/datasets',
    summary: 'Every flat file dataset with your plan entitlement.',
    description: 'Return the flat file catalog: the dataset slug to pass to the other flat file endpoints, its archive path, granularity, the lowest plan that unlocks it, and whether your key is currently entitled to it. The catalog spans U.S. stocks, options, and indices. Downloads themselves are unmetered — the plan decides which datasets you may reach, and nothing is charged per byte.',
    useCases: ['Discovery', 'Entitlement checks', 'Pipeline configuration', 'Plan comparison'],
    parameters: [],
    fields: [
      { name: 'plan', type: 'string', description: 'Plan key resolved for the calling API key.' },
      { name: 'keyPattern', type: 'string', description: 'Object key layout inside each dataset.' },
      { name: 'datasets[].slug', type: 'string', description: 'Identifier to pass as the dataset parameter.' },
      { name: 'datasets[].path', type: 'string', description: 'Archive path for the dataset.' },
      { name: 'datasets[].title', type: 'string', description: 'Human-readable dataset name.' },
      { name: 'datasets[].market', type: 'string', description: 'One of stocks, options, or indices.' },
      { name: 'datasets[].granularity', type: 'string', description: 'Either day, minute, or tick.' },
      { name: 'datasets[].description', type: 'string', description: 'What one row of the dataset contains.' },
      { name: 'datasets[].minPlan', type: 'string', description: 'Lowest plan that unlocks the dataset.' },
      { name: 'datasets[].entitled', type: 'boolean', description: 'Whether the calling key may download it.' },
    ],
    request: 'https://api.eroya.co/v1/flatfiles/datasets?apiKey=EROYA_API_KEY',
    response: `{
  "plan": "pro",
  "keyPattern": "{dataset}/{YYYY}/{MM}/{YYYY-MM-DD}.csv.gz",
  "datasets": [
    {
      "slug": "us-stocks-day-aggs",
      "path": "us_stocks_sip/day_aggs_v1",
      "title": "U.S. stocks day aggregates",
      "market": "stocks",
      "granularity": "day",
      "minPlan": "pro",
      "entitled": true
    },
    {
      "slug": "us-stocks-trades",
      "path": "us_stocks_sip/trades_v1",
      "title": "U.S. stocks trades",
      "market": "stocks",
      "granularity": "tick",
      "minPlan": "premium",
      "entitled": false
    },
    {
      "slug": "us-indices-day-aggs",
      "path": "us_indices/day_aggs_v1",
      "title": "U.S. indices day aggregates",
      "market": "indices",
      "granularity": "day",
      "minPlan": "pro",
      "entitled": true
    }
  ]
}`,
    notes: [
      'Day and minute aggregate datasets require Pro; tick trades, quotes, and index values require Premium.',
      'Both the slug and the full archive path are accepted wherever a dataset parameter is required.',
      'Stock and option archives are us_stocks_sip and us_options_opra; index archives are us_indices, with records dating back to February 14, 2023.',
      'Every object is a gzipped CSV with a header row. Day and minute aggregates carry ticker, volume, open, close, high, low, window_start, and transactions.',
      'Timestamp units differ by dataset family: aggregate window_start values are Unix nanoseconds, while index files use Unix seconds. Both are UTC — convert to ET before aligning with market hours.',
    ],
  },
  {
    id: 'flatfiles-list',
    group: 'Flat Files',
    title: 'List flat files',
    method: 'GET',
    path: '/flatfiles/list',
    summary: 'Available session files and their sizes for a date range.',
    description: 'List the flat files published for a dataset over a date range, with the exact byte size of each object. Only trading sessions appear. Feed each returned files[].date into /flatfiles/download to run a complete backfill: Eroya automatically creates a fresh upstream signature for every file.',
    useCases: ['Backfill planning', 'Transfer sizing', 'Incremental sync', 'Gap detection'],
    parameters: [
      { name: 'dataset', type: 'string', required: true, description: 'Dataset slug or archive path.', example: 'us-stocks-day-aggs' },
      { name: 'start', type: 'string', required: true, description: 'Inclusive range start as YYYY-MM-DD.', example: '2026-03-02' },
      { name: 'end', type: 'string', description: 'Inclusive range end as YYYY-MM-DD. Defaults to start.', example: '2026-03-06' },
    ],
    fields: [
      { name: 'dataset', type: 'string', description: 'Resolved dataset slug.' },
      { name: 'path', type: 'string', description: 'Archive path for the dataset.' },
      { name: 'start', type: 'string', description: 'Inclusive range start.' },
      { name: 'end', type: 'string', description: 'Inclusive range end.' },
      { name: 'count', type: 'integer', description: 'Number of files in the range.' },
      { name: 'totalBytes', type: 'integer', description: 'Combined compressed size of the listed files.' },
      { name: 'files[].date', type: 'string', description: 'Session date as YYYY-MM-DD.' },
      { name: 'files[].key', type: 'string', description: 'Object key to pass to a download.' },
      { name: 'files[].sizeBytes', type: 'integer', description: 'Compressed object size in bytes.' },
      { name: 'files[].lastModified', type: 'string', description: 'Publication timestamp in UTC.' },
    ],
    request: 'https://api.eroya.co/v1/flatfiles/list?dataset=us-stocks-day-aggs&start=2026-03-02&end=2026-03-06&apiKey=EROYA_API_KEY',
    response: `{
  "dataset": "us-stocks-day-aggs",
  "path": "us_stocks_sip/day_aggs_v1",
  "start": "2026-03-02",
  "end": "2026-03-06",
  "count": 5,
  "totalBytes": 36962304,
  "files": [
    {
      "date": "2026-03-02",
      "key": "us_stocks_sip/day_aggs_v1/2026/03/2026-03-02.csv.gz",
      "sizeBytes": 7340032,
      "lastModified": "2026-03-03T11:02:11.000Z"
    },
    {
      "date": "2026-03-03",
      "key": "us_stocks_sip/day_aggs_v1/2026/03/2026-03-03.csv.gz",
      "sizeBytes": 7405568,
      "lastModified": "2026-03-04T11:01:44.000Z"
    }
  ]
}`,
    notes: [
      'A single listing may not span more than 366 days.',
      'For a range download, iterate over files[].date and call /flatfiles/download for each value. Your client never handles S3 credentials or signs a request.',
    ],
  },
  {
    id: 'flatfiles-url',
    group: 'Flat Files',
    title: 'Get a presigned download URL',
    method: 'GET',
    path: '/flatfiles/url',
    summary: 'Short-lived direct link to one session file.',
    description: 'Return a presigned HTTPS URL for a single session file. The link is a standard S3 signed URL, so any HTTP client, S3 client, or dataframe library can read it directly, including ranged reads. Eroya signs the request and never relays the bytes, so download speed is limited only by your own connection.',
    useCases: ['Direct downloads', 'Ranged reads from Polars or DuckDB', 'Parallel backfills', 'Warehouse ingestion'],
    parameters: [
      { name: 'dataset', type: 'string', required: true, description: 'Dataset slug or archive path.', example: 'us-stocks-day-aggs' },
      { name: 'date', type: 'string', required: true, description: 'Session date as YYYY-MM-DD.', example: '2026-03-06' },
      { name: 'expiresIn', type: 'integer', description: 'Link lifetime in seconds, from 60 to 3600. Defaults to 900.', example: '900' },
    ],
    fields: [
      { name: 'dataset', type: 'string', description: 'Resolved dataset slug.' },
      { name: 'date', type: 'string', description: 'Session date of the file.' },
      { name: 'key', type: 'string', description: 'Object key of the file.' },
      { name: 'url', type: 'string', description: 'Presigned HTTPS URL for the object.' },
      { name: 'expiresIn', type: 'integer', description: 'Link lifetime in seconds.' },
      { name: 'expiresAt', type: 'string', description: 'Link expiry as a UTC timestamp.' },
      { name: 'sizeBytes', type: 'integer', description: 'Compressed object size in bytes. Present when the archive reports it; use /flatfiles/list for a guaranteed size.' },
      { name: 'format', type: 'string', description: 'Object format. Always csv.gz.' },
    ],
    request: 'https://api.eroya.co/v1/flatfiles/url?dataset=us-stocks-day-aggs&date=2026-03-06&apiKey=EROYA_API_KEY',
    response: `{
  "dataset": "us-stocks-day-aggs",
  "date": "2026-03-06",
  "key": "us_stocks_sip/day_aggs_v1/2026/03/2026-03-06.csv.gz",
  "url": "https://files.massive.com/flatfiles/us_stocks_sip/day_aggs_v1/2026/03/2026-03-06.csv.gz?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=...&X-Amz-Date=20260307T110000Z&X-Amz-Expires=900&X-Amz-SignedHeaders=host&X-Amz-Signature=...",
  "expiresIn": 900,
  "expiresAt": "2026-03-07T11:15:00.000Z",
  "sizeBytes": 7405568,
  "format": "csv.gz"
}`,
    notes: [
      'A URL grants exactly one object until it expires. Request a fresh one rather than storing it.',
      'Sessions are published by about 11:00 AM ET on the next trading day.',
      'The link is signed without reading the object first, so a date that was never published fails at the archive rather than here. Enumerate dates with /flatfiles/list to avoid that.',
      'Index values sessions run to several gigabytes each. Size the job with /flatfiles/list before requesting links.',
    ],
  },
  {
    id: 'flatfiles-download',
    group: 'Flat Files',
    title: 'Download a flat file',
    method: 'GET',
    path: '/flatfiles/download',
    summary: 'Redirect straight to the signed file.',
    description: 'Answers with an HTTP 302 to a freshly signed upstream link. Any redirect-following client can treat the stable Eroya URL as the file itself. For a date range, call /flatfiles/list once and download every returned date in parallel or a loop; every request is signed automatically.',
    useCases: ['Automatic range backfills', 'curl and wget', 'Direct reads from pandas', 'Airflow and cron jobs'],
    parameters: [
      { name: 'dataset', type: 'string', required: true, description: 'Dataset slug or archive path.', example: 'us-stocks-day-aggs' },
      { name: 'date', type: 'string', required: true, description: 'Session date as YYYY-MM-DD.', example: '2026-03-06' },
      { name: 'expiresIn', type: 'integer', description: 'Link lifetime in seconds, from 60 to 3600. Defaults to 900.', example: '900' },
    ],
    fields: [
      { name: 'Location', type: 'header', description: 'Presigned HTTPS URL of the requested object.' },
    ],
    request: `# One file: Eroya signs it automatically and redirects to the archive
curl -fL -o 2026-03-06.csv.gz \\
  "https://api.eroya.co/v1/flatfiles/download?dataset=us-stocks-day-aggs&date=2026-03-06&apiKey=$EROYA_API_KEY"

# A whole range: list published sessions, then let Eroya sign every download
curl -fsS "https://api.eroya.co/v1/flatfiles/list?dataset=us-stocks-day-aggs&start=2026-03-02&end=2026-03-06&apiKey=$EROYA_API_KEY" \\
  | jq -r '.files[].date' \\
  | while read -r date; do
      curl -fL --retry 3 -o "$date.csv.gz" \\
        "https://api.eroya.co/v1/flatfiles/download?dataset=us-stocks-day-aggs&date=$date&apiKey=$EROYA_API_KEY"
    done`,
    response: `HTTP/1.1 302 Found
Location: https://files.massive.com/flatfiles/us_stocks_sip/day_aggs_v1/2026/03/2026-03-06.csv.gz?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Signature=...
Cache-Control: private, no-store`,
    notes: [
      'Pass -L to curl, or allow_redirects in requests, so the client follows the redirect.',
      'Keep the Eroya download URL in jobs rather than persisting the temporary upstream URL. Each call receives a new signature automatically.',
      'The range example uses jq to read only real trading sessions returned by /flatfiles/list; weekends and market holidays are skipped without special logic.',
    ],
  },
  {
    id: 'flatfiles-bulk-catalog', group: 'Flat Files', regions: ['US', 'HK', 'CN'],
    title: 'Published Sample Files', method: 'GET', path: '/bulk/catalog',
    summary: 'Discover actual US, HK and CN sample-file availability and coverage.',
    description: 'The live bulk shelf returns available monthly bars, single-session ticks and reference snapshots. Coverage is determined by published files, not an implied promise of historical coverage.',
    useCases: ['Research samples', 'Cross-market coverage discovery'], parameters: [],
    fields: [{name:'files',type:'array[object]',description:'Published files with id, market, dataset, period, sizeBytes, format and availability.'}],
    request: 'https://api.eroya.co/v1/bulk/catalog', response: '{"files":[]}',
  },
  {
    id: 'flatfiles-bulk-url', group: 'Flat Files', regions: ['US', 'HK', 'CN'],
    title: 'Sample File Download Link', method: 'GET', path: '/bulk/url',
    summary: 'Get a short-lived link to an existing published sample by file ID.',
    description: 'Use an exact file ID returned by Published Sample Files. The response is a signed download link and metadata; no archive bytes are returned inline.',
    useCases: ['Download research samples'],
    parameters: [{name:'fileId',type:'string',required:true,description:'Published sample ID from /bulk/catalog.'}],
    fields: [{name:'url',type:'string',description:'Short-lived HTTPS download link.'},{name:'expiresIn',type:'integer',description:'Link validity in seconds.'},{name:'file',type:'object',description:'File metadata including size and period.'}],
    request: 'https://api.eroya.co/v1/bulk/url?fileId=REPLACE_WITH_PUBLISHED_ID', response: '{"url":"https://example.invalid/sample","expiresIn":900}',
  },
  ...websocketEndpoints,
];

export const groups = ['U.S. Stocks', 'U.S. Options', 'U.S. Indices', 'Hong Kong Stocks', 'China A-shares', 'Global Markets', 'Flat Files', 'WebSocket'] as const;

const endpointOrder = [
  // Unified HK/CN/global catalog.
  ...longbridgeEndpoints.map(({ id }) => id),
  // Stocks: mirrors the public catalog from reference data through filings.
  'us-stock-tickers',
  'us-stock-ticker-overview',
  'us-stock-ticker-events',
  'us-stocks-custom-bars',
  'us-stocks-daily-summary',
  'us-stocks-previous-day-bar',
  'us-stocks-full-market-snapshot',
  'us-trades',
  'us-stocks-last-trade',
  'us-stocks-quotes',
  'us-sma',
  'us-balance-sheets',
  'us-cash-flow-statements',
  'us-income-statements',
  'us-financial-ratios',
  'us-short-interest',
  'us-short-volume',
  'us-disclosure-categories',
  'us-news',
  // Options.
  'us-options-contracts',
  'us-options-custom-bars',
  'us-options-daily-summary',
  'us-options-previous-day-bar',
  'us-options-chain-snapshot',
  'us-options-trades',
  'us-options-last-trade',
  'us-options-quotes',
  'us-options-exchanges',
  // Existing index and China catalogs retain their own order.
  'indices-custom-bars',
  'indices-previous-day-bar',
  'indices-daily-summary',
  'indices-snapshot',
  'indices-tickers',
  'cn-daily',
  'cn-cyq-perf',
  'cn-technical-factors',
  'cn-balance-sheet',
  'cn-disclosure-date',
  // Flat-file query/sign/download helpers stay above the asset datasets.
  'flatfiles-datasets',
  'flatfiles-list',
  'flatfiles-url',
  'flatfiles-download',
  // Every asset class then reads overview -> one-shot datasets.
  'flatfiles-stocks-overview',
  'flatfiles-stocks-day-aggregates',
  'flatfiles-stocks-minute-aggregates',
  'flatfiles-stocks-trades',
  'flatfiles-stocks-quotes',
  'flatfiles-options-overview',
  'flatfiles-options-day-aggregates',
  'flatfiles-options-minute-aggregates',
  'flatfiles-options-trades',
  'flatfiles-options-quotes',
  'flatfiles-indices-overview',
  'flatfiles-indices-day-aggregates',
  'flatfiles-indices-minute-aggregates',
  'flatfiles-indices-values',
  // Streaming: the handshake, then one page per event type.
  'ws-overview',
  'ws-stocks-aggregates-minute',
  'ws-stocks-aggregates-second',
  'ws-stocks-trades',
  'ws-options-aggregates-minute',
  'ws-options-aggregates-second',
  'ws-stocks-quotes',
  'ws-options-trades',
  'ws-options-quotes',
  'ws-indices-aggregates-minute',
  'ws-indices-aggregates-second',
  'ws-indices-value',
] as const;

const endpointRank = new Map<string, number>(endpointOrder.map((id, index) => [id, index]));

/** Return endpoints in the canonical documentation/navigation order. */
export function orderEndpoints(items: Endpoint[]): Endpoint[] {
  return [...items].sort(
    (a, b) => (endpointRank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (endpointRank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
}

/** Asset classes whose sidebar should list this endpoint. */
export function groupsOf(endpoint: Endpoint): Array<Endpoint['group']> {
  return endpoint.markets ?? [endpoint.group];
}

/**
 * Ordered navigation tree. Both levels are listed explicitly rather than
 * derived, because the order is an editorial decision — a reader should meet
 * reference data before prices, and statements before the estimates built on
 * them — and alphabetical or insertion order gives neither.
 */
const SECTION_ORDER: readonly EndpointSection[] = [
  'Reference Data',
  'Aggregates (OHLC)',
  'Snapshots',
  'Trades & Quotes',
  'Prices & Activity',
  'Company Fundamentals',
  'Estimates & Analyst Coverage',
  'Ownership & Disclosures',
  'Corporate Actions & Calendars',
  'Market Analytics',
  'Derivatives',
  'News & Content',
  'Flat Files',
  'Streaming',
] as const;

const SUBSECTION_ORDER: Record<EndpointSection, readonly EndpointSubsection[]> = {
  'Reference Data': ['Securities & Tickers', 'Trading Sessions', 'Taxonomies'],
  'Prices & Activity': ['Technical Indicators'],
  'Aggregates (OHLC)': ['Aggregate Bars (OHLC)'],
  'Snapshots': ['Intraday & Snapshots'],
  'Trades & Quotes': ['Trades & Quotes'],
  'Company Fundamentals': ['Financial Statements', 'Metrics & Ratios', 'Valuation', 'Company Profile', 'Funds & ETFs'],
  'Estimates & Analyst Coverage': ['Consensus Estimates', 'Estimate Revisions', 'Ratings & Price Targets', 'Earnings Results'],
  'Ownership & Disclosures': ['Shareholders', 'Insider Activity', 'Filings', 'Short Interest', 'Sustainability'],
  'Corporate Actions & Calendars': ['Dividends & Buybacks', 'Corporate Actions', 'Event Calendars'],
  'Market Analytics': [
    'Capital Flow', 'Market Sentiment', 'Trading Statistics', 'Broker Holdings',
    'Cross-Listing (A/H)', 'Index Composition', 'Macroeconomics', 'Quantitative Factors',
  ],
  Derivatives: ['Options', 'Warrants'],
  'News & Content': ['Company News', 'Topics'],
  'Flat Files': ['Query & Download', 'Stock Archives', 'Options Archives', 'Index Archives'],
  Streaming: ['Connecting', 'Stock Streams', 'Options Streams', 'Index Streams'],
};

type Placement = readonly [EndpointSection, EndpointSubsection];

/**
 * Where each endpoint sits in the tree.
 *
 * Kept as an explicit table rather than the substring rules this replaced.
 * Those rules silently sent anything unmatched to a catch-all, so a new route
 * landed in the wrong place without anyone noticing; `assertTaxonomyComplete`
 * now fails the build instead. Grouping is by what a dataset answers, not by
 * which upstream serves it or which URL prefix it happens to use — reported
 * financials and the estimates about them are different questions even though
 * both live under `/fundamentals/`.
 */
const TAXONOMY: Record<string, Placement> = {
  // --- Reference Data ------------------------------------------------------
  'lb-security-static': ['Reference Data', 'Securities & Tickers'],
  'us-stock-tickers': ['Reference Data', 'Securities & Tickers'],
  'us-stock-ticker-overview': ['Reference Data', 'Securities & Tickers'],
  'indices-tickers': ['Reference Data', 'Securities & Tickers'],
  'lb-market-status': ['Reference Data', 'Trading Sessions'],
  'lb-market-trading-sessions': ['Reference Data', 'Trading Sessions'],
  'lb-market-trading-days': ['Reference Data', 'Trading Sessions'],
  'lb-market-rank-categories': ['Reference Data', 'Taxonomies'],
  'us-disclosure-categories': ['Reference Data', 'Taxonomies'],

  // --- Prices & Activity ---------------------------------------------------
  'us-stocks-custom-bars': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'us-stocks-daily-summary': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'us-stocks-previous-day-bar': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'indices-custom-bars': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'indices-daily-summary': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'indices-previous-day-bar': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'lb-candlesticks': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'cn-daily': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'lb-intraday': ['Snapshots', 'Intraday & Snapshots'],
  'us-stocks-full-market-snapshot': ['Snapshots', 'Intraday & Snapshots'],
  'indices-snapshot': ['Snapshots', 'Intraday & Snapshots'],
  'lb-hk-quotes': ['Trades & Quotes', 'Trades & Quotes'],
  'us-trades': ['Trades & Quotes', 'Trades & Quotes'],
  'us-stocks-last-trade': ['Trades & Quotes', 'Trades & Quotes'],
  'us-stocks-quotes': ['Trades & Quotes', 'Trades & Quotes'],
  'us-sma': ['Prices & Activity', 'Technical Indicators'],

  // --- Company Fundamentals ------------------------------------------------
  'lb-fundamental-financial-report': ['Company Fundamentals', 'Financial Statements'],
  'lb-fundamental-financial-report-snapshot': ['Company Fundamentals', 'Financial Statements'],
  'lb-fundamental-us-financial-overview': ['Company Fundamentals', 'Financial Statements'],
  'lb-fundamental-us-financial-statement': ['Company Fundamentals', 'Financial Statements'],
  'us-fundamental-us-income-statement': ['Company Fundamentals', 'Financial Statements'],
  'us-fundamental-us-balance-sheet': ['Company Fundamentals', 'Financial Statements'],
  'us-fundamental-us-cash-flow': ['Company Fundamentals', 'Financial Statements'],
  'us-income-statements': ['Company Fundamentals', 'Financial Statements'],
  'us-balance-sheets': ['Company Fundamentals', 'Financial Statements'],
  'us-cash-flow-statements': ['Company Fundamentals', 'Financial Statements'],
  'cn-balance-sheet': ['Company Fundamentals', 'Financial Statements'],
  'us-financial-ratios': ['Company Fundamentals', 'Metrics & Ratios'],
  'lb-fundamental-us-key-metrics': ['Company Fundamentals', 'Metrics & Ratios'],
  'lb-fundamental-operating-metrics': ['Company Fundamentals', 'Metrics & Ratios'],
  'lb-fundamental-valuations': ['Company Fundamentals', 'Valuation'],
  'lb-fundamental-valuation-history': ['Company Fundamentals', 'Valuation'],
  'lb-fundamental-valuation-comparison': ['Company Fundamentals', 'Valuation'],
  'lb-fundamental-industry-valuation': ['Company Fundamentals', 'Valuation'],
  'lb-fundamental-industry-valuation-distribution': ['Company Fundamentals', 'Valuation'],
  'lb-fundamental-us-valuation-overview': ['Company Fundamentals', 'Valuation'],
  'lb-fundamental-company-profile': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-us-company-overview': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-executives': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-investment-relations': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-business-segments': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-business-segments-history': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-industry-ranking': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-industry-peer-hierarchy': ['Company Fundamentals', 'Company Profile'],
  'lb-fundamental-fund-holdings': ['Company Fundamentals', 'Funds & ETFs'],
  'lb-fundamental-etf-asset-allocation': ['Company Fundamentals', 'Funds & ETFs'],
  'lb-fundamental-us-etf-dividends': ['Company Fundamentals', 'Funds & ETFs'],
  'lb-fundamental-us-etf-files': ['Company Fundamentals', 'Funds & ETFs'],
  'us-fundamental-us-fund-profile': ['Company Fundamentals', 'Funds & ETFs'],

  // --- Estimates & Analyst Coverage ---------------------------------------
  'lb-fundamental-forecast-eps': ['Estimates & Analyst Coverage', 'Consensus Estimates'],
  'lb-fundamental-financial-consensus': ['Estimates & Analyst Coverage', 'Consensus Estimates'],
  'lb-fundamental-us-analyst-consensus': ['Estimates & Analyst Coverage', 'Consensus Estimates'],
  'us-fundamental-us-earnings-estimate': ['Estimates & Analyst Coverage', 'Consensus Estimates'],
  'us-fundamental-us-revenue-estimate': ['Estimates & Analyst Coverage', 'Consensus Estimates'],
  'us-fundamental-us-growth-estimates': ['Estimates & Analyst Coverage', 'Consensus Estimates'],
  'us-fundamental-us-eps-trend': ['Estimates & Analyst Coverage', 'Estimate Revisions'],
  'us-fundamental-us-eps-revisions': ['Estimates & Analyst Coverage', 'Estimate Revisions'],
  'lb-fundamental-analyst-ratings': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'lb-fundamental-institution-rating': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'lb-fundamental-institution-rating-detail': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'lb-fundamental-institutional-rating-views': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'us-fundamental-us-recommendations': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'us-fundamental-us-recommendation-summary': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'us-fundamental-us-upgrades-downgrades': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'us-fundamental-us-analyst-price-targets': ['Estimates & Analyst Coverage', 'Ratings & Price Targets'],
  'us-fundamental-us-earnings': ['Estimates & Analyst Coverage', 'Earnings Results'],
  'us-fundamental-us-earnings-history': ['Estimates & Analyst Coverage', 'Earnings Results'],
  'us-fundamental-us-earnings-dates': ['Estimates & Analyst Coverage', 'Earnings Results'],
  'us-fundamental-us-earnings-calendar': ['Estimates & Analyst Coverage', 'Earnings Results'],

  // --- Ownership & Disclosures --------------------------------------------
  'lb-fundamental-shareholders': ['Ownership & Disclosures', 'Shareholders'],
  'lb-fundamental-top-shareholders': ['Ownership & Disclosures', 'Shareholders'],
  'lb-fundamental-shareholder-detail': ['Ownership & Disclosures', 'Shareholders'],
  'us-fundamental-us-major-holders': ['Ownership & Disclosures', 'Shareholders'],
  'us-fundamental-us-institutional-holders': ['Ownership & Disclosures', 'Shareholders'],
  'us-fundamental-us-mutualfund-holders': ['Ownership & Disclosures', 'Shareholders'],
  'us-fundamental-us-insider-transactions': ['Ownership & Disclosures', 'Insider Activity'],
  'us-fundamental-us-insider-purchases': ['Ownership & Disclosures', 'Insider Activity'],
  'us-fundamental-us-insider-roster': ['Ownership & Disclosures', 'Insider Activity'],
  'lb-analytics-filings': ['Ownership & Disclosures', 'Filings'],
  'us-fundamental-us-sec-filings': ['Ownership & Disclosures', 'Filings'],
  'cn-disclosure-date': ['Ownership & Disclosures', 'Filings'],
  'us-short-interest': ['Ownership & Disclosures', 'Short Interest'],
  'us-short-volume': ['Ownership & Disclosures', 'Short Interest'],
  'lb-analytics-short-positions': ['Ownership & Disclosures', 'Short Interest'],
  'lb-analytics-short-volume': ['Ownership & Disclosures', 'Short Interest'],
  'us-fundamental-us-sustainability': ['Ownership & Disclosures', 'Sustainability'],

  // --- Corporate Actions & Calendars ---------------------------------------
  'us-stock-ticker-events': ['Corporate Actions & Calendars', 'Corporate Actions'],
  'lb-fundamental-dividends': ['Corporate Actions & Calendars', 'Dividends & Buybacks'],
  'lb-fundamental-dividend-detail': ['Corporate Actions & Calendars', 'Dividends & Buybacks'],
  'lb-fundamental-us-company-dividends': ['Corporate Actions & Calendars', 'Dividends & Buybacks'],
  'lb-fundamental-buyback': ['Corporate Actions & Calendars', 'Dividends & Buybacks'],
  'lb-fundamental-corporate-actions': ['Corporate Actions & Calendars', 'Corporate Actions'],
  'lb-calendar-earnings': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-dividends': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-splits': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-ipos': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-macro': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-closures': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-meetings': ['Corporate Actions & Calendars', 'Event Calendars'],
  'lb-calendar-mergers': ['Corporate Actions & Calendars', 'Event Calendars'],

  // --- Market Analytics ----------------------------------------------------
  'lb-analytics-capital-flow': ['Market Analytics', 'Capital Flow'],
  'lb-analytics-capital-distribution': ['Market Analytics', 'Capital Flow'],
  'lb-market-temperature': ['Market Analytics', 'Market Sentiment'],
  'lb-market-temperature-history': ['Market Analytics', 'Market Sentiment'],
  'lb-market-popularity': ['Market Analytics', 'Market Sentiment'],
  'lb-market-top-movers': ['Market Analytics', 'Market Sentiment'],
  'lb-market-unusual-items': ['Market Analytics', 'Market Sentiment'],
  'lb-market-trade-statistics': ['Market Analytics', 'Trading Statistics'],
  'lb-analytics-calc-indexes': ['Market Analytics', 'Trading Statistics'],
  'lb-market-broker-holdings': ['Market Analytics', 'Broker Holdings'],
  'lb-market-broker-holding-detail': ['Market Analytics', 'Broker Holdings'],
  'lb-market-broker-holding-history': ['Market Analytics', 'Broker Holdings'],
  'lb-market-ah-premium': ['Market Analytics', 'Cross-Listing (A/H)'],
  'lb-market-ah-premium-intraday': ['Market Analytics', 'Cross-Listing (A/H)'],
  'lb-market-index-constituents': ['Market Analytics', 'Index Composition'],
  'lb-fundamental-macroeconomic-indicators': ['Market Analytics', 'Macroeconomics'],
  'lb-fundamental-macroeconomic-history': ['Market Analytics', 'Macroeconomics'],
  'cn-cyq-perf': ['Market Analytics', 'Quantitative Factors'],
  'cn-technical-factors': ['Market Analytics', 'Quantitative Factors'],

  // --- Derivatives ---------------------------------------------------------
  // Options use the same documentation categories as stocks: tickers, bars,
  // snapshots, and trades/quotes. Warrants remain in their own family.
  'us-options-contracts': ['Reference Data', 'Securities & Tickers'],
  'us-options-custom-bars': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'us-options-daily-summary': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'us-options-previous-day-bar': ['Aggregates (OHLC)', 'Aggregate Bars (OHLC)'],
  'us-options-chain-snapshot': ['Snapshots', 'Intraday & Snapshots'],
  'us-options-trades': ['Trades & Quotes', 'Trades & Quotes'],
  'us-options-last-trade': ['Trades & Quotes', 'Trades & Quotes'],
  'us-options-quotes': ['Trades & Quotes', 'Trades & Quotes'],
  'us-options-exchanges': ['Reference Data', 'Taxonomies'],
  'lb-warrant-quotes': ['Derivatives', 'Warrants'],
  'lb-warrant-issuers': ['Derivatives', 'Warrants'],
  'lb-warrant-screener': ['Derivatives', 'Warrants'],

  // --- News & Content ------------------------------------------------------
  'us-news': ['News & Content', 'Company News'],
  'lb-content-security-news': ['News & Content', 'Company News'],
  'lb-content-topics': ['News & Content', 'Topics'],

  // --- Flat Files ----------------------------------------------------------
  'flatfiles-bulk-catalog': ['Flat Files', 'Query & Download'],
  'flatfiles-bulk-url': ['Flat Files', 'Query & Download'],
  'flatfiles-datasets': ['Flat Files', 'Query & Download'],
  'flatfiles-list': ['Flat Files', 'Query & Download'],
  'flatfiles-url': ['Flat Files', 'Query & Download'],
  'flatfiles-download': ['Flat Files', 'Query & Download'],
  'flatfiles-stocks-overview': ['Flat Files', 'Stock Archives'],
  'flatfiles-stocks-day-aggregates': ['Flat Files', 'Stock Archives'],
  'flatfiles-stocks-minute-aggregates': ['Flat Files', 'Stock Archives'],
  'flatfiles-stocks-trades': ['Flat Files', 'Stock Archives'],
  'flatfiles-stocks-quotes': ['Flat Files', 'Stock Archives'],
  'flatfiles-options-overview': ['Flat Files', 'Options Archives'],
  'flatfiles-options-day-aggregates': ['Flat Files', 'Options Archives'],
  'flatfiles-options-minute-aggregates': ['Flat Files', 'Options Archives'],
  'flatfiles-options-trades': ['Flat Files', 'Options Archives'],
  'flatfiles-options-quotes': ['Flat Files', 'Options Archives'],
  'flatfiles-indices-overview': ['Flat Files', 'Index Archives'],
  'flatfiles-indices-day-aggregates': ['Flat Files', 'Index Archives'],
  'flatfiles-indices-minute-aggregates': ['Flat Files', 'Index Archives'],
  'flatfiles-indices-values': ['Flat Files', 'Index Archives'],

  // --- Streaming -----------------------------------------------------------
  'ws-overview': ['Streaming', 'Connecting'],
  'ws-stocks-aggregates-minute': ['Streaming', 'Stock Streams'],
  'ws-stocks-aggregates-second': ['Streaming', 'Stock Streams'],
  'ws-stocks-trades': ['Streaming', 'Stock Streams'],
  'ws-options-aggregates-minute': ['Streaming', 'Options Streams'],
  'ws-options-aggregates-second': ['Streaming', 'Options Streams'],
  'ws-stocks-quotes': ['Streaming', 'Stock Streams'],
  'ws-options-trades': ['Streaming', 'Options Streams'],
  'ws-options-quotes': ['Streaming', 'Options Streams'],
  'ws-indices-aggregates-minute': ['Streaming', 'Index Streams'],
  'ws-indices-aggregates-second': ['Streaming', 'Index Streams'],
  'ws-indices-value': ['Streaming', 'Index Streams'],
};

/**
 * Every endpoint must be placed. An unplaced one would silently vanish from the
 * sidebar, so this throws at module load — in the dev server, the build, and
 * the test run alike — rather than shipping a catalog with a hole in it.
 */
function assertTaxonomyComplete(): void {
  const missing = endpoints.filter((endpoint) => !TAXONOMY[endpoint.id]).map((endpoint) => endpoint.id);
  if (missing.length) {
    throw new Error(`Endpoints missing a navigation placement: ${missing.join(', ')}`);
  }
}
assertTaxonomyComplete();

export function sectionOf(endpoint: Endpoint): EndpointSection {
  return TAXONOMY[endpoint.id][0];
}

export function subsectionOf(endpoint: Endpoint): EndpointSubsection {
  return TAXONOMY[endpoint.id][1];
}

/** Sections holding at least one endpoint for this asset class, in reading order. */
export function sectionsFor(group: Endpoint['group']): EndpointSection[] {
  const present = new Set(
    endpoints.filter((endpoint) => groupsOf(endpoint).includes(group)).map(sectionOf),
  );
  return SECTION_ORDER.filter((section) => present.has(section));
}

/** Subsections holding at least one endpoint for this asset class and section. */
export function subsectionsFor(
  group: Endpoint['group'],
  section: EndpointSection,
): EndpointSubsection[] {
  const present = new Set(
    endpoints
      .filter((endpoint) => groupsOf(endpoint).includes(group) && sectionOf(endpoint) === section)
      .map(subsectionOf),
  );
  return SUBSECTION_ORDER[section].filter((subsection) => present.has(subsection));
}

/** Top-level products; analyst coverage is part of the REST fundamentals catalog. */
export const products = ['rest', 'flat-files', 'websocket'] as const;
export type Product = (typeof products)[number];

export function productOf(endpoint: Endpoint): Product {
  if (endpoint.group === 'WebSocket') return 'websocket';
  return endpoint.group === 'Flat Files' ? 'flat-files' : 'rest';
}

export function regionsOf(endpoint: Endpoint): Array<'US' | 'HK' | 'CN' | 'GLOBAL'> {
  if (endpoint.regions) return endpoint.regions;
  if (productOf(endpoint) !== 'rest') return ['US'];
  const region = { 'U.S. Stocks': 'US', 'U.S. Options': 'US', 'U.S. Indices': 'US',
    'Hong Kong Stocks': 'HK', 'China A-shares': 'CN', 'Global Markets': 'GLOBAL' } as const;
  return [...new Set(groupsOf(endpoint).map(group => region[group as keyof typeof region] ?? 'GLOBAL'))];
}

export function assetClassOf(endpoint: Endpoint): string {
  const text = `${endpoint.id} ${endpoint.group}`.toLowerCase();
  if (/options/.test(text)) return 'options';
  if (/indices|index/.test(text)) return 'indices';
  if (/stocks|shares/.test(text) || regionsOf(endpoint).some(r => r === 'HK' || r === 'CN')) return 'equities';
  return 'cross-asset';
}

/** Asset-class groups belonging to a product, in sidebar order. */
export function groupsFor(product: Product) {
  return groups.filter((group) => {
    if (product === 'flat-files') return group === 'Flat Files';
    if (product === 'websocket') return group === 'WebSocket';
    return group !== 'Flat Files' && group !== 'WebSocket';
  });
}

export function endpointsFor(product: Product) {
  return orderEndpoints(endpoints.filter((endpoint) => productOf(endpoint) === product));
}

export function endpointHref(endpoint: Endpoint) {
  if (productOf(endpoint) === 'websocket') {
    return `/websocket/${endpoint.id.replace(/^ws-/, '')}`;
  }
  return productOf(endpoint) === 'flat-files'
    ? `/flat-files/${endpoint.id.replace(/^flatfiles-/, '')}`
    : `/reference/${endpoint.id}`;
}

/** Resolve either product's URL shape back to an endpoint. */
export function endpointFromPath(path: string): Endpoint | null {
  const stream = path.match(/^\/websocket\/([^/]+)\/?$/)?.[1];
  if (stream) {
    return endpoints.find((endpoint) => endpoint.id === `ws-${stream}`) ?? null;
  }
  const flatFile = path.match(/^\/flat-files\/([^/]+)\/?$/)?.[1];
  if (flatFile) {
    return endpoints.find((endpoint) => endpoint.id === `flatfiles-${flatFile}`) ?? null;
  }
  const reference = path.match(/^\/reference\/([^/]+)\/?$/)?.[1];
  if (reference) {
    return endpoints.find((endpoint) => endpoint.id === reference) ?? null;
  }
  return null;
}

/** The endpoint a product tab should open. */
export function landingEndpoint(product: Product): Endpoint {
  return endpointsFor(product)[0];
}
